import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { parsearPdf, confirmarImportacion, guardarBorrador, obtenerBorrador, buscarBorrador, previsualizarRegularidadRti, obtenerResumenAportesCliente, obtenerCajasAprendidas, aprenderCajasEmpleador } from '../services/historialLaboralService';
import { buscarClientes } from '../services/clienteService';
import { listarLey24476 } from '../services/aperturaPlanService';
import {
    expandirYAnalizar, formatearYearMonth, validarPeriodo, validarRango,
    analizarRegistrosHasta0694, cruzarSecciones,
    normalizarYearMonth,
    calcularMesesDefinitivosConSicam,
    expandirRangoYearMonth,
    aplicarReglasEspecialesResumen,
    calcularOpcionesComplementoAportes,
    calcularPrimerMesComputable,
    calcularProrrateoRegimenes,
    calcularCajaOtorgante,
    CAJAS_PROVINCIALES,
} from '../utils/historialLaboralUtils';
import DatosMaestrosService from '../services/datosMaestrosService';
import RegularidadMinusvalia from '../components/RegularidadMinusvalia';
import {
    parseDesde0794Section,
    getResumenAdjustmentsFromDesde0794,
} from '../utils/desde0794Parser';
import '../style/pages/ImportarHistorialLaboral.css';
import ModalRegistrarCliente from '../components/ModalRegistrarCliente';
import RegularidadAporte from '../components/RegularidadAporte';
import { useLocation, useNavigate } from 'react-router-dom';
import { apellidoNombre } from '../utils/nombrePersona';
import PageHeader from '../components/ui/PageHeader';

const PASOS = ['Cargar PDF', 'Revisar y Editar', 'Confirmar Importación'];

// Normaliza el nombre del empleador igual que el backend (sin acentos, mayúsculas, espacios colapsados).
const normalizarEmpleador = (nombre) => String(nombre || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim().toUpperCase().replace(/\s+/g, ' ');

// Meses (YYYY-MM) que cubre una fila del Resumen (rango Desde/Hasta en MM/YYYY).
const mesesDeFilaResumen = (reg) => {
    if (!reg || !validarPeriodo(reg.desde) || !validarPeriodo(reg.hasta) || !validarRango(reg.desde, reg.hasta)) return [];
    return expandirRangoYearMonth(normalizarYearMonth(reg.desde), normalizarYearMonth(reg.hasta));
};

// Meses (YYYY-MM) que cubre una fila de HASTA 06/94 (año + alta/baja "DD/MM - DD/MM").
const mesesDeFilaHasta = (reg) => {
    const anio = parseInt(reg?.anio, 10);
    if (!Number.isFinite(anio)) return [];
    let mesIni = 1;
    let mesFin = 12;
    const m = String(reg?.altaBaja || '').match(/(\d{1,2})\/(\d{1,2}).*?(\d{1,2})\/(\d{1,2})/);
    if (m) {
        mesIni = parseInt(m[2], 10);
        mesFin = parseInt(m[4], 10);
    } else {
        const mt = parseInt(reg?.mesesTrabajados, 10);
        if (Number.isFinite(mt) && mt > 0 && mt <= 12) { mesFin = 12; mesIni = 13 - mt; }
    }
    if (!(mesIni >= 1 && mesIni <= 12 && mesFin >= 1 && mesFin <= 12 && mesIni <= mesFin)) { mesIni = 1; mesFin = 12; }
    const out = [];
    for (let mm = mesIni; mm <= mesFin; mm++) out.push(`${anio}-${String(mm).padStart(2, '0')}`);
    return out;
};

const ImportarHistorialLaboralPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // === Estado global ===
    const [pasoActual, setPasoActual] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // ── Paso 1: Upload ──
    const [dragOver, setDragOver] = useState(false);
    const [archivo, setArchivo] = useState(null);
    const fileInputRef = useRef(null);

    // ── Datos parseados: Resumen ──
    const [nombreCompleto, setNombreCompleto] = useState('');
    const [cuil, setCuil] = useState('');
    const [registros, setRegistros] = useState([]);
    const [analisis, setAnalisis] = useState(null);
    const [resumenReglas, setResumenReglas] = useState({
        registrosClasificados: [],
        registrosComputables: [],
        registrosExcluidos: [],
        alertas: [],
    });

    // ── Datos parseados: HASTA 06/94 ──
    const [registrosHasta0694, setRegistrosHasta0694] = useState([]);

    // ── Datos parseados: DESDE 07/94 (solo control/ajustes) ──
    const [registrosDesde0794, setRegistrosDesde0794] = useState([]);
    const [ajustesDesde0794, setAjustesDesde0794] = useState([]);

    // ── Tab activa en paso 2 ──
    const [tabActiva, setTabActiva] = useState('resumen');

    // ── Correcciones y totales definitivos ──
    const [correcciones, setCorrecciones] = useState([]);
    const [totalMesesComputables, setTotalMesesComputables] = useState(0);
    const [totalMesesNoComputables, setTotalMesesNoComputables] = useState(0);
    const [mesesComputablesBase, setMesesComputablesBase] = useState([]);

    // Efecto para buscar borrador si se recibe CUIL de navegación
    useEffect(() => {
        if (location.state?.prefilledCuil && location.state?.buscarBorrador) {
            buscarYRecuperarBorrador(location.state.prefilledCuil);
        }
    }, [location.state]);

    const buscarYRecuperarBorrador = async (cuilBuscado) => {
        try {
            setLoading(true);
            const datos = await obtenerBorrador(cuilBuscado);
            if (!datos) {
                return;
            }

            resetStateForNewPdf();
            
            // Restaurar estado
            setNombreCompleto(datos.nombreCompleto || '');
            const cLimpio1 = datos.cuil ? datos.cuil.toString().replace(/\D/g, '') : '';
            setCuil(cLimpio1);
            setRegistros(datos.registros || []);
            setAnalisis(datos.analisis || { mesesExpandidos: [], solapamientos: [], totalMesesUnicos: 0, totalMesesSuperpuestos: 0 });
            setResumenReglas(datos.resumenReglas || { registrosClasificados: [], registrosComputables: [], registrosExcluidos: [], alertas: [] });
            setRegistrosHasta0694(datos.registrosHasta0694 || []);
            setRegistrosDesde0794(datos.registrosDesde0794 || []);
            setAjustesDesde0794(datos.ajustesDesde0794 || []);
            setCorrecciones(datos.correcciones || []);
            setTotalMesesComputables(datos.totalMesesComputables || 0);
            setTotalMesesNoComputables(datos.totalMesesNoComputables || 0);
            setMesesComputablesBase(datos.mesesComputablesBase || []);
            setManualPeriodosSicam(datos.manualPeriodosSicam || []);
            setAsignacionesRegimen(datos.asignacionesRegimen || []);
            setAsignacionesCaja(datos.asignacionesCaja || []);
            setMarcasFila(datos.marcasFila || {});
            
            // Estado de Step 3
            if (datos.fechaNacimientoPdf) setFechaNacimientoPdf(datos.fechaNacimientoPdf);
            if (datos.moratoriaFechaNacimiento) setMoratoriaFechaNacimiento(datos.moratoriaFechaNacimiento);
            if (datos.moratoriaSexo) setMoratoriaSexo(datos.moratoriaSexo);
            if (datos.moratoriaHijos) setMoratoriaHijos(datos.moratoriaHijos);
            if (datos.moratoriaEstrategiasSeleccion) setMoratoriaEstrategiasSeleccion(datos.moratoriaEstrategiasSeleccion);
            setChequeoRtiIncluirExistentes(Boolean(datos.chequeoRtiIncluirExistentes));
            setChequeoRtiResultado(null);
            setChequeoRtiError(null);

            setTabActiva('resumen');
            setPasoActual(1);
            setSuccessMsg('Borrador cargado exitosamente.');
            setTimeout(() => setSuccessMsg(null), 4000);

        } catch (error) {
            console.error('Error buscando borrador:', error);
            // No bloqueamos ni mostramos error, solo seguimos normalmente si no hay
        } finally {
            setLoading(false);
        }
    };

    // ── Paso 3: Cliente ──
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [resultadosCliente, setResultadosCliente] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [aportesActualesCliente, setAportesActualesCliente] = useState(null);
    const [modalReemplazoOpen, setModalReemplazoOpen] = useState(false);
    const [resumenReemplazo, setResumenReemplazo] = useState(null);
    const [mesesPendientesImportacion, setMesesPendientesImportacion] = useState([]);
    const [importacionResultado, setImportacionResultado] = useState(null);
    const [chequeoRtiIncluirExistentes, setChequeoRtiIncluirExistentes] = useState(false);
    const [chequeoRtiCargando, setChequeoRtiCargando] = useState(false);
    const [chequeoRtiResultado, setChequeoRtiResultado] = useState(null);
    const [chequeoRtiError, setChequeoRtiError] = useState(null);
    const [moratoriaFechaNacimiento, setMoratoriaFechaNacimiento] = useState('');
    const [moratoriaSexo, setMoratoriaSexo] = useState('');
    const [moratoriaHijos, setMoratoriaHijos] = useState([]);
    const [moratoriaEstrategiasSeleccion, setMoratoriaEstrategiasSeleccion] = useState({
        ley24476: 0,
        monotributoFuturo: 0,
        ley27705: 0,
    });
    // E14: selección visual de la tarjeta "Aporte futuro + exceso de edad" (solo estética).
    const [aporteFuturoSeleccionado, setAporteFuturoSeleccionado] = useState(false);
    // B1: override manual de ventanas (desde/hasta) por estrategia. Vacío => usa la ventana calculada.
    const [ventanasEstrategiasOverride, setVentanasEstrategiasOverride] = useState({
        ley24476: { inicioYm: '', finYm: '' },
        monotributoFuturo: { inicioYm: '', finYm: '' },
        ley27705: { inicioYm: '', finYm: '' },
    });
    const [mostrarEditarVentanas, setMostrarEditarVentanas] = useState(false);
    // B2: panel opcional de costos de las estrategias (carga manual, se estima al día de impresión).
    const [mostrarCostos, setMostrarCostos] = useState(false);
    const [costos, setCostos] = useState({ ley24476Total: '', monotributoMensual: '', ley27705Mensual: '' });
    const [manualPeriodosSicam, setManualPeriodosSicam] = useState([]);
    const [manualDesdeInput, setManualDesdeInput] = useState('');
    const [manualHastaInput, setManualHastaInput] = useState('');
    const [manualTipoInput, setManualTipoInput] = useState('MONOTRIBUTO');
    // Regímenes diferenciales (prorrateo ANSES): catálogo activo + asignaciones de rangos de meses.
    const [regimenesDisponibles, setRegimenesDisponibles] = useState([]);
    const [asignacionesRegimen, setAsignacionesRegimen] = useState([]);
    const [regimenAsignInput, setRegimenAsignInput] = useState({ desde: '', hasta: '', regimenId: '' });
    // Cajas provinciales (F3): asignación de rangos de meses a una caja no transferida.
    const [asignacionesCaja, setAsignacionesCaja] = useState([]);
    const [cajaAsignInput, setCajaAsignInput] = useState({ desde: '', hasta: '', cajaId: '' });
    const [cajasAprendidas, setCajasAprendidas] = useState([]);
    const [aprendizajeMsg, setAprendizajeMsg] = useState(null);
    // Marcas de régimen diferencial / caja provincial por fila (Resumen "R:<id>" y HASTA "H:<id>").
    // { [claveFila]: { regimenId, cajaId } }. Fuente primaria del prorrateo y el otorgante.
    const [marcasFila, setMarcasFila] = useState({});

    // ── Fecha de nacimiento (pedida en paso 2 para filtro de 18 años) ──
    const [fechaNacimientoPdf, setFechaNacimientoPdf] = useState('');
    // U1: toggle de formato de contadores (meses vs años+meses)
    const [mostrarAnios, setMostrarAnios] = useState(false);
    // Permite editar fecha/sexo inline desde el paso 3
    const [paso3EditarDatos, setPaso3EditarDatos] = useState(false);
    const [mostrarNuevaFilaResumen, setMostrarNuevaFilaResumen] = useState(false);
    const [nuevaFilaResumen, setNuevaFilaResumen] = useState({
        razonSocial: '',
        cuitCuenta: '',
        desde: '',
        hasta: '',
    });

    const [mostrarNuevaFilaHasta, setMostrarNuevaFilaHasta] = useState(false);
    const [nuevaFilaHasta, setNuevaFilaHasta] = useState({
        razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: ''
    });

    const [mostrarNuevaFilaDesde, setMostrarNuevaFilaDesde] = useState(false);
    const [nuevaFilaDesde, setNuevaFilaDesde] = useState({
        razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: ''
    });
    
    // ── Borrador ──
    const [criterioBorrador, setCriterioBorrador] = useState('');
    const [resultadosBorrador, setResultadosBorrador] = useState([]);
    const [busquedaBorradorStr, setBusquedaBorradorStr] = useState('');
    const [borradorGuardadoMsg, setBorradorGuardadoMsg] = useState(null);
    const [showCrearClienteModal, setShowCrearClienteModal] = useState(false);

    // ── E9: Carga manual sin archivo (historial sin aportes que el scanner no detecta) ──
    const [mostrarCargaManual, setMostrarCargaManual] = useState(false);
    const [manualApellido, setManualApellido] = useState('');
    const [manualNombre, setManualNombre] = useState('');
    const [manualCuil, setManualCuil] = useState('');
    const searchTimeout = useRef(null);
    const searchBorradorTimeout = useRef(null);
    const parseRequestIdRef = useRef(0);
    const printIframeRef = useRef(null);

    const resetStateForNewPdf = useCallback(() => {
        setPasoActual(0);
        setError(null);
        setSuccessMsg(null);
        setNombreCompleto('');
        setCuil('');
        setRegistros([]);
        setAnalisis(null);
        setResumenReglas({
            registrosClasificados: [],
            registrosComputables: [],
            registrosExcluidos: [],
            alertas: [],
        });
        setRegistrosHasta0694([]);
        setRegistrosDesde0794([]);
        setAjustesDesde0794([]);
        setTabActiva('resumen');
        setCorrecciones([]);
        setTotalMesesComputables(0);
        setTotalMesesNoComputables(0);
        setMesesComputablesBase([]);

        // Datos de importacion/auxiliares dependen del PDF actual.
        setClienteSeleccionado(null);
        setImportacionResultado(null);
        setChequeoRtiIncluirExistentes(false);
        setChequeoRtiCargando(false);
        setChequeoRtiResultado(null);
        setChequeoRtiError(null);
        setAportesActualesCliente(null);
        setModalReemplazoOpen(false);
        setResumenReemplazo(null);
        setMesesPendientesImportacion([]);
        setMoratoriaFechaNacimiento('');
        setMoratoriaSexo('');
        setMoratoriaHijos([]);
        setMoratoriaEstrategiasSeleccion({ ley24476: 0, monotributoFuturo: 0, ley27705: 0 });
        setAporteFuturoSeleccionado(false);
        setVentanasEstrategiasOverride({
            ley24476: { inicioYm: '', finYm: '' },
            monotributoFuturo: { inicioYm: '', finYm: '' },
            ley27705: { inicioYm: '', finYm: '' },
        });
        setMostrarEditarVentanas(false);
        setMostrarCostos(false);
        setCostos({ ley24476Total: '', monotributoMensual: '', ley27705Mensual: '' });
        setManualPeriodosSicam([]);
        setAsignacionesRegimen([]);
        setAsignacionesCaja([]);
        setMarcasFila({});
        setManualDesdeInput('');
        setManualHastaInput('');
        setManualTipoInput('MONOTRIBUTO');
        setMostrarNuevaFilaResumen(false);
        setNuevaFilaResumen({ razonSocial: '', cuitCuenta: '', desde: '', hasta: '' });

        setMostrarNuevaFilaHasta(false);
        setNuevaFilaHasta({ razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: '' });

        setMostrarNuevaFilaDesde(false);
        setNuevaFilaDesde({ razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: '' });

        setBorradorGuardadoMsg(null);
        // No reseteamos fechaNacimientoPdf: el usuario la pone una vez y sirve para múltiples PDFs.
    }, []);

    // Primer mes computable = mes en que la persona cumple 18 años
    const primerMesComputableYm = useMemo(
        () => calcularPrimerMesComputable(fechaNacimientoPdf),
        [fechaNacimientoPdf]
    );

    // Tipo SICAM manual que NO es aporte: ocupa lugar (bloquea moratorias y estrategias) pero no
    // suma como mes computable, no se importa y no entra en los totales ni en régimen/caja.
    const TIPO_NO_APORTANTE = 'NO_APORTANTE';

    // Solo los SICAM que son aportes (todos menos "No aportante"): estos sí son computables.
    const manualMesesSicamExpandidos = useMemo(() => {
        const set = new Set();
        for (const periodo of manualPeriodosSicam || []) {
            if (periodo?.tipo === TIPO_NO_APORTANTE) continue;
            const meses = expandirRangoYearMonth(periodo?.desde, periodo?.hasta);
            for (const ym of meses) set.add(ym);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [manualPeriodosSicam]);

    // Mapa mes -> subtipo del SICAM manual (MONOTRIBUTO / AUTONOMO / LEY_24476). Se usa al importar
    // para preservar el tipo real en vez de aplanar todo a 'SICAM'. Necesario para la regularidad de
    // minusvalía, que distingue los comprados (Ley 24.476/27.705) de los aportes efectivos.
    const manualSicamTipoPorMes = useMemo(() => {
        const map = {};
        for (const periodo of manualPeriodosSicam || []) {
            if (periodo?.tipo === TIPO_NO_APORTANTE) continue;
            const meses = expandirRangoYearMonth(periodo?.desde, periodo?.hasta);
            for (const ym of meses) map[ym] = periodo?.tipo || 'SICAM';
        }
        return map;
    }, [manualPeriodosSicam]);

    // Meses "No aportante": se descuentan de las ventanas de moratorias/estrategias.
    const manualMesesNoAportanteExpandidos = useMemo(() => {
        const set = new Set();
        for (const periodo of manualPeriodosSicam || []) {
            if (periodo?.tipo !== TIPO_NO_APORTANTE) continue;
            for (const ym of expandirRangoYearMonth(periodo?.desde, periodo?.hasta)) set.add(ym);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [manualPeriodosSicam]);

    // M2: meses cargados como tipo LEY_24476 — sirven para restar de la ventana disponible
    const manualMesesSicamLey24476Expandidos = useMemo(() => {
        const set = new Set();
        for (const periodo of manualPeriodosSicam || []) {
            if (periodo.tipo !== 'LEY_24476') continue;
            const meses = expandirRangoYearMonth(periodo?.desde, periodo?.hasta);
            for (const ym of meses) set.add(ym);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [manualPeriodosSicam]);

    const calculoDefinitivoConSicam = useMemo(() => {
        return calcularMesesDefinitivosConSicam({
            mesesExpandidos: analisis?.mesesExpandidos || [],
            ajustesDesde0794: ajustesDesde0794 || [],
            manualMesesSicam: manualMesesSicamExpandidos,
            mesesComputablesBase: mesesComputablesBase || [],
        });
    }, [analisis, ajustesDesde0794, manualMesesSicamExpandidos, mesesComputablesBase]);

    const resumenComputablesPaso3 = useMemo(() => {
        const analisisHasta = analizarRegistrosHasta0694(registrosHasta0694, primerMesComputableYm);
        const mesesHasta = analisisHasta.totalMesesUnicos;

        const mesesPost94ResumenSet = new Set(
            (analisis?.mesesExpandidos || [])
                .map((m) => normalizarYearMonth(m.yearMonth))
                .filter((ym) => ym && ym >= '1994-07')
        );
        const mesesDesdeNoComputablesAplicados = new Set(
            (ajustesDesde0794 || [])
                .map((a) => normalizarYearMonth(a.periodo))
                .filter((ym) => ym && mesesPost94ResumenSet.has(ym))
        ).size;
        const mesesDesdeComputables = Math.max(0, mesesPost94ResumenSet.size - mesesDesdeNoComputablesAplicados);

        // E2: cuando HASTA 06/94 está vacío, usar meses pre-94 del Resumen (misma lógica que cruzarSecciones).
        const mesesPre94ResumenCount = (analisis?.mesesExpandidos || [])
            .filter(m => {
                const ym = normalizarYearMonth(m?.yearMonth);
                return ym && ym < '1994-07' && (!primerMesComputableYm || ym >= primerMesComputableYm);
            }).length;
        const mesesPre94Efectivos = mesesHasta > 0 ? mesesHasta : mesesPre94ResumenCount;

        const mesesNuevosAportadosPorSicam = Math.max(
            0,
            (calculoDefinitivoConSicam.mesesComputablesDefinitivos || []).length -
            (calculoDefinitivoConSicam.totalMesesComputablesAutomaticos || 0)
        );
        // mesesRelDepSinSicam: solo para el desglose visual (HASTA + post-94), sin UCAP/Subsidio/SICAM
        const mesesRelDepSinSicam = mesesPre94Efectivos + mesesDesdeComputables;
        // totalMesesDefinitivos: usa totalMesesComputablesAutomaticos (que ya incluye UCAP/Subsidio) + SICAM nuevo
        const totalMesesDefinitivos = (calculoDefinitivoConSicam.totalMesesComputablesAutomaticos || mesesRelDepSinSicam) + mesesNuevosAportadosPorSicam;

        return {
            mesesHasta,
            mesesHastaSuperpuestos: analisisHasta.mesesSuperpuestos,
            mesesExcluidosPorEdad: analisisHasta.mesesExcluidosPorEdad || 0,
            mesesDesdeComputables,
            mesesRelDepSinSicam,
            mesesNuevosAportadosPorSicam,
            totalMesesDefinitivos,
            mesesPre94Efectivos,
        };
    }, [registrosHasta0694, analisis, ajustesDesde0794, calculoDefinitivoConSicam, primerMesComputableYm]);

    // Marca de régimen / caja por mes (YYYY-MM), derivada de las marcas por fila del Paso 2.
    // Fallback: las asignaciones por rango del Paso 3 (solo para el caso sin filas cargadas)
    // completan los meses que ninguna fila marcó.
    const marcasPorMes = useMemo(() => {
        const regimen = {};
        const caja = {};
        const aplicar = (meses, marca) => {
            for (const ym of meses) {
                if (marca.regimenId) regimen[ym] = String(marca.regimenId);
                if (marca.cajaId) caja[ym] = String(marca.cajaId);
            }
        };
        for (const reg of registros || []) {
            const marca = marcasFila[`R:${reg.indiceOriginal}`];
            if (marca && (marca.regimenId || marca.cajaId)) aplicar(mesesDeFilaResumen(reg), marca);
        }
        for (const reg of registrosHasta0694 || []) {
            const marca = marcasFila[`H:${reg.indiceOriginal}`];
            if (marca && (marca.regimenId || marca.cajaId)) aplicar(mesesDeFilaHasta(reg), marca);
        }
        for (const asig of asignacionesRegimen || []) {
            if (!asig?.regimenId || !asig?.desde) continue;
            for (const ym of expandirRangoYearMonth(asig.desde, asig.hasta || asig.desde)) if (!regimen[ym]) regimen[ym] = String(asig.regimenId);
        }
        for (const asig of asignacionesCaja || []) {
            if (!asig?.cajaId || !asig?.desde) continue;
            for (const ym of expandirRangoYearMonth(asig.desde, asig.hasta || asig.desde)) if (!caja[ym]) caja[ym] = String(asig.cajaId);
        }
        return { regimen, caja };
    }, [registros, registrosHasta0694, marcasFila, asignacionesRegimen, asignacionesCaja]);

    // Prorrateo de edad y servicios requeridos combinando regímenes diferenciales con el general.
    const prorrateoRegimenes = useMemo(() => {
        if (!moratoriaSexo) return null;
        const computablesSet = new Set((calculoDefinitivoConSicam.mesesComputablesDefinitivos || []).map((m) => normalizarYearMonth(m)).filter(Boolean));
        if (computablesSet.size === 0) return null;

        const conteo = {}; // regimenId -> meses computables marcados
        for (const ym of computablesSet) {
            const rid = marcasPorMes.regimen[ym];
            if (rid) conteo[rid] = (conteo[rid] || 0) + 1;
        }

        const regimenesConMeses = (regimenesDisponibles || [])
            .filter((r) => conteo[String(r.id)] > 0)
            .map((r) => ({
                nombre: r.nombre,
                edadMinima: r.edadMinima,
                serviciosMinimosMeses: r.serviciosMinimosMeses,
                alertaPersonalizada: r.alertaPersonalizada,
                meses: conteo[String(r.id)],
            }));

        if (regimenesConMeses.length === 0) return null;

        const totalDiferenciales = regimenesConMeses.reduce((a, r) => a + r.meses, 0);
        const mesesGenerales = Math.max(0, computablesSet.size - totalDiferenciales);

        const resultado = calcularProrrateoRegimenes({ sexo: moratoriaSexo, mesesGenerales, regimenesConMeses });
        return { ...resultado, regimenesConMeses, mesesGenerales };
    }, [moratoriaSexo, calculoDefinitivoConSicam, marcasPorMes, regimenesDisponibles]);

    // Fecha (MM/AAAA) en la que la persona cumpliría la edad requerida prorrateada.
    const fechaCumpleEdadRequerida = useMemo(() => {
        if (!prorrateoRegimenes?.aplica || !prorrateoRegimenes.edadRequeridaMeses) return null;
        const partes = (moratoriaFechaNacimiento || '').split('-');
        if (partes.length !== 3) return null;
        const y = parseInt(partes[0], 10);
        const mm = parseInt(partes[1], 10);
        if (!Number.isFinite(y) || !Number.isFinite(mm)) return null;
        const total = (y * 12 + (mm - 1)) + prorrateoRegimenes.edadRequeridaMeses;
        return `${String((total % 12) + 1).padStart(2, '0')}/${Math.floor(total / 12)}`;
    }, [prorrateoRegimenes, moratoriaFechaNacimiento]);

    // Caja otorgante (F3): quién otorga según qué caja tiene más meses.
    const cajaOtorgante = useMemo(() => {
        const computablesSet = new Set((calculoDefinitivoConSicam.mesesComputablesDefinitivos || []).map((m) => normalizarYearMonth(m)).filter(Boolean));
        if (computablesSet.size === 0) return null;

        const conteo = {};
        for (const ym of computablesSet) {
            const cid = marcasPorMes.caja[ym];
            if (cid) conteo[cid] = (conteo[cid] || 0) + 1;
        }

        const totalProvinciales = Object.values(conteo).reduce((a, b) => a + b, 0);
        const mesesGenerales = Math.max(0, computablesSet.size - totalProvinciales);
        return calcularCajaOtorgante({ mesesGenerales, mesesPorCaja: conteo });
    }, [calculoDefinitivoConSicam, marcasPorMes]);

    // Resumen de las marcas por fila (para la alerta del Paso 2).
    const resumenMarcas = useMemo(() => {
        const regimenCount = {};
        const cajaCount = {};
        for (const m of Object.values(marcasFila || {})) {
            if (m.regimenId) regimenCount[m.regimenId] = (regimenCount[m.regimenId] || 0) + 1;
            if (m.cajaId) cajaCount[m.cajaId] = (cajaCount[m.cajaId] || 0) + 1;
        }
        const regimenes = Object.entries(regimenCount).map(([id, n]) => ({
            nombre: (regimenesDisponibles || []).find((r) => String(r.id) === id)?.nombre || `Régimen ${id}`, filas: n,
        }));
        const cajas = Object.entries(cajaCount).map(([id, n]) => {
            const c = CAJAS_PROVINCIALES.find((x) => x.id === id);
            return { nombre: c?.nombre || id, esCordoba: !!c?.esCordoba, filas: n };
        });
        return { regimenes, cajas, hay: regimenes.length > 0 || cajas.length > 0 };
    }, [marcasFila, regimenesDisponibles]);

    // Sin filas cargadas (ni Resumen ni HASTA): habilita el fallback de asignación por rango en el Paso 3.
    const sinFilasCargadas = (registros?.length || 0) === 0 && (registrosHasta0694?.length || 0) === 0;

    const analisisMoratorias = useMemo(() => {
        if (!moratoriaFechaNacimiento || !moratoriaSexo) return null;

        // E4: pasar meses únicos de HASTA 06/94 para calcular correctamente la ventana Ley 24.476
        const analisisHastaParaMoratoria = analizarRegistrosHasta0694(registrosHasta0694, primerMesComputableYm);

        // Con regímenes diferenciales, el requisito de servicios y la edad se prorratean.
        const prorrateoAplica = Boolean(prorrateoRegimenes?.aplica);

        return calcularOpcionesComplementoAportes({
            fechaNacimiento: moratoriaFechaNacimiento,
            sexo: moratoriaSexo,
            hijos: moratoriaHijos,
            totalComputablesOverride: resumenComputablesPaso3.totalMesesDefinitivos,
            estrategiasSeleccionadas: moratoriaEstrategiasSeleccion,
            mesesComputablesDefinitivos: calculoDefinitivoConSicam.mesesComputablesDefinitivos || [],
            fechaActual: new Date(),
            mesesHasta0694: analisisHastaParaMoratoria.mesesUnicos,
            manualMesesSicamLey24476: manualMesesSicamLey24476Expandidos,
            mesesNoAportante: manualMesesNoAportanteExpandidos,
            ventanasOverride: ventanasEstrategiasOverride,
            serviciosRequeridosMeses: prorrateoAplica ? prorrateoRegimenes.serviciosRequeridosMeses : 360,
            // Edad requerida redondeada al mes (ceil): se usa así para la fecha de jubilación y el exceso.
            edadRequeridaAnios: prorrateoAplica ? (prorrateoRegimenes.edadRequeridaMeses / 12) : null,
            esJubilacionProvincial: Boolean(cajaOtorgante?.otorganteEsCordoba),
        });
    }, [moratoriaFechaNacimiento, moratoriaSexo, moratoriaHijos, moratoriaEstrategiasSeleccion, calculoDefinitivoConSicam, resumenComputablesPaso3, registrosHasta0694, primerMesComputableYm, manualMesesSicamLey24476Expandidos, manualMesesNoAportanteExpandidos, ventanasEstrategiasOverride, prorrateoRegimenes, cajaOtorgante]);

    // Catálogo de regímenes diferenciales activos + empleadores de caja aprendidos.
    useEffect(() => {
        let activo = true;
        DatosMaestrosService.getRegimenesDiferenciales(true)
            .then((resp) => { if (activo) setRegimenesDisponibles(Array.isArray(resp.data) ? resp.data : []); })
            .catch(() => { if (activo) setRegimenesDisponibles([]); });
        obtenerCajasAprendidas()
            .then((data) => { if (activo) setCajasAprendidas(Array.isArray(data) ? data : []); })
            .catch(() => { if (activo) setCajasAprendidas([]); });
        return () => { activo = false; };
    }, []);

    const agregarAsignacionRegimen = useCallback(() => {
        const desde = normalizarYearMonth(regimenAsignInput.desde);
        const hasta = normalizarYearMonth(regimenAsignInput.hasta || regimenAsignInput.desde);
        if (!regimenAsignInput.regimenId) { setError('Elegí un régimen diferencial para asignar.'); return; }
        if (!desde || !hasta) { setError('Rango de régimen inválido. Usá MM/AAAA o AAAA-MM.'); return; }
        if (desde > hasta) { setError('En la asignación de régimen, Desde no puede ser mayor que Hasta.'); return; }
        setAsignacionesRegimen((prev) => [...prev, {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            desde, hasta, regimenId: String(regimenAsignInput.regimenId),
        }]);
        setRegimenAsignInput({ desde: '', hasta: '', regimenId: '' });
        setError(null);
    }, [regimenAsignInput]);

    const eliminarAsignacionRegimen = useCallback((id) => {
        setAsignacionesRegimen((prev) => prev.filter((a) => a.id !== id));
    }, []);

    const agregarAsignacionCaja = useCallback(() => {
        const desde = normalizarYearMonth(cajaAsignInput.desde);
        const hasta = normalizarYearMonth(cajaAsignInput.hasta || cajaAsignInput.desde);
        if (!cajaAsignInput.cajaId) { setError('Elegí una caja provincial para asignar.'); return; }
        if (!desde || !hasta) { setError('Rango de caja inválido. Usá MM/AAAA o AAAA-MM.'); return; }
        if (desde > hasta) { setError('En la asignación de caja, Desde no puede ser mayor que Hasta.'); return; }
        setAsignacionesCaja((prev) => [...prev, {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            desde, hasta, cajaId: String(cajaAsignInput.cajaId),
        }]);
        setCajaAsignInput({ desde: '', hasta: '', cajaId: '' });
        setError(null);
    }, [cajaAsignInput]);

    const eliminarAsignacionCaja = useCallback((id) => {
        setAsignacionesCaja((prev) => prev.filter((a) => a.id !== id));
    }, []);

    // Marca una fila (Resumen "R:<id>" / HASTA "H:<id>") con un régimen o caja ('' = quitar).
    const setMarcaFila = useCallback((clave, tipo, value) => {
        setMarcasFila((prev) => {
            const actual = prev[clave] || { regimenId: '', cajaId: '' };
            const nuevo = { ...actual, [tipo]: value };
            const next = { ...prev };
            if (!nuevo.regimenId && !nuevo.cajaId) delete next[clave];
            else next[clave] = nuevo;
            return next;
        });
    }, []);

    // Aplica la marca a TODAS las filas de la misma razón social (atajo "por empleador").
    const aplicarMarcaAEmpleador = useCallback((seccion, razonSocial, tipo, value) => {
        const razonNorm = normalizarEmpleador(razonSocial);
        if (!razonNorm) return;
        const filas = seccion === 'R' ? (registros || []) : (registrosHasta0694 || []);
        setMarcasFila((prev) => {
            const next = { ...prev };
            for (const reg of filas) {
                if (normalizarEmpleador(reg.razonSocial) !== razonNorm) continue;
                const clave = `${seccion}:${reg.indiceOriginal}`;
                const actual = next[clave] || { regimenId: '', cajaId: '' };
                const nuevo = { ...actual, [tipo]: value };
                if (!nuevo.regimenId && !nuevo.cajaId) delete next[clave];
                else next[clave] = nuevo;
            }
            return next;
        });
    }, [registros, registrosHasta0694]);

    // Celda con los dos selectores (régimen / caja) + atajo "aplicar a la razón social".
    const renderCeldaMarcas = (seccion, razonSocial, clave, marca) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <select value={marca.regimenId || ''} onChange={(e) => setMarcaFila(clave, 'regimenId', e.target.value)} style={{ fontSize: '0.72rem', padding: '2px 4px' }}>
                <option value="">Régimen…</option>
                {(regimenesDisponibles || []).map((r) => <option key={r.id} value={String(r.id)}>{r.nombre}</option>)}
            </select>
            <select value={marca.cajaId || ''} onChange={(e) => setMarcaFila(clave, 'cajaId', e.target.value)} style={{ fontSize: '0.72rem', padding: '2px 4px' }}>
                <option value="">Caja…</option>
                {CAJAS_PROVINCIALES.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            {(marca.regimenId || marca.cajaId) && (
                <button
                    type="button"
                    title="Aplicar estas marcas a todas las filas de esta razón social"
                    onClick={() => {
                        if (marca.regimenId) aplicarMarcaAEmpleador(seccion, razonSocial, 'regimenId', marca.regimenId);
                        if (marca.cajaId) aplicarMarcaAEmpleador(seccion, razonSocial, 'cajaId', marca.cajaId);
                    }}
                    style={{ fontSize: '0.66rem', border: '1px solid #ccc', borderRadius: '4px', background: '#f8fafc', cursor: 'pointer', padding: '1px 4px' }}
                >
                    <i className="bi bi-people-fill me-1"></i>a la razón social
                </button>
            )}
        </div>
    );

    // Empleadores (razón social) distintos del historial, de las secciones marcables + detalle DESDE.
    const empleadoresHistorial = useMemo(() => {
        const map = new Map(); // norm -> { norm, razonSocial }
        const add = (razonRaw) => {
            const razon = String(razonRaw || '').trim();
            if (!razon) return;
            const norm = normalizarEmpleador(razon);
            if (!map.has(norm)) map.set(norm, { norm, razonSocial: razon });
        };
        for (const r of registros || []) add(r?.razonSocial);
        for (const r of registrosHasta0694 || []) add(r?.razonSocial);
        for (const r of registrosDesde0794 || []) add(r?.razonSocial);
        return Array.from(map.values());
    }, [registros, registrosHasta0694, registrosDesde0794]);

    // Empleadores de este historial que ya fueron aprendidos como Córdoba.
    const sugerenciasCordoba = useMemo(() => {
        const aprendidasCordoba = new Map(
            (cajasAprendidas || [])
                .filter((a) => a.cajaId === 'CORDOBA')
                .map((a) => [a.empleadorNormalizado, a])
        );
        return empleadoresHistorial
            .filter((e) => aprendidasCordoba.has(e.norm))
            .map((e) => ({ ...e, vecesConfirmado: aprendidasCordoba.get(e.norm)?.vecesConfirmado || 1 }));
    }, [empleadoresHistorial, cajasAprendidas]);

    // Marca como Córdoba (en el Paso 2) todas las filas de los empleadores sugeridos (aprendidos).
    const asignarSugeridosCordoba = useCallback(() => {
        for (const e of sugerenciasCordoba) {
            aplicarMarcaAEmpleador('R', e.razonSocial, 'caja', 'CORDOBA');
            aplicarMarcaAEmpleador('H', e.razonSocial, 'caja', 'CORDOBA');
        }
    }, [sugerenciasCordoba, aplicarMarcaAEmpleador]);

    // Aprende como Córdoba la razón social de las filas marcadas como Córdoba (exacto, sin heurística).
    const aprenderEmpleadoresCordoba = useCallback(async () => {
        const empleadores = new Map(); // norm -> { razonSocial, cuit }
        const recolectar = (filas, seccion) => {
            for (const reg of filas || []) {
                const marca = marcasFila[`${seccion}:${reg.indiceOriginal}`];
                if (marca?.cajaId !== 'CORDOBA') continue;
                const razon = String(reg.razonSocial || '').trim();
                if (!razon) continue;
                const norm = normalizarEmpleador(razon);
                if (!empleadores.has(norm)) empleadores.set(norm, { razonSocial: razon, cuit: reg.cuitCuenta || reg.cuit || '' });
            }
        };
        recolectar(registros, 'R');
        recolectar(registrosHasta0694, 'H');

        const aAprender = Array.from(empleadores.values())
            .map((e) => ({ empleadorOriginal: e.razonSocial, cuit: e.cuit, cajaId: 'CORDOBA' }));
        if (aAprender.length === 0) {
            setAprendizajeMsg('No hay filas marcadas como Córdoba para aprender.');
            setTimeout(() => setAprendizajeMsg(null), 5000);
            return;
        }
        try {
            const guardadas = await aprenderCajasEmpleador(aAprender);
            const refetch = await obtenerCajasAprendidas();
            setCajasAprendidas(Array.isArray(refetch) ? refetch : []);
            setAprendizajeMsg(`Aprendidos ${guardadas.length} empleador(es) como Córdoba.`);
        } catch {
            setAprendizajeMsg('No se pudo guardar el aprendizaje.');
        }
        setTimeout(() => setAprendizajeMsg(null), 5000);
    }, [marcasFila, registros, registrosHasta0694]);

    // Actualización dinámica de meses computables cuando cambia la fecha de nacimiento.
    // Necesario porque totalMesesComputables es estado (no memo) y no se actualiza automáticamente.
    useEffect(() => {
        if (!analisis) return;
        recalcularCruce(
            analisis.mesesExpandidos || [],
            registrosHasta0694,
            ajustesDesde0794,
            resumenReglas?.registrosClasificados
        );
    // Solo se dispara cuando cambia primerMesComputableYm; el resto de valores se leen en el momento correcto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primerMesComputableYm]);

    // Actualización del contador cuando cambian los períodos SICAM manuales.
    // recalcularCruce incluye mesesSicamManualPre94 en el cómputo de cruzarSecciones.
    useEffect(() => {
        if (!analisis) return;
        recalcularCruce(
            analisis.mesesExpandidos || [],
            registrosHasta0694,
            ajustesDesde0794,
            resumenReglas?.registrosClasificados
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualMesesSicamExpandidos]);

    useEffect(() => {
        if (!analisisMoratorias?.estrategias?.maximos) return;
        setMoratoriaEstrategiasSeleccion((prev) => {
            const maximos = analisisMoratorias.estrategias.maximos;
            const clamp = (valor, maximo) => {
                const num = Number.isFinite(Number(valor)) ? Math.floor(Number(valor)) : 0;
                return Math.max(0, Math.min(num, maximo));
            };
            const next = {
                ley24476: clamp(prev.ley24476, maximos.ley24476 || 0),
                monotributoFuturo: clamp(prev.monotributoFuturo, maximos.monotributoFuturo || 0),
                ley27705: clamp(prev.ley27705, maximos.ley27705 || 0),
            };
            if (
                next.ley24476 === prev.ley24476 &&
                next.monotributoFuturo === prev.monotributoFuturo &&
                next.ley27705 === prev.ley27705
            ) {
                return prev;
            }
            return next;
        });
    }, [analisisMoratorias]);

    const toggleEstrategiaMoratoria = useCallback((campo, maximo, habilitada = true) => {
        if (!habilitada) return;
        const tope = Math.max(0, Number.isFinite(Number(maximo)) ? Math.floor(Number(maximo)) : 0);
        setMoratoriaEstrategiasSeleccion((prev) => {
            if (prev[campo] > 0) {
                return { ...prev, [campo]: 0 };
            }

            const faltantesBase = Math.max(0, Number(analisisMoratorias?.faltantesA360 || 0));
            const totalOtros = Object.entries(prev)
                .filter(([key]) => key !== campo)
                .reduce((acc, [, value]) => acc + Math.max(0, Number(value || 0)), 0);
            const restanteParaCampo = Math.max(0, faltantesBase - totalOtros);
            const efectivo = Math.min(tope, restanteParaCampo);

            return {
                ...prev,
                [campo]: efectivo,
            };
        });
    }, [analisisMoratorias]);

    // B1: actualizar override de ventana de una estrategia (desde/hasta en MM/YYYY).
    const actualizarVentanaOverride = useCallback((key, campo, valor) => {
        setVentanasEstrategiasOverride((prev) => ({
            ...prev,
            [key]: { ...prev[key], [campo]: valor },
        }));
    }, []);

    const restaurarVentanasPorDefecto = useCallback(() => {
        setVentanasEstrategiasOverride({
            ley24476: { inicioYm: '', finYm: '' },
            monotributoFuturo: { inicioYm: '', finYm: '' },
            ley27705: { inicioYm: '', finYm: '' },
        });
    }, []);

    const agregarHijoMoratoria = useCallback(() => {
        setMoratoriaHijos((prev) => [
            ...prev,
            {
                id: `hijo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                auh: false,
                discapacidad: false,
                adoptado: false,
            },
        ]);
    }, []);

    const quitarHijoMoratoria = useCallback((id) => {
        setMoratoriaHijos((prev) => prev.filter((hijo) => hijo.id !== id));
    }, []);

    const toggleHijoMoratoria = useCallback((id, campo, valor) => {
        setMoratoriaHijos((prev) => prev.map((hijo) => (
            hijo.id === id ? { ...hijo, [campo]: Boolean(valor) } : hijo
        )));
    }, []);

    const formatearFechaIso = useCallback((iso) => {
        if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '-';
        const [yyyy, mm, dd] = iso.split('-');
        return `${dd}/${mm}/${yyyy}`;
    }, []);

    // B2: parsea/formatea montos (carga manual). Devuelve número o NaN.
    const parsearMonto = useCallback((valor) => {
        const limpio = String(valor ?? '').replace(/[^\d.,]/g, '').replace(/\./g, '').replace(',', '.');
        const n = Number(limpio);
        return Number.isFinite(n) ? n : Number.NaN;
    }, []);
    const formatearMonto = useCallback((valor) => {
        const n = typeof valor === 'number' ? valor : parsearMonto(valor);
        if (!Number.isFinite(n)) return '';
        return `$${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
    }, [parsearMonto]);

    const formatearPeriodosComoRangos = useCallback((periodosYm = []) => {
        const ordenados = Array.from(new Set((periodosYm || []).filter((ym) => /^\d{4}-\d{2}$/.test(ym))))
            .sort((a, b) => a.localeCompare(b));
        if (!ordenados.length) return '-';

        const parseYm = (ym) => {
            const [y, m] = ym.split('-').map(Number);
            return { y, m };
        };
        const siguienteYm = (ym) => {
            const { y, m } = parseYm(ym);
            const d = new Date(y, m - 1, 1);
            d.setMonth(d.getMonth() + 1);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        };

        const rangos = [];
        let inicio = ordenados[0];
        let fin = ordenados[0];

        for (let i = 1; i < ordenados.length; i += 1) {
            const actual = ordenados[i];
            if (actual === siguienteYm(fin)) {
                fin = actual;
                continue;
            }
            rangos.push([inicio, fin]);
            inicio = actual;
            fin = actual;
        }
        rangos.push([inicio, fin]);

        return rangos
            .map(([desde, hasta]) => {
                const desdeFmt = formatearYearMonth(desde);
                const hastaFmt = formatearYearMonth(hasta);
                return desde === hasta ? desdeFmt : `${desdeFmt} a ${hastaFmt}`;
            })
            .join(' | ');
    }, []);

    const formatearDuracionMeses = useCallback((meses) => {
        const total = Math.max(0, Number.isFinite(Number(meses)) ? Math.floor(Number(meses)) : 0);
        const anios = Math.floor(total / 12);
        const restoMeses = total % 12;

        if (anios === 0) return `${restoMeses} mes${restoMeses === 1 ? '' : 'es'}`;
        if (restoMeses === 0) return `${anios} año${anios === 1 ? '' : 's'}`;
        return `${anios} año${anios === 1 ? '' : 's'} y ${restoMeses} mes${restoMeses === 1 ? '' : 'es'}`;
    }, []);

    // U1: formatea un número de meses según el toggle global.
    // Cuando mostrarAnios=false devuelve "N meses"; cuando=true devuelve "X años y Y meses".
    const formatearContador = useCallback((meses) => {
        const n = Math.max(0, Number.isFinite(Number(meses)) ? Math.floor(Number(meses)) : 0);
        if (mostrarAnios) return formatearDuracionMeses(n);
        return `${n} mes${n === 1 ? '' : 'es'}`;
    }, [mostrarAnios, formatearDuracionMeses]);

    // U1: toggle reutilizable
    const renderToggleAnios = useCallback(() => (
        <button
            type="button"
            title={mostrarAnios ? 'Ver en meses' : 'Ver en años y meses'}
            onClick={() => setMostrarAnios(p => !p)}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.18rem 0.55rem', borderRadius: '10px',
                background: mostrarAnios ? '#e3f2fd' : '#f5f5f5',
                border: `1px solid ${mostrarAnios ? '#1976d2' : '#ccc'}`,
                color: mostrarAnios ? '#1976d2' : '#777',
                fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                lineHeight: 1.4,
            }}
        >
            <i className={`bi bi-toggle-${mostrarAnios ? 'on' : 'off'}`}></i>
            {mostrarAnios ? 'años' : 'meses'}
        </button>
    ), [mostrarAnios]);

    // ══════════════════════════════════════
    // PASO 1: Carga de PDF
    // ══════════════════════════════════════
    const handleFile = useCallback(async (file) => {
        if (!file || file.type !== 'application/pdf') {
            setError('Por favor seleccione un archivo PDF válido.');
            return;
        }

        const requestId = parseRequestIdRef.current + 1;
        parseRequestIdRef.current = requestId;

        resetStateForNewPdf();
        setArchivo(file);
        setLoading(true);

        try {
            const resultado = await parsearPdf(file);
            if (requestId !== parseRequestIdRef.current) return;

            setNombreCompleto(resultado.nombreCompleto || '');
            const cuilCrudo = resultado.cuil || '';
            setCuil(cuilCrudo.replace(/\D/g, ''));

            // Resumen (reglas especiales aplicadas localmente)
            const reglas = aplicarReglasEspecialesResumen(resultado.registros || []);
            const analisisResumen = expandirYAnalizar(reglas.registrosComputables || []);
            setRegistros(reglas.registrosClasificados || []);
            setResumenReglas(reglas);
            setAnalisis(analisisResumen);

            // HASTA 06/94
            setRegistrosHasta0694(resultado.registrosHasta0694 || []);

            // DESDE 07/94 (prioriza backend; fallback frontend si viene texto completo)
            let desdeRows = resultado.registrosDesde0794 || [];
            let desdeAjustes = resultado.ajustesDesde0794 || [];
            if ((desdeRows.length === 0 && desdeAjustes.length === 0) && resultado.textoCompleto) {
                desdeRows = parseDesde0794Section(resultado.textoCompleto);
                desdeAjustes = getResumenAdjustmentsFromDesde0794(desdeRows);
            }
            
            desdeRows = desdeRows.map((r, i) => ({
                ...r,
                indiceOriginal: r.indiceOriginal != null ? r.indiceOriginal : i
            }));

            setRegistrosDesde0794(desdeRows);
            setAjustesDesde0794(desdeAjustes);

            // Correcciones y totales definitivos (recalcular local con DESDE 07/94 si aplica)
            const cruce = cruzarSecciones({
                mesesExpandidos: analisisResumen.mesesExpandidos || [],
                registrosHasta0694: resultado.registrosHasta0694 || [],
                ajustesDesde0794: desdeAjustes,
                registrosClasificados: reglas.registrosClasificados || [],
                manualMesesSicamExpandidos: [],
                primerMesComputableYm: calcularPrimerMesComputable(fechaNacimientoPdf),
            });
            setCorrecciones(cruce.correcciones || []);
            setTotalMesesComputables(cruce.totalMesesComputables || 0);
            setTotalMesesNoComputables(cruce.totalMesesNoComputables || 0);
            setMesesComputablesBase(cruce.mesesComputablesBase || []);

            // Decidir tab inicial
            if (resultado.registros && resultado.registros.length > 0) {
                setTabActiva('resumen');
            } else if ((resultado.registrosHasta0694 || []).length > 0) {
                setTabActiva('hasta0694');
            } else {
                setTabActiva('desde0794');
            }

            setPasoActual(1);
        } catch (err) {
            if (requestId !== parseRequestIdRef.current) return;
            const msg = err.response?.data || err.message || 'Error al procesar el PDF';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            if (requestId === parseRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [resetStateForNewPdf]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const handleFileInput = useCallback((e) => {
        const file = e.target.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    // ── E9: Carga manual sin archivo ──
    // Normaliza a Title Case igual que el backend (normalizarNombre): primera letra en
    // mayúscula y el resto en minúscula por palabra. Así el nombre queda con el mismo
    // formato que el que se extrae automáticamente del PDF.
    const normalizarNombreManual = useCallback((texto) => {
        return String(texto || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((palabra) => (
                palabra.length > 1
                    ? palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase()
                    : palabra.toUpperCase()
            ))
            .join(' ');
    }, []);

    const handleCargaManualSinArchivo = useCallback(() => {
        const apellido = manualApellido.trim();
        const nombre = manualNombre.trim();
        const cuilDigits = manualCuil.replace(/\D/g, '');

        if (!apellido || !nombre) {
            setError('Completá apellido y nombre para continuar sin archivo.');
            return;
        }
        if (cuilDigits.length !== 11) {
            setError('El CUIL debe tener 11 dígitos.');
            return;
        }

        // Invalidar cualquier parseo de PDF en vuelo para que no pise este estado.
        parseRequestIdRef.current += 1;

        resetStateForNewPdf();
        setArchivo(null);

        setNombreCompleto(normalizarNombreManual(`${apellido} ${nombre}`));
        setCuil(cuilDigits);

        // Secciones vacías + análisis vacío para habilitar el paso 2. El usuario cargará
        // las filas a mano con los botones "Agregar" de cada tab.
        const reglas = aplicarReglasEspecialesResumen([]);
        const analisisVacio = expandirYAnalizar([]);
        setRegistros([]);
        setResumenReglas(reglas);
        setAnalisis(analisisVacio);
        setRegistrosHasta0694([]);
        setRegistrosDesde0794([]);
        setAjustesDesde0794([]);
        setCorrecciones([]);
        setTotalMesesComputables(0);
        setTotalMesesNoComputables(0);
        setMesesComputablesBase([]);
        setTabActiva('resumen');

        setMostrarCargaManual(false);
        setManualApellido('');
        setManualNombre('');
        setManualCuil('');
        setPasoActual(1);
    }, [manualApellido, manualNombre, manualCuil, normalizarNombreManual, resetStateForNewPdf]);

    // ══════════════════════════════════════
    // BORRADORES
    // ══════════════════════════════════════
    const handleGuardarBorrador = useCallback(async () => {
        if (!cuil) {
            setError('No hay un CUIL detectado para guardar el borrador.');
            return;
        }
        setLoading(true);
        setError(null);
        setBorradorGuardadoMsg(null);
        try {
            const borradorData = {
                nombreCompleto,
                cuil,
                registros,
                analisis,
                resumenReglas,
                registrosHasta0694,
                registrosDesde0794,
                ajustesDesde0794,
                correcciones,
                totalMesesComputables,
                totalMesesNoComputables,
                mesesComputablesBase,
                manualPeriodosSicam,
                asignacionesRegimen,
                asignacionesCaja,
                marcasFila,
                fechaNacimientoPdf,
                moratoriaFechaNacimiento,
                moratoriaSexo,
                moratoriaHijos,
                moratoriaEstrategiasSeleccion,
                chequeoRtiIncluirExistentes
            };
            await guardarBorrador(cuil, borradorData, nombreCompleto);
            setBorradorGuardadoMsg('Borrador guardado exitosamente.');
            setTimeout(() => setBorradorGuardadoMsg(null), 4000);
        } catch (err) {
            const msg = err.response?.data || err.message || 'Error al guardar el borrador';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    }, [cuil, nombreCompleto, registros, analisis, resumenReglas, registrosHasta0694, registrosDesde0794, ajustesDesde0794, correcciones, totalMesesComputables, totalMesesNoComputables, mesesComputablesBase, manualPeriodosSicam, asignacionesRegimen, asignacionesCaja, marcasFila, fechaNacimientoPdf, moratoriaFechaNacimiento, moratoriaSexo, moratoriaHijos, moratoriaEstrategiasSeleccion, chequeoRtiIncluirExistentes]);

    const handleBuscarBorrador = useCallback((criterio) => {
        setBusquedaBorradorStr(criterio);
        setCriterioBorrador('');
        if (searchBorradorTimeout.current) clearTimeout(searchBorradorTimeout.current);
        if (!criterio || criterio.trim().length < 2) {
            setResultadosBorrador([]);
            return;
        }
        searchBorradorTimeout.current = setTimeout(async () => {
            try {
                const res = await buscarBorrador(criterio);
                setResultadosBorrador(res || []);
            } catch { setResultadosBorrador([]); }
        }, 350);
    }, []);

    const handleSeleccionarBorrador = useCallback((borrador) => {
        setCriterioBorrador(borrador.cuil);
        setBusquedaBorradorStr(`${borrador.nombreCompleto || 'Desconocido'} - ${borrador.cuil}`);
        setResultadosBorrador([]);
    }, []);

    const handleCargarBorrador = useCallback(async () => {
        const cuilLimpio = criterioBorrador.trim().replace(/\D/g, '');
        if (!cuilLimpio || cuilLimpio.length < 10) {
            setError('Debe ingresar un CUIL válido para buscar el borrador.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const datos = await obtenerBorrador(criterioBorrador.trim());
            if (!datos) {
                setError('No se encontró un borrador para este CUIL.');
                return;
            }

            resetStateForNewPdf();
            
            // Restaurar estado
            setNombreCompleto(datos.nombreCompleto || '');
            const cLimpio2 = datos.cuil ? datos.cuil.toString().replace(/\D/g, '') : '';
            setCuil(cLimpio2);
            setRegistros(datos.registros || []);
            setAnalisis(datos.analisis || { mesesExpandidos: [], solapamientos: [], totalMesesUnicos: 0, totalMesesSuperpuestos: 0 });
            setResumenReglas(datos.resumenReglas || { registrosClasificados: [], registrosComputables: [], registrosExcluidos: [], alertas: [] });
            setRegistrosHasta0694(datos.registrosHasta0694 || []);
            setRegistrosDesde0794(datos.registrosDesde0794 || []);
            setAjustesDesde0794(datos.ajustesDesde0794 || []);
            setCorrecciones(datos.correcciones || []);
            setTotalMesesComputables(datos.totalMesesComputables || 0);
            setTotalMesesNoComputables(datos.totalMesesNoComputables || 0);
            setMesesComputablesBase(datos.mesesComputablesBase || []);
            setManualPeriodosSicam(datos.manualPeriodosSicam || []);
            setAsignacionesRegimen(datos.asignacionesRegimen || []);
            setAsignacionesCaja(datos.asignacionesCaja || []);
            setMarcasFila(datos.marcasFila || {});
            
            // Estado de Step 3
            if (datos.fechaNacimientoPdf) setFechaNacimientoPdf(datos.fechaNacimientoPdf);
            if (datos.moratoriaFechaNacimiento) setMoratoriaFechaNacimiento(datos.moratoriaFechaNacimiento);
            if (datos.moratoriaSexo) setMoratoriaSexo(datos.moratoriaSexo);
            if (datos.moratoriaHijos) setMoratoriaHijos(datos.moratoriaHijos);
            if (datos.moratoriaEstrategiasSeleccion) setMoratoriaEstrategiasSeleccion(datos.moratoriaEstrategiasSeleccion);
            setChequeoRtiIncluirExistentes(Boolean(datos.chequeoRtiIncluirExistentes));
            setChequeoRtiResultado(null);
            setChequeoRtiError(null);

            setTabActiva('resumen');
            setPasoActual(1);
            setSuccessMsg('Borrador cargado exitosamente.');
            setTimeout(() => setSuccessMsg(null), 4000);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('No se encontró un borrador para este CUIL.');
            } else {
                const msg = err.response?.data || err.message || 'Error al cargar el borrador';
                setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
        } finally {
            setLoading(false);
        }
    }, [criterioBorrador, resetStateForNewPdf]);


    // ══════════════════════════════════════
    // RECALCULAR CRUCE INTEGRADO
    // ══════════════════════════════════════
    // registrosClasificadosOverride: permite que los handlers de edición pasen el valor fresco
    // sin depender del estado (que aún no se actualizó al momento del call dentro del updater).
    const recalcularCruce = useCallback((mesesExp, regHasta, ajustesDesde = ajustesDesde0794, registrosClasificadosOverride = null) => {
        const cruce = cruzarSecciones({
            mesesExpandidos: mesesExp,
            registrosHasta0694: regHasta,
            ajustesDesde0794: ajustesDesde,
            registrosClasificados: registrosClasificadosOverride ?? resumenReglas?.registrosClasificados ?? [],
            manualMesesSicamExpandidos,
            primerMesComputableYm,
        });
        setCorrecciones(cruce.correcciones || []);
        setTotalMesesComputables(cruce.totalMesesComputables || 0);
        setTotalMesesNoComputables(cruce.totalMesesNoComputables || 0);
        setMesesComputablesBase(cruce.mesesComputablesBase || []);
    }, [ajustesDesde0794, resumenReglas, manualMesesSicamExpandidos, primerMesComputableYm]);

    // ══════════════════════════════════════
    // PASO 2: Edición — Resumen
    // ══════════════════════════════════════
    const handleEditRegistro = useCallback((indice, campo, valor) => {
        setRegistros(prev => {
            const edited = prev.map(r =>
                r.indiceOriginal === indice ? { ...r, [campo]: valor } : r
            );
            const reglas = aplicarReglasEspecialesResumen(edited);
            const updated = reglas.registrosClasificados;
            const nuevoAnalisis = expandirYAnalizar(reglas.registrosComputables || []);
            setResumenReglas(reglas);
            setAnalisis(nuevoAnalisis);
            recalcularCruce(nuevoAnalisis.mesesExpandidos, registrosHasta0694, ajustesDesde0794, reglas.registrosClasificados);
            return updated;
        });
    }, [recalcularCruce, registrosHasta0694, ajustesDesde0794]);

    const handleEliminarRegistro = useCallback((indice) => {
        setRegistros(prev => {
            const edited = prev.filter(r => r.indiceOriginal !== indice);
            const reglas = aplicarReglasEspecialesResumen(edited);
            const updated = reglas.registrosClasificados;
            const nuevoAnalisis = expandirYAnalizar(reglas.registrosComputables || []);
            setResumenReglas(reglas);
            setAnalisis(nuevoAnalisis);
            recalcularCruce(nuevoAnalisis.mesesExpandidos, registrosHasta0694, ajustesDesde0794, reglas.registrosClasificados);
            return updated;
        });
    }, [recalcularCruce, registrosHasta0694, ajustesDesde0794]);

    const handleIniciarNuevaFilaResumen = useCallback(() => {
        setMostrarNuevaFilaResumen(true);
        setNuevaFilaResumen({ razonSocial: '', cuitCuenta: '', desde: '', hasta: '' });
    }, []);

    const handleCancelarNuevaFilaResumen = useCallback(() => {
        setMostrarNuevaFilaResumen(false);
        setNuevaFilaResumen({ razonSocial: '', cuitCuenta: '', desde: '', hasta: '' });
        setError(null);
    }, []);

    const handleGuardarNuevaFilaResumen = useCallback(() => {
        const { razonSocial, cuitCuenta, desde, hasta } = nuevaFilaResumen;
        if (!razonSocial?.trim()) {
            setError('Completá la Razón Social para agregar el período al resumen.');
            return;
        }
        if (!validarPeriodo(desde) || !validarPeriodo(hasta) || !validarRango(desde, hasta)) {
            setError('Período inválido en la nueva fila. Usá formato MM/YYYY y verificá que Desde <= Hasta.');
            return;
        }

        setRegistros((prev) => {
            const nextIndice = prev.reduce((max, r) => Math.max(max, Number(r?.indiceOriginal ?? -1)), -1) + 1;
            const nuevoRegistro = {
                indiceOriginal: nextIndice,
                razonSocial: razonSocial.trim(),
                cuitCuenta: cuitCuenta.trim(),
                desde,
                hasta,
            };

            const edited = [...prev, nuevoRegistro];
            const reglas = aplicarReglasEspecialesResumen(edited);
            const updated = reglas.registrosClasificados;
            const nuevoAnalisis = expandirYAnalizar(reglas.registrosComputables || []);
            setResumenReglas(reglas);
            setAnalisis(nuevoAnalisis);
            recalcularCruce(nuevoAnalisis.mesesExpandidos, registrosHasta0694, ajustesDesde0794, reglas.registrosClasificados);
            return updated;
        });

        setMostrarNuevaFilaResumen(false);
        setNuevaFilaResumen({ razonSocial: '', cuitCuenta: '', desde: '', hasta: '' });
        setError(null);
    }, [nuevaFilaResumen, recalcularCruce, registrosHasta0694]);

    // ══════════════════════════════════════
    // PASO 2: Edición — HASTA 06/94
    // ══════════════════════════════════════
    const handleEditHasta0694 = useCallback((indice, campo, valor) => {
        setRegistrosHasta0694(prev => {
            const updated = prev.map((r) => {
                if (r.indiceOriginal !== indice) return r;
                if (campo !== 'mesesTrabajados') return { ...r, [campo]: valor };
                const numero = Number.parseInt(String(valor || '').trim(), 10);
                return { ...r, mesesTrabajados: Number.isFinite(numero) ? numero : null };
            });
            recalcularCruce(analisis?.mesesExpandidos || [], updated);
            return updated;
        });
    }, [recalcularCruce, analisis]);

    const handleEliminarHasta0694 = useCallback((indice) => {
        setRegistrosHasta0694(prev => {
            const updated = prev.filter(r => r.indiceOriginal !== indice);
            recalcularCruce(analisis?.mesesExpandidos || [], updated);
            return updated;
        });
    }, [recalcularCruce, analisis]);

    const handleIniciarNuevaFilaHasta = useCallback(() => {
        setMostrarNuevaFilaHasta(true);
        setNuevaFilaHasta({ razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: '' });
    }, []);

    const handleCancelarNuevaFilaHasta = useCallback(() => {
        setMostrarNuevaFilaHasta(false);
        setNuevaFilaHasta({ razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: '' });
        setError(null);
    }, []);

    const handleGuardarNuevaFilaHasta = useCallback(() => {
        const { razonSocial, mesesTrabajados } = nuevaFilaHasta;
        if (!razonSocial?.trim()) {
            setError('Completá la Razón Social para agregar el período a HASTA 06/94.');
            return;
        }

        setRegistrosHasta0694((prev) => {
            const nextIndice = prev.reduce((max, r) => Math.max(max, Number(r?.indiceOriginal ?? -1)), -1) + 1;
            const mesesNumero = Number.parseInt(String(mesesTrabajados || '').trim(), 10);
            const nuevoRegistro = {
                ...nuevaFilaHasta,
                indiceOriginal: nextIndice,
                mesesTrabajados: Number.isFinite(mesesNumero) ? mesesNumero : null
            };
            const updated = [...prev, nuevoRegistro];
            recalcularCruce(analisis?.mesesExpandidos || [], updated);
            return updated;
        });

        setMostrarNuevaFilaHasta(false);
        setNuevaFilaHasta({ razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: '' });
        setError(null);
    }, [nuevaFilaHasta, analisis, recalcularCruce]);

    // ══════════════════════════════════════
    // PASO 2: Edición — DESDE 07/94
    // ══════════════════════════════════════
    const handleEditDesde0794 = useCallback((indice, campo, valor) => {
        setRegistrosDesde0794(prev => {
            const updated = prev.map(r => r.indiceOriginal === indice ? { ...r, [campo]: campo === 'remImpSs' ? (valor === '' ? 0 : parseFloat(valor)) : valor } : r);
            const nuevosAjustes = getResumenAdjustmentsFromDesde0794(updated);
            setAjustesDesde0794(nuevosAjustes);
            recalcularCruce(analisis?.mesesExpandidos || [], registrosHasta0694, nuevosAjustes);
            return updated;
        });
    }, [recalcularCruce, analisis, registrosHasta0694]);

    const handleEliminarDesde0794 = useCallback((indice) => {
        setRegistrosDesde0794(prev => {
            const updated = prev.filter(r => r.indiceOriginal !== indice);
            const nuevosAjustes = getResumenAdjustmentsFromDesde0794(updated);
            setAjustesDesde0794(nuevosAjustes);
            recalcularCruce(analisis?.mesesExpandidos || [], registrosHasta0694, nuevosAjustes);
            return updated;
        });
    }, [recalcularCruce, analisis, registrosHasta0694]);

    const handleIniciarNuevaFilaDesde = useCallback(() => {
        setMostrarNuevaFilaDesde(true);
        setNuevaFilaDesde({ razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: '' });
    }, []);

    const handleCancelarNuevaFilaDesde = useCallback(() => {
        setMostrarNuevaFilaDesde(false);
        setNuevaFilaDesde({ razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: '' });
        setError(null);
    }, []);

    const handleGuardarNuevaFilaDesde = useCallback(() => {
        const { periodo, remImpSs } = nuevaFilaDesde;
        if (!validarPeriodo(periodo)) {
            setError('Período inválido en DESDE 07/94. Usá formato MM/YYYY.');
            return;
        }

        setRegistrosDesde0794((prev) => {
            const nextIndice = prev.reduce((max, r) => Math.max(max, Number(r?.indiceOriginal ?? -1)), -1) + 1;
            const nuevoRegistro = {
                ...nuevaFilaDesde,
                indiceOriginal: nextIndice,
                remImpSs: remImpSs ? parseFloat(remImpSs) : 0,
                remImpSsEsCero: (remImpSs ? parseFloat(remImpSs) : 0) <= 1
            };
            const updated = [...prev, nuevoRegistro];
            const nuevosAjustes = getResumenAdjustmentsFromDesde0794(updated);
            setAjustesDesde0794(nuevosAjustes);
            recalcularCruce(analisis?.mesesExpandidos || [], registrosHasta0694, nuevosAjustes);
            return updated;
        });

        setMostrarNuevaFilaDesde(false);
        setNuevaFilaDesde({ razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: '' });
        setError(null);
    }, [nuevaFilaDesde, analisis, registrosHasta0694, recalcularCruce]);

    // ══════════════════════════════════════
    // PASO 3: Selección de cliente y confirmación
    // ══════════════════════════════════════
    const handleBuscarCliente = useCallback((criterio) => {
        setBusquedaCliente(criterio);
        setClienteSeleccionado(null);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!criterio || criterio.trim().length < 2) {
            setResultadosCliente([]);
            return;
        }
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await buscarClientes(criterio);
                setResultadosCliente(res || []);
            } catch { setResultadosCliente([]); }
        }, 350);
    }, []);

    const handleSeleccionarCliente = useCallback((cliente) => {
        setClienteSeleccionado(cliente);
        setResultadosCliente([]);
        setBusquedaCliente('');
        setChequeoRtiResultado(null);
        setChequeoRtiError(null);

        const fechaRaw = cliente?.fechaNacimiento || cliente?.fecha_nacimiento || cliente?.nacimiento || '';
        if (typeof fechaRaw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(fechaRaw)) {
            setMoratoriaFechaNacimiento(fechaRaw.slice(0, 10));
        }
    }, []);

    const handleClienteCreado = useCallback((nuevoCliente) => {
        const clienteFormateado = {
            id: nuevoCliente.id,
            nombre: nuevoCliente.nombre,
            apellido: nuevoCliente.apellido,
            cuil: nuevoCliente.cuil,
            fechaNacimiento: nuevoCliente.fechaNacimiento || nuevoCliente.nacimiento
        };
        handleSeleccionarCliente(clienteFormateado);
        setShowCrearClienteModal(false);
    }, [handleSeleccionarCliente]);

    // Preselección de cliente desde ?cuil=... (al abrir la calculadora desde la ficha de atención o
    // desde "Modificar aportes" del plan de pago). Vía A: si el cliente existe, se preselecciona. Si
    // no, no bloquea: sigue el flujo normal (parsear + guardar borrador + matchear después).
    // Si tiene borrador se recupera primero. El cliente queda FIJADO (clienteFijadoRef): subir el
    // PDF o recuperar un borrador limpian la selección, y sin esto el ?cuil= se perdía al primer paso.
    const preseleccionCuilHecha = useRef(false);
    const clienteFijadoRef = useRef(null);
    useEffect(() => {
        if (preseleccionCuilHecha.current) return;
        const cuilParam = (new URLSearchParams(location.search).get('cuil') || '').replace(/\D/g, '');
        if (!cuilParam) return;
        preseleccionCuilHecha.current = true;
        (async () => {
            try {
                const res = await buscarClientes(cuilParam);
                const lista = Array.isArray(res) ? res : [];
                const match = lista.find((c) => String(c?.cuil || '').replace(/\D/g, '') === cuilParam);
                if (!match) return;
                clienteFijadoRef.current = match;
                await buscarYRecuperarBorrador(cuilParam);
                handleSeleccionarCliente(match);
            } catch {
                // noop: si no se encuentra, se sigue el flujo normal
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, handleSeleccionarCliente]);

    // Vuelve a poner al cliente fijado cuando un reset lo sacó, salvo que el PDF sea de otra persona.
    useEffect(() => {
        const fijado = clienteFijadoRef.current;
        if (clienteSeleccionado || !fijado) return;
        const cuilFijado = String(fijado.cuil || '').replace(/\D/g, '');
        if (cuil && cuil !== cuilFijado) return;
        handleSeleccionarCliente(fijado);
    }, [clienteSeleccionado, cuil, handleSeleccionarCliente]);

    // La Ley 24.476 que el cliente ya compró (V267, cargada al abrir el plan de pago o en su ficha)
    // entra como SICAM manual "Ley 24.476": resta de la ventana disponible y no se vuelve a ofrecer.
    const cuilSeleccionado = clienteSeleccionado?.cuil || '';
    useEffect(() => {
        if (!cuilSeleccionado) return;
        let vigente = true;
        listarLey24476(String(cuilSeleccionado).replace(/\D/g, ''))
            .then((periodos) => {
                if (!vigente || !Array.isArray(periodos) || periodos.length === 0) return;
                setManualPeriodosSicam((prev) => {
                    const nuevos = periodos
                        .map((p) => ({ desde: String(p.desde).slice(0, 7), hasta: String(p.hasta).slice(0, 7) }))
                        .filter((p) => !prev.some((x) => x.tipo === 'LEY_24476' && x.desde === p.desde && x.hasta === p.hasta))
                        .map((p) => ({
                            id: `CLIENTE-LEY_24476-${p.desde}-${p.hasta}`,
                            desde: p.desde,
                            hasta: p.hasta,
                            tipo: 'LEY_24476',
                            cantidadMeses: expandirRangoYearMonth(p.desde, p.hasta).length,
                            delCliente: true,
                        }));
                    return nuevos.length ? [...prev, ...nuevos] : prev;
                });
            })
            .catch(() => { /* sin períodos: la calculadora sigue igual */ });
        return () => { vigente = false; };
    }, [cuilSeleccionado]);

    useEffect(() => {
        const cargarResumenAportes = async () => {
            if (!clienteSeleccionado?.cuil) {
                setAportesActualesCliente(null);
                return;
            }
            try {
                const resumen = await obtenerResumenAportesCliente(clienteSeleccionado.cuil);
                setAportesActualesCliente(Number(resumen?.aportesActuales || 0));
            } catch {
                setAportesActualesCliente(null);
            }
        };
        cargarResumenAportes();
    }, [clienteSeleccionado]);

    const obtenerMesesComputablesParaImportacion = useCallback(() => {
        return calculoDefinitivoConSicam.mesesComputablesDefinitivos || [];
    }, [calculoDefinitivoConSicam]);

    const construirMesesDetalladosParaImportacion = useCallback(() => {
        const mesesFinales = obtenerMesesComputablesParaImportacion();

        const mesesDetallados = mesesFinales.map((ym) => {
            const esManual = calculoDefinitivoConSicam.mesesManualSicam?.includes(ym);
            if (esManual) {
                // Preservar el subtipo (MONOTRIBUTO/AUTONOMO/LEY_24476) en vez de aplanar a 'SICAM'.
                const tipoManual = manualSicamTipoPorMes[ym] || 'SICAM';
                return { periodo: ym, tipo: tipoManual, empleador: 'Manual - SICAM', cuit: '', esSicam: true };
            }
            const expandido = analisis?.mesesExpandidos?.find((m) => m.yearMonth === ym);
            if (expandido && expandido.origenIndices?.length > 0) {
                const idx = expandido.origenIndices[0];
                const registro = registros.find((r) => r.indiceOriginal === idx);
                if (registro) {
                    return {
                        periodo: ym,
                        tipo: registro.tipoAporteResumen || 'RELACION_DEPENDENCIA',
                        empleador: registro.razonSocial || '',
                        cuit: registro.cuitCuenta || '',
                        esSicam: false,
                    };
                }
            }
            return { periodo: ym, tipo: ym < '1994-07' ? 'HASTA_0694' : 'RELACION_DEPENDENCIA', empleador: 'Resumen', cuit: '', esSicam: false };
        });

        return { mesesFinales, mesesDetallados };
    }, [obtenerMesesComputablesParaImportacion, calculoDefinitivoConSicam, analisis, registros, manualSicamTipoPorMes]);

    const ejecutarChequeoRegularidadRti = useCallback(async () => {
        const { mesesDetallados } = construirMesesDetalladosParaImportacion();
        if (!mesesDetallados.length) {
            setChequeoRtiResultado(null);
            setChequeoRtiError('No hay meses computables para evaluar regularidad RTI.');
            return;
        }

        if (chequeoRtiIncluirExistentes && !clienteSeleccionado?.cuil) {
            setChequeoRtiResultado(null);
            setChequeoRtiError('Seleccioná un cliente para incluir aportes existentes.');
            return;
        }

        setChequeoRtiCargando(true);
        setChequeoRtiError(null);
        try {
            const resultado = await previsualizarRegularidadRti({
                cuilCliente: clienteSeleccionado?.cuil || null,
                meses: mesesDetallados,
                incluirAportesExistentes: Boolean(chequeoRtiIncluirExistentes && clienteSeleccionado?.cuil),
            });
            setChequeoRtiResultado(resultado);
        } catch (err) {
            const msg = err.response?.data || err.message || 'No se pudo calcular la regularidad RTI.';
            setChequeoRtiResultado(null);
            setChequeoRtiError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setChequeoRtiCargando(false);
        }
    }, [chequeoRtiIncluirExistentes, clienteSeleccionado, construirMesesDetalladosParaImportacion]);

    // Auto-cálculo de regularidad RTI/ART/minusvalía al llegar al paso 3. Ya no depende del
    // botón: se recalcula solo cuando cambian los datos de aportes. Debounce para no disparar
    // una llamada por cada tecla al editar. El botón queda como recálculo manual.
    useEffect(() => {
        if (pasoActual !== 2) return;
        const { mesesDetallados } = construirMesesDetalladosParaImportacion();
        if (!mesesDetallados.length) return;
        const temporizador = setTimeout(() => { ejecutarChequeoRegularidadRti(); }, 500);
        return () => clearTimeout(temporizador);
    }, [pasoActual, ejecutarChequeoRegularidadRti, construirMesesDetalladosParaImportacion]);

    const handleImprimirPaso3 = useCallback(() => {
        const escaparHtml = (valor) => String(valor ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        const formatearFechaCorta = (iso) => {
            if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'No informada';
            const [yyyy, mm, dd] = iso.split('-');
            return `${dd}/${mm}/${yyyy}`;
        };

        const {
            mesesHasta,
            mesesDesdeComputables,
            mesesRelDepSinSicam,
            mesesNuevosAportadosPorSicam,
            totalMesesDefinitivos,
        } = resumenComputablesPaso3;

        const personaInforme = clienteSeleccionado
            ? `${clienteSeleccionado.apellido || ''}, ${clienteSeleccionado.nombre || ''}`.replace(/^,\s*/, '').trim()
            : (nombreCompleto || 'No informado');
        const cuilInforme = clienteSeleccionado?.cuil || cuil || 'No informado';
        const fechaInforme = new Date().toLocaleString('es-AR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
        // ── Datos de moratorias ──
        const fechaJubilatoria        = formatearFechaCorta(analisisMoratorias?.fechaJubilacion || '');
        const edadActual              = analisisMoratorias?.edadActual ?? '';
        const edadJubilatoria         = analisisMoratorias?.edadJubilatoria ?? '';
        // La edad requerida puede ser fraccionaria por prorrateo (ej. 57,5 => "57 años 6 meses").
        const edadJubilatoriaTexto = edadJubilatoria === '' ? '' : (() => {
            const total = Number(edadJubilatoria);
            const a = Math.floor(total + 1e-9);
            const m = Math.round((total - a) * 12);
            return m === 0 ? `${a} años` : `${a} años ${m} meses`;
        })();
        const totalConBonificaciones  = analisisMoratorias?.totalComputablesConBonificacion ?? totalMesesDefinitivos;
        // Meta de servicios (puede ser < 360 por prorrateo de regímenes diferenciales).
        const metaMesesPdf = Number(analisisMoratorias?.metaServiciosMeses ?? 360);
        const metaMesesLabel = `${metaMesesPdf} meses`;

        // Faltantes antes de estrategias: si no hay análisis de moratorias calculamos
        // directamente desde el total de meses para evitar mostrar 0 por error.
        const faltantesAntesEstrategias = analisisMoratorias
            ? Math.max(0, Number(analisisMoratorias.faltantesA360 ?? 0))
            : Math.max(0, 360 - totalMesesDefinitivos);

        // ── Bonificación por hijos ──
        const bonifHijosAplica        = Boolean(analisisMoratorias?.bonificacionHijos?.aplica);
        const bonifHijosMeses         = analisisMoratorias?.bonificacionHijos?.mesesBonificados ?? 0;
        const totalHijosCargados      = analisisMoratorias?.bonificacionHijos?.detalle?.length ?? 0;

        // ── Bonificación por exceso de edad ──
        const bonifEdadAplica         = Boolean(analisisMoratorias?.bonificacionExcesoEdad?.aplica);
        const bonifEdadMesesExcedidos = analisisMoratorias?.bonificacionExcesoEdad?.mesesExcedidos ?? 0;
        const bonifEdadMeses          = analisisMoratorias?.bonificacionExcesoEdad?.mesesBonificados ?? 0;

        const totalBonificaciones     = bonifHijosMeses + bonifEdadMeses;
        const hayBonificaciones       = totalBonificaciones > 0;

        // ── Estrategias ──
        const estrategiasCatalogo = [
            { key: 'ley24476',          titulo: 'Ley 24.476',        inicioYm: analisisMoratorias?.ley24476?.inicioYm,          finYm: analisisMoratorias?.ley24476?.finYm },
            { key: 'monotributoFuturo', titulo: 'Aporte futuro', inicioYm: analisisMoratorias?.monotributoFuturo?.inicioYm, finYm: analisisMoratorias?.monotributoFuturo?.finYm },
            { key: 'ley27705',          titulo: 'Ley 27.705',         inicioYm: analisisMoratorias?.ley27705?.inicioYm,          finYm: analisisMoratorias?.ley27705?.finYm },
        ];
        const estrategiasUtilizadas = estrategiasCatalogo
            .map((e) => ({ ...e, meses: Math.max(0, Number(moratoriaEstrategiasSeleccion?.[e.key] ?? 0)) }))
            .filter((e) => e.meses > 0);
        const totalEstrategiasSeleccionadas = estrategiasUtilizadas.reduce((acc, e) => acc + e.meses, 0);

        // ── B2: costos (solo si el panel está abierto) ──
        const mostrarCostosPdf = mostrarCostos;
        const montoLey24476Pdf = parsearMonto(costos.ley24476Total);
        const montoMonoMensualPdf = parsearMonto(costos.monotributoMensual);
        const monto27705MensualPdf = parsearMonto(costos.ley27705Mensual);
        const costoCeldaEstrategia = (e) => {
            if (e.key === 'ley24476') return Number.isFinite(montoLey24476Pdf) ? `${formatearMonto(montoLey24476Pdf)} total` : '—';
            if (e.key === 'monotributoFuturo') return Number.isFinite(montoMonoMensualPdf) ? `${formatearMonto(montoMonoMensualPdf)} /mes` : '—';
            if (e.key === 'ley27705') return Number.isFinite(monto27705MensualPdf) ? `${formatearMonto(monto27705MensualPdf)}/mes × ${e.meses} ≈ ${formatearMonto(monto27705MensualPdf * e.meses)}` : '—';
            return '—';
        };

        // Total proyectado: si hay análisis de moratorias lo tomamos de ahí (incluye
        // bonificaciones calculadas); si no, sumamos base + estrategias manuales.
        const totalProyectadoConEstrategias = analisisMoratorias
            ? Math.max(0, Number(analisisMoratorias.estrategias?.totalProyectadoConEstrategias ?? totalConBonificaciones + totalEstrategiasSeleccionadas))
            : totalMesesDefinitivos + totalEstrategiasSeleccionadas;

        // Faltantes finales: mismo criterio, con doble guarda para que nunca
        // muestre "cumplido" si el total proyectado no llega a 360.
        const faltantesLuegoestrategias = analisisMoratorias
            ? Math.max(0, Number(analisisMoratorias.estrategias?.faltantesLuegoSeleccion ?? faltantesAntesEstrategias))
            : Math.max(0, metaMesesPdf - totalProyectadoConEstrategias);
        const llegaA360ConEstrategias = faltantesLuegoestrategias === 0 && totalProyectadoConEstrategias >= metaMesesPdf;

        // ── Aporte futuro + exceso de edad ──
        // Solo se imprime si la tarjeta está seleccionada; en ese caso reemplaza al cuadro final
        // de "Total proyectado" (se llegaría a 360 en la fecha estimada).
        const aporteFuturo = analisisMoratorias?.aporteFuturoMonotributo;
        const aporteFuturoActivo = Boolean(aporteFuturo?.aplica) && aporteFuturoSeleccionado;
        const aporteFuturoFaltan = aporteFuturo?.mesesAportesFaltantes ?? 0;
        const aporteFuturoTiempo = aporteFuturo?.mesesTiempoNecesario ?? 0;
        const aporteFuturoFechaYm = aporteFuturo?.fechaEstimadaYm ? formatearYearMonth(aporteFuturo.fechaEstimadaYm) : '';

        const htmlInforme = `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Resumen de aportes</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #1f2937; font-size: 11.5px; line-height: 1.4; }
    .report { width: 100%; max-width: 186mm; margin: 0 auto; }

    /* ── Header ── */
    .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
    .brand-title { margin: 0; font-size: 19px; font-weight: 900; color: #1d4ed8; letter-spacing: -0.4px; line-height: 1; }
    .brand-subtitle { margin: 2px 0 0; color: #94a3b8; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
    .doc-title { text-align: right; }
    .doc-title h2 { margin: 0; color: #0f172a; font-size: 13px; font-weight: 800; }
    .doc-title p { margin: 2px 0 0; color: #64748b; font-size: 9.5px; }
    .sep { border: none; border-top: 2px solid #bfdbfe; margin: 0 0 9px; }

    /* ── Sección genérica ── */
    .sec { border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 8px; overflow: hidden; }
    .sec-head { background: #f1f5f9; padding: 5px 9px; font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid #e2e8f0; }
    .sec-body { padding: 8px 9px; }

    /* ── Grid datos personales ── */
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; }
    .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px 10px; }
    .field b { display: block; font-size: 9.5px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 1px; }
    .field span { font-size: 12px; font-weight: 600; color: #0f172a; }

    /* ── Tarjetas de aportes ── */
    .aporte-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 7px; }
    .aporte-card { border: 1px solid #e2e8f0; border-radius: 6px; padding: 7px 9px; text-align: center; background: #f8fafc; }
    .aporte-card .lbl { font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 4px; }
    .aporte-card .val { font-size: 20px; font-weight: 800; line-height: 1; }
    .aporte-card .sub { font-size: 9px; color: #94a3b8; margin-top: 2px; }
    .c-blue { color: #1d4ed8; }
    .c-violet { color: #7c3aed; }
    .c-green { color: #15803d; }
    .c-orange { color: #b45309; }
    .c-red { color: #b91c1c; }
    .c-gray { color: #64748b; }
    .bg-green { background: #f0fdf4 !important; border-color: #bbf7d0 !important; }
    .bg-red { background: #fff1f2 !important; border-color: #fecdd3 !important; }
    .bg-blue { background: #eff6ff !important; border-color: #bfdbfe !important; }

    /* ── Tabla proyección ── */
    table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
    td { padding: 5px 9px; border-bottom: 1px solid #f1f5f9; color: #1e293b; }
    tfoot td { padding: 8px 9px; }
    .num { text-align: right; font-weight: 700; white-space: nowrap; }
    .muted { color: #94a3b8; font-style: italic; }

    /* ── Footer ── */
    .footer { margin-top: 10px; color: #94a3b8; font-size: 9.5px; text-align: right; border-top: 1px solid #e2e8f0; padding-top: 5px; }
  </style>
</head>
<body>
<main class="report">

  <!-- ══ ENCABEZADO ══ -->
  <header class="header">
    <div>
      <h1 class="brand-title">GESTION EJP</h1>
      <p class="brand-subtitle">Estudio Juridico Previsional</p>
    </div>
    <div class="doc-title">
      <h2>RESUMEN DE APORTES</h2>
      <p>Generado: ${escaparHtml(fechaInforme)}</p>
    </div>
  </header>
  <hr class="sep" />

  <!-- ══ DATOS PERSONALES ══ -->
  <div class="sec">
    <div class="sec-head">Datos personales</div>
    <div class="sec-body">
      <div class="grid2" style="margin-bottom:6px;">
        <div class="field"><b>Apellido y Nombre</b><span>${escaparHtml(personaInforme || 'No informado')}</span></div>
        <div class="field"><b>CUIL</b><span>${escaparHtml(cuilInforme)}</span></div>
      </div>
      <div class="grid3">
        <div class="field"><b>Fecha de nacimiento</b><span>${escaparHtml(formatearFechaCorta(moratoriaFechaNacimiento))}</span></div>
        <div class="field"><b>Edad actual</b><span>${edadActual !== '' ? `${edadActual} años` : 'No informada'}</span></div>
        <div class="field"><b>Fecha edad jubilatoria</b><span>${escaparHtml(fechaJubilatoria)}${edadJubilatoriaTexto ? ` (${escaparHtml(edadJubilatoriaTexto)})` : ''}</span></div>
      </div>
    </div>
  </div>

  <!-- ══ APORTES COMPUTADOS ══ -->
  <div class="sec">
    <div class="sec-head">Aportes computados</div>
    <div class="sec-body">
      <div class="aporte-cards">
        <div class="aporte-card">
          <div class="lbl">HASTA 06/94</div>
          <div class="val c-blue">${mesesHasta}</div>
          <div class="sub">meses</div>
        </div>
        <div class="aporte-card">
          <div class="lbl">DESDE 07/94</div>
          <div class="val c-blue">${mesesDesdeComputables}</div>
          <div class="sub">meses</div>
        </div>
        <div class="aporte-card ${mesesNuevosAportadosPorSicam > 0 ? '' : ''}">
          <div class="lbl">SICAM</div>
          <div class="val c-violet">${mesesNuevosAportadosPorSicam}</div>
          <div class="sub">meses</div>
        </div>
        <div class="aporte-card bg-blue">
          <div class="lbl">TOTAL MESES</div>
          <div class="val c-green">${totalMesesDefinitivos}</div>
          <div class="sub">meses</div>
        </div>
      </div>
    </div>
  </div>

  <!-- ══ BONIFICACIONES ══ -->
  ${hayBonificaciones ? `
  <div class="sec">
    <div class="sec-head">Bonificaciones</div>
    <div class="sec-body" style="padding:0;">
      <table>
        <tbody>
          ${bonifHijosAplica ? `
          <tr>
            <td>Por tareas de cuidado — ${totalHijosCargados} hijo${totalHijosCargados !== 1 ? 's' : ''}</td>
            <td class="num c-green">+ ${bonifHijosMeses} meses</td>
          </tr>` : ''}
          ${bonifEdadAplica ? `
          <tr>
            <td>Por exceso de edad jubilatoria (${bonifEdadMesesExcedidos} meses excedidos)</td>
            <td class="num c-green">+ ${bonifEdadMeses} meses</td>
          </tr>` : ''}
        </tbody>
        <tfoot>
          <tr style="background:#f0fdf4; border-top: 2px solid #bbf7d0;">
            <td style="font-weight:700; color:#15803d;">Total aportes + bonificaciones</td>
            <td class="num" style="font-weight:800; color:#15803d;">${totalConBonificaciones} meses</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>` : ''}

  <!-- ══ FALTANTES ══ -->
  <div class="sec">
    <div class="sec-head">Situación respecto a ${escaparHtml(metaMesesLabel)}${metaMesesPdf !== 360 ? ' (prorrateado)' : ''}</div>
    <div class="sec-body" style="padding:0;">
      <table>
        <tfoot>
          <tr style="background:${faltantesAntesEstrategias === 0 ? '#f0fdf4' : '#fff7ed'}; border-top: 2px solid ${faltantesAntesEstrategias === 0 ? '#bbf7d0' : '#fed7aa'};">
            <td style="font-weight:700; color:${faltantesAntesEstrategias === 0 ? '#15803d' : '#b45309'};">
              ${faltantesAntesEstrategias === 0 ? `Ya alcanza los ${escaparHtml(metaMesesLabel)} sin estrategias` : `Faltantes para alcanzar ${escaparHtml(metaMesesLabel)}`}
            </td>
            <td class="num" style="font-weight:800; color:${faltantesAntesEstrategias === 0 ? '#15803d' : '#b45309'};">
              ${faltantesAntesEstrategias === 0 ? '—' : `${faltantesAntesEstrategias} meses`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>

  <!-- ══ ESTRATEGIAS ══ -->
  <div class="sec">
    <div class="sec-head">Estrategias de complemento</div>
    <div class="sec-body" style="padding:0;">
      <table>
        <thead>
          <tr>
            <th>Estrategia</th>
            <th>Ventana aplicada</th>
            <th style="text-align:right;">Meses</th>
            ${mostrarCostosPdf ? '<th style="text-align:right;">Costo aprox.</th>' : ''}
          </tr>
        </thead>
        <tbody>
          ${estrategiasUtilizadas.length > 0
            ? estrategiasUtilizadas.map((e) => `
          <tr>
            <td>${escaparHtml(e.titulo)}</td>
            <td style="text-align:center;">${escaparHtml(formatearYearMonth(e.inicioYm || ''))} – ${escaparHtml(formatearYearMonth(e.finYm || ''))}</td>
            <td class="num c-blue">+ ${e.meses}</td>
            ${mostrarCostosPdf ? `<td class="num">${escaparHtml(costoCeldaEstrategia(e))}</td>` : ''}
          </tr>`).join('')
            : `<tr><td colspan="${mostrarCostosPdf ? 4 : 3}" class="muted">Sin estrategias seleccionadas.</td></tr>`}
        </tbody>
        ${estrategiasUtilizadas.length > 0 ? `
        <tfoot>
          <tr style="background:#eff6ff; border-top: 2px solid #bfdbfe;">
            <td colspan="${mostrarCostosPdf ? 3 : 2}" style="font-weight:700; color:#1d4ed8;">Total estrategias</td>
            <td class="num" style="color:#1d4ed8;">${totalEstrategiasSeleccionadas}</td>
          </tr>
        </tfoot>` : ''}
      </table>
      ${mostrarCostosPdf ? `
      <div style="padding:8px 12px; font-size:10px; color:#64748b; border-top:1px solid #eee; line-height:1.5;">
        Costos estimados al día de impresión.
        <b>Ley 24.476:</b> el monto real se confirma el día del pago.
        <b>Aporte futuro:</b> monto mensual si se aporta como monotributista; se paga todos los meses (no se puede adelantar).
        <b>Ley 27.705:</b> precio del mes actual; el total es aproximado, varía con la inflación.
      </div>` : ''}
    </div>
  </div>

  ${aporteFuturoActivo ? `
  <!-- ══ APORTE FUTURO + EXCESO DE EDAD (reemplaza al total proyectado) ══ -->
  <div style="border: 2px solid #00897b; border-radius: 7px; background:#e6f4f2; overflow:hidden; margin-bottom: 8px;">
    <table>
      <tbody>
        <tr>
          <td style="padding: 10px 12px;">
            <div style="font-size:13px; font-weight:800; color:#00695c; margin-bottom:3px;">Aporte futuro + exceso de edad</div>
            <div style="font-size:11.5px; color:#00695c;">
              Faltan <b>${aporteFuturoFaltan} ${aporteFuturoFaltan === 1 ? 'mes' : 'meses'}</b> de aportes, se obtendrían en <b>${aporteFuturoTiempo} ${aporteFuturoTiempo === 1 ? 'mes' : 'meses'}</b> de tiempo aportando como monotributista.
            </div>
          </td>
          <td style="text-align:right; padding: 10px 12px; white-space:nowrap;">
            <div style="font-size:10.5px; font-weight:600; color:#64748b;">Fecha estimada</div>
            <div style="font-size:20px; font-weight:900; color:#00695c;">${aporteFuturoFechaYm}</div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>` : `
  <!-- ══ RESULTADO FINAL ══ -->
  <div style="border: 2px solid ${llegaA360ConEstrategias ? '#86efac' : '#fca5a5'}; border-radius: 7px; background: ${llegaA360ConEstrategias ? '#f0fdf4' : '#fff1f2'}; overflow:hidden; margin-bottom: 8px;">
    <table>
      <tbody>
        <tr>
          <td style="font-size:13px; font-weight:800; color:${llegaA360ConEstrategias ? '#15803d' : '#b91c1c'}; padding: 10px 12px;">
            TOTAL PROYECTADO
          </td>
          <td style="text-align:right; padding: 10px 12px;">
            <span style="font-size:22px; font-weight:900; color:${llegaA360ConEstrategias ? '#15803d' : '#b91c1c'};">${totalProyectadoConEstrategias}</span>
            <span style="font-size:12px; font-weight:600; color:#64748b;"> / 360 meses</span>
          </td>
          <td style="padding: 10px 12px; white-space:nowrap;">
            <span style="display:inline-block; padding:4px 11px; border-radius:99px; font-size:10.5px; font-weight:800;
              background:${llegaA360ConEstrategias ? '#dcfce7' : '#fee2e2'};
              color:${llegaA360ConEstrategias ? '#14532d' : '#7f1d1d'};
              border: 1px solid ${llegaA360ConEstrategias ? '#86efac' : '#fca5a5'};">
              ${llegaA360ConEstrategias ? '✓ Cumple el objetivo' : `✗ Restan ${faltantesLuegoestrategias} meses`}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>`}

  <p class="footer">Calculadora de aportes &mdash; Resumen informativo &mdash; Gestion EJP</p>
</main>
</body>
</html>`;

        let iframe = printIframeRef.current;
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.setAttribute('title', 'impresion-paso-3');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.style.visibility = 'hidden';
            document.body.appendChild(iframe);
            printIframeRef.current = iframe;
        }

        iframe.onload = () => {
            const frameWindow = iframe.contentWindow;
            if (!frameWindow) {
                setError('No se pudo generar la vista de impresion del Paso 3.');
                return;
            }
            frameWindow.focus();
            frameWindow.print();
        };
        iframe.srcdoc = htmlInforme;
    }, [resumenComputablesPaso3, clienteSeleccionado, nombreCompleto, cuil, moratoriaFechaNacimiento, moratoriaSexo, moratoriaHijos, analisisMoratorias, moratoriaEstrategiasSeleccion, aporteFuturoSeleccionado, mostrarCostos, costos, parsearMonto, formatearMonto]);

    // PDF aparte con la evaluación de regularidades (RTI, ART y minusvalía). Se imprime
    // por separado del resumen de importación: el cliente puede cumplir sin intención de
    // iniciar esos trámites. Misma estética que el resumen del paso 3.
    const handleImprimirRegularidades = useCallback(() => {
        if (!chequeoRtiResultado) {
            setError('Todavía no se calculó la regularidad. Esperá unos segundos e intentá de nuevo.');
            return;
        }
        const escaparHtml = (valor) => String(valor ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        const formatearFechaCorta = (iso) => {
            if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'No informada';
            const [yyyy, mm, dd] = iso.split('-');
            return `${dd}/${mm}/${yyyy}`;
        };

        const personaInforme = clienteSeleccionado
            ? `${clienteSeleccionado.apellido || ''}, ${clienteSeleccionado.nombre || ''}`.replace(/^,\s*/, '').trim()
            : (nombreCompleto || 'No informado');
        const cuilInforme = clienteSeleccionado?.cuil || cuil || 'No informado';
        const fechaInforme = new Date().toLocaleString('es-AR', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        });

        const htmlInforme = `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Evaluacion de regularidades</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #1f2937; font-size: 11.5px; line-height: 1.4; }
    .report { width: 100%; max-width: 186mm; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 8px; }
    .brand-title { margin: 0; font-size: 19px; font-weight: 900; color: #1d4ed8; letter-spacing: -0.4px; line-height: 1; }
    .brand-subtitle { margin: 2px 0 0; color: #94a3b8; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
    .doc-title { text-align: right; }
    .doc-title h2 { margin: 0; color: #0f172a; font-size: 13px; font-weight: 800; }
    .doc-title p { margin: 2px 0 0; color: #64748b; font-size: 9.5px; }
    .sep { border: none; border-top: 2px solid #bfdbfe; margin: 0 0 9px; }
    .sec { border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 8px; overflow: hidden; }
    .sec-head { background: #f1f5f9; padding: 5px 9px; font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid #e2e8f0; }
    .sec-body { padding: 8px 9px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; }
    .field b { display: block; font-size: 9.5px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 1px; }
    .field span { font-size: 12px; font-weight: 600; color: #0f172a; }
    .footer { margin-top: 10px; color: #94a3b8; font-size: 9.5px; text-align: right; border-top: 1px solid #e2e8f0; padding-top: 5px; }
  </style>
</head>
<body>
<main class="report">

  <header class="header">
    <div>
      <h1 class="brand-title">GESTION EJP</h1>
      <p class="brand-subtitle">Estudio Juridico Previsional</p>
    </div>
    <div class="doc-title">
      <h2>EVALUACION DE REGULARIDADES</h2>
      <p>Generado: ${escaparHtml(fechaInforme)}</p>
    </div>
  </header>
  <hr class="sep" />

  <div class="sec">
    <div class="sec-head">Datos personales</div>
    <div class="sec-body">
      <div class="grid2">
        <div class="field"><b>Apellido y Nombre</b><span>${escaparHtml(personaInforme || 'No informado')}</span></div>
        <div class="field"><b>CUIL</b><span>${escaparHtml(cuilInforme)}</span></div>
      </div>
    </div>
  </div>

  <!-- ══ REGULARIDAD RTI ══ -->
  ${(() => {
    const rtiReg = chequeoRtiResultado?.regularidad;
    if (!rtiReg) return '';
    const cumple = Boolean(rtiReg.cumpleRegularidad);
    const via = escaparHtml(rtiReg.viaDeCumplimiento || '');
    const vence = rtiReg.puedePerderla && rtiReg.fechaProximoCambio
        ? formatearFechaCorta(rtiReg.fechaProximoCambio)
        : null;
    const tieneSicam = Boolean(rtiReg.dependeDeSicamEnVentana)
        || Boolean(rtiReg.advertenciaSicam)
        || (Array.isArray(rtiReg.periodosSicam) && rtiReg.periodosSicam.length > 0)
        || (Array.isArray(rtiReg.periodosMonotributoSocial) && rtiReg.periodosMonotributoSocial.length > 0);
    return `
  <div class="sec">
    <div class="sec-head">Regularidad RTI</div>
    <div class="sec-body">
      <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
        <span style="display:inline-block; padding:5px 14px; border-radius:99px; font-size:11px; font-weight:800;
          background:${cumple ? '#dcfce7' : '#fee2e2'};
          color:${cumple ? '#14532d' : '#7f1d1d'};
          border:1px solid ${cumple ? '#86efac' : '#fca5a5'};">
          ${cumple ? '✓ CUMPLE' : '✗ NO CUMPLE'}
        </span>
        ${cumple && via ? `<span style="font-size:11.5px; color:#1e293b; font-weight:600;">Vía: ${via}</span>` : ''}
        ${vence ? `<span style="font-size:10.5px; color:#b45309; font-weight:600;">⚠ Vence: ${escaparHtml(vence)}</span>` : ''}
        ${tieneSicam ? `<span style="display:inline-block; padding:3px 10px; border-radius:99px; font-size:10px; font-weight:700; background:#fef9c3; color:#854d0e; border:1px solid #fde68a;">DDJJ — incluye SICAM</span>` : ''}
      </div>
    </div>
  </div>`;
  })()}

  <!-- ══ REGULARIDAD ART ══ -->
  ${(() => {
    const art = chequeoRtiResultado?.art;
    if (!art) return '';
    const apto = Boolean(art.puedeIniciarTramite);
    const tienePeriodo = art.periodoUltimaRelacionDesde && art.periodoUltimaRelacionHasta;
    return `
  <div class="sec">
    <div class="sec-head">Regularidad ART</div>
    <div class="sec-body">
      <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
        <span style="display:inline-block; padding:5px 14px; border-radius:99px; font-size:11px; font-weight:800;
          background:${apto ? '#dcfce7' : '#fee2e2'};
          color:${apto ? '#14532d' : '#7f1d1d'};
          border:1px solid ${apto ? '#86efac' : '#fca5a5'};">
          ${apto ? '✓ EN PLAZO' : '✗ PLAZO VENCIDO'}
        </span>
        ${tienePeriodo ? `<span style="font-size:10.5px; color:#334155;">${Number(art.mesesDesdeUltimaRelacion || 0)} meses desde la última relación de dependencia</span>` : ''}
      </div>
      ${tienePeriodo ? `
      <div style="margin-top:8px; font-size:10.5px; color:#334155; line-height:1.5;">
        <div><strong>Última relación:</strong> ${escaparHtml(formatearYearMonth(art.periodoUltimaRelacionDesde))} a ${escaparHtml(formatearYearMonth(art.periodoUltimaRelacionHasta))}</div>
        <div><strong>Empleador:</strong> ${escaparHtml(art.ultimoEmpleador || 'No informado')} &middot; <strong>CUIT:</strong> ${escaparHtml(art.ultimoCuitEmpleador || 'No informado')}</div>
      </div>` : `<div style="margin-top:6px; font-size:10.5px; color:#64748b;">${escaparHtml(art.mensaje || '')}</div>`}
    </div>
  </div>`;
  })()}

  <!-- ══ REGULARIDAD DE MINUSVALÍA ══ -->
  ${(() => {
    const m = chequeoRtiResultado?.minusvalia;
    if (!m) return '';
    const cumple = Boolean(m.cumpleAportes);
    const tieneEfectivos = Number(m.mesesEfectivos || 0) > 0;
    const edad = tieneEfectivos
        ? `${m.edadEstimadaAnios} años${m.edadEstimadaMeses ? ` ${m.edadEstimadaMeses} meses` : ''}`
        : 'No calculable';
    return `
  <div class="sec">
    <div class="sec-head">Regularidad de minusvalía</div>
    <div class="sec-body">
      <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
        <span style="display:inline-block; padding:5px 14px; border-radius:99px; font-size:11px; font-weight:800;
          background:${cumple ? '#dcfce7' : '#fee2e2'};
          color:${cumple ? '#14532d' : '#7f1d1d'};
          border:1px solid ${cumple ? '#86efac' : '#fca5a5'};">
          ${cumple ? '✓ CUMPLE APORTES' : '✗ NO CUMPLE APORTES'}
        </span>
      </div>
      <div style="margin-top:8px; font-size:10.5px; color:#334155; line-height:1.5;">
        <div><strong>Meses totales:</strong> ${Number(m.mesesTotales || 0)} / 240</div>
        <div><strong>Efectivos consecutivos previos al cese:</strong> ${Number(m.mesesConsecutivosDesdeCese || 0)} / 120</div>
        ${m.periodoCese ? `<div><strong>Tramo consecutivo:</strong> ${escaparHtml(formatearYearMonth(m.periodoInicioConsecutivo))} a ${escaparHtml(formatearYearMonth(m.periodoCese))}</div>` : ''}
        <div><strong>Edad prorrateada estimada:</strong> ${escaparHtml(edad)}</div>
      </div>
    </div>
  </div>`;
  })()}

  <p class="footer">Calculadora de aportes &mdash; Evaluacion de regularidades &mdash; Gestion EJP</p>
</main>
</body>
</html>`;

        let iframe = printIframeRef.current;
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.setAttribute('title', 'impresion-regularidades');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.style.visibility = 'hidden';
            document.body.appendChild(iframe);
            printIframeRef.current = iframe;
        }

        iframe.onload = () => {
            const frameWindow = iframe.contentWindow;
            if (!frameWindow) {
                setError('No se pudo generar la vista de impresion de regularidades.');
                return;
            }
            frameWindow.focus();
            frameWindow.print();
        };
        iframe.srcdoc = htmlInforme;
    }, [clienteSeleccionado, nombreCompleto, cuil, chequeoRtiResultado]);

    const agregarPeriodoManualSicam = useCallback(() => {
        const desdeYm = normalizarYearMonth(manualDesdeInput);
        const hastaYm = normalizarYearMonth(manualHastaInput || manualDesdeInput);

        if (!desdeYm || !hastaYm) {
            setError('Período manual inválido. Usá MM/YYYY o YYYY-MM en Desde/Hasta.');
            return;
        }
        if (desdeYm > hastaYm) {
            setError('El período manual es inválido: Desde no puede ser mayor que Hasta.');
            return;
        }

        const mesesPeriodo = expandirRangoYearMonth(desdeYm, hastaYm);
        if (mesesPeriodo.length === 0) {
            setError('No se pudieron expandir los meses del período manual.');
            return;
        }

        // M2: validación de ventana para Ley 24.476
        // El inicio correcto es el mes siguiente al 18° cumpleaños (o 10/1988 si no hay fecha de nacimiento).
        if (manualTipoInput === 'LEY_24476') {
            const INICIO_24476 = analisisMoratorias?.ley24476?.inicioYm || '1988-10';
            const FIN_24476 = '1993-09';
            const ventanaLabel = `${formatearYearMonth(INICIO_24476)} – ${formatearYearMonth(FIN_24476)}`;
            const mesesFuera = mesesPeriodo.filter(ym => ym < INICIO_24476 || ym > FIN_24476);
            if (mesesFuera.length === mesesPeriodo.length) {
                setError(`El período está completamente fuera de la ventana Ley 24.476 (${ventanaLabel}). No se agregaría ningún mes computable.`);
                return;
            }
            if (mesesFuera.length > 0) {
                setError(`Advertencia: ${mesesFuera.length} mes${mesesFuera.length > 1 ? 'es' : ''} están fuera de la ventana Ley 24.476 (${ventanaLabel}) y no reducirán la ventana disponible. Se agrega de todas formas.`);
                // No return: se agrega igual con advertencia
            }
        }

        setManualPeriodosSicam((prev) => {
            const existe = prev.some(
                (p) => p.desde === desdeYm && p.hasta === hastaYm && p.tipo === manualTipoInput
            );
            if (existe) return prev;

            return [
                ...prev,
                {
                    id: `${manualTipoInput}-${desdeYm}-${hastaYm}-${Date.now()}`,
                    desde: desdeYm,
                    hasta: hastaYm,
                    tipo: manualTipoInput,
                    cantidadMeses: mesesPeriodo.length,
                },
            ];
        });
        setManualDesdeInput('');
        setManualHastaInput('');
        setError(null);
    }, [manualDesdeInput, manualHastaInput, manualTipoInput]);

    const eliminarPeriodoManualSicam = useCallback((idPeriodo) => {
        setManualPeriodosSicam((prev) => prev.filter((p) => p.id !== idPeriodo));
    }, []);

    const ejecutarImportacionConfirmada = useCallback(async (mesesDetallados) => {
        if (!clienteSeleccionado?.cuil) return;
        setLoading(true);
        setError(null);
        try {
            // Datos de la moratoria que se vuelcan al cliente (para el plan de pago Ley 27.705).
            // La fecha límite es la edad jubilatoria NORMAL (65 H / 60 M), no la prorrateada.
            const edadNormal = moratoriaSexo === 'F' ? 60 : 65;
            let fechaCumpleEdad = null;
            if (/^\d{4}-\d{2}-\d{2}$/.test(moratoriaFechaNacimiento || '')) {
                const [y, m, d] = moratoriaFechaNacimiento.split('-');
                fechaCumpleEdad = `${parseInt(y, 10) + edadNormal}-${m}-${d}`;
            }
            const hijosImport = (moratoriaHijos || []).map((h) => ({
                auh: !!h.auh, discapacidad: !!h.discapacidad, adoptado: !!h.adoptado,
            }));
            const mesesLey27705 = Math.max(0, Number(moratoriaEstrategiasSeleccion?.ley27705 || 0));
            const datosExtra = {
                mesesLey27705: mesesLey27705 > 0 ? mesesLey27705 : null,
                fechaCumpleEdad,
                hijos: hijosImport.length ? hijosImport : null,
            };
            const resultado = await confirmarImportacion(clienteSeleccionado.cuil, mesesDetallados, datosExtra);
            await handleGuardarBorrador();
            setImportacionResultado(resultado);
            setSuccessMsg(resultado.mensaje || 'Importación completada');
            setModalReemplazoOpen(false);
            setResumenReemplazo(null);
            setMesesPendientesImportacion([]);
        } catch (err) {
            const msg = err.response?.data || err.message || 'Error al confirmar';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    }, [clienteSeleccionado, handleGuardarBorrador, moratoriaSexo, moratoriaFechaNacimiento, moratoriaHijos, moratoriaEstrategiasSeleccion]);

    const handleConfirmar = useCallback(async () => {
        if (!clienteSeleccionado || !analisis) return;
        try {
            const { mesesFinales, mesesDetallados } = construirMesesDetalladosParaImportacion();

            if (mesesFinales.length === 0) {
                setError('No hay meses para importar.');
                return;
            }

            // El total a importar debe representar los meses únicos definitivos (incluye SICAM manual).
            if (calculoDefinitivoConSicam.totalMesesComputablesDefinitivo > 0 && mesesFinales.length !== calculoDefinitivoConSicam.totalMesesComputablesDefinitivo) {
                setError(
                    `No se puede confirmar la importación: meses definitivos (${calculoDefinitivoConSicam.totalMesesComputablesDefinitivo}) ` +
                    `difieren de los meses únicos importables (${mesesFinales.length}). Revisá la configuración SICAM/HASTA 06/94.`
                );
                return;
            }

            const actuales = Number(aportesActualesCliente || 0);
            const aImportar = mesesFinales.length;
            setResumenReemplazo({ actuales, aImportar });
            setMesesPendientesImportacion(mesesDetallados);
            setModalReemplazoOpen(true);
        } catch (err) {
            const msg = err.response?.data || err.message || 'Error al confirmar';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
    }, [clienteSeleccionado, analisis, construirMesesDetalladosParaImportacion, calculoDefinitivoConSicam, aportesActualesCliente]);

    const handleReset = () => {
        clienteFijadoRef.current = null;
        setPasoActual(0);
        setArchivo(null);
        setRegistros([]);
        setAnalisis(null);
        setResumenReglas({
            registrosClasificados: [],
            registrosComputables: [],
            registrosExcluidos: [],
            alertas: [],
        });
        setNombreCompleto('');
        setCuil('');
        setRegistrosHasta0694([]);
        setRegistrosDesde0794([]);
        setAjustesDesde0794([]);
        setTabActiva('resumen');
        setCorrecciones([]);
        setTotalMesesComputables(0);
        setTotalMesesNoComputables(0);
        setMesesComputablesBase([]);
        setClienteSeleccionado(null);
        setAportesActualesCliente(null);
        setModalReemplazoOpen(false);
        setResumenReemplazo(null);
        setMesesPendientesImportacion([]);
        setImportacionResultado(null);
        setChequeoRtiIncluirExistentes(false);
        setChequeoRtiCargando(false);
        setChequeoRtiResultado(null);
        setChequeoRtiError(null);
        setFechaNacimientoPdf('');
        setMoratoriaFechaNacimiento('');
        setMoratoriaSexo('');
        setManualPeriodosSicam([]);
        setAsignacionesRegimen([]);
        setAsignacionesCaja([]);
        setMarcasFila({});
        setManualDesdeInput('');
        setManualHastaInput('');
        setManualTipoInput('MONOTRIBUTO');
        setMostrarNuevaFilaResumen(false);
        setNuevaFilaResumen({ razonSocial: '', cuitCuenta: '', desde: '', hasta: '' });

        setMostrarNuevaFilaHasta(false);
        setNuevaFilaHasta({ razonSocial: '', cuenta: '', anio: '', caracterServicios: '', altaBaja: '', mesesTrabajados: '' });

        setMostrarNuevaFilaDesde(false);
        setNuevaFilaDesde({ razonSocial: '', cuit: '', periodo: '', p: '', tr: '', dias: '', horas: '', remImpSs: '' });

        setBorradorGuardadoMsg(null);
        setError(null);
        setSuccessMsg(null);
    };

    // ══════════════════════════════════════
    // RENDER
    // ══════════════════════════════════════

    const renderStepper = (sinMargen = false) => (
        <div className="hl-stepper" style={sinMargen ? { marginBottom: 0 } : undefined}>
            {PASOS.map((paso, i) => (
                <div key={i} className={`hl-step ${i === pasoActual ? 'active' : ''} ${i < pasoActual ? 'completed' : ''}`}>
                    <span className="hl-step-number">
                        {i < pasoActual ? '✓' : i + 1}
                    </span>
                    <span className="hl-step-label">{paso}</span>
                </div>
            ))}
        </div>
    );

    const renderAccionSuperiorIzquierda = () => {
        if (pasoActual === 2) {
            return (
                <button
                    className="hl-btn-outline"
                    onClick={() => { setPasoActual(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                    <i className="bi bi-arrow-left me-2"></i>Volver al Paso 2
                </button>
            );
        }
        return <span />;
    };

    const renderAccionSuperiorDerecha = () => {
        if (pasoActual === 1) {
            return (
                <button
                    className="hl-btn-primary"
                    disabled={analisis == null}
                    onClick={() => { setPasoActual(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                    Ir al Paso 3<i className="bi bi-arrow-right ms-2"></i>
                </button>
            );
        }
        return <span />;
    };

    const renderPaso1 = () => (
        <div>
            <div style={{ paddingTop: '1rem', borderTop: '1px solid #eaeaea' }}>
                <h5 style={{ color: '#555', marginBottom: '0.8rem' }}>
                    <i className="bi bi-cloud-arrow-down me-2"></i>Recuperar Borrador
                </h5>
                <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: '0.8rem' }}>
                    Si previamente guardaste tu progreso de revisión, buscalo por nombre, apellido o CUIL.
                </p>
                <div
                    className="hl-cliente-search"
                    style={{
                        marginBottom: '1.2rem',
                        maxWidth: '500px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                    }}
                >
                    <input
                        type="text"
                        value={busquedaBorradorStr}
                        onChange={(e) => handleBuscarBorrador(e.target.value)}
                        placeholder="Ej: Farias o 27-17398319-6"
                        style={{ flex: 1,
                            minWidth: 1,
                            padding: '0.6rem 1rem',
                            border: '1px solid #ddd' }}
                    />
                    <button
                        className="hl-btn-primary"
                        onClick={handleCargarBorrador}
                        disabled={!criterioBorrador.trim()}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        Recuperar Borrador
                    </button>
                    {resultadosBorrador.length > 0 && (
                        <div className="hl-cliente-dropdown">
                            {resultadosBorrador.map((b) => (
                                <div
                                    key={b.cuil}
                                    className="hl-cliente-option"
                                    onClick={() => handleSeleccionarBorrador(b)}
                                >
                                    <strong>{b.nombreCompleto || 'Sin nombre'}</strong>
                                    <span style={{ color: '#888', marginLeft: '0.5rem' }}>CUIL: {b.cuil}</span>
                                    {b.fechaActualizacion && (
                                        <span style={{ color: '#aaa', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                                            (Act: {new Date(b.fechaActualizacion).toLocaleDateString()})
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <h5 style={{ marginTop: '2rem', color: '#555' }}>
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Cargar PDF de Historia Laboral ANSES
            </h5>
            <div
                className={`hl-upload-zone ${dragOver ? 'dragover' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <div className="hl-upload-icon">
                    <i className="bi bi-cloud-arrow-up-fill"></i>
                </div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#555' }}>
                    Arrastrá el PDF aquí o hacé clic para seleccionar
                </p>
                <p style={{ fontSize: '0.85rem', color: '#999' }}>
                    Solo archivos PDF de "Historia Laboral" de ANSES
                </p>
                {archivo && (
                    <p style={{ color: '#1976d2', fontWeight: 600, marginTop: '0.5rem' }}>
                        <i className="bi bi-file-earmark-check me-1"></i> {archivo.name}
                    </p>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileInput}
                />
            </div>

            {/* E9: Carga manual sin archivo — para históricos sin aportes que el scanner no detecta */}
            <div style={{ marginTop: '1.5rem' }}>
                <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.85rem', marginBottom: '1rem' }}>
                    — o —
                </div>

                {!mostrarCargaManual ? (
                    <div style={{ textAlign: 'center' }}>
                        <button
                            type="button"
                            className="hl-btn-secondary"
                            onClick={() => { setMostrarCargaManual(true); setError(null); }}
                            style={{
                                padding: '0.6rem 1.2rem',
                                borderRadius: '8px',
                                border: '1px solid #1976d2',
                                background: '#fff',
                                color: '#1976d2',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            <i className="bi bi-pencil-square me-2"></i>
                            Cargar manualmente (sin archivo)
                        </button>
                        <p style={{ fontSize: '0.82rem', color: '#999', marginTop: '0.6rem' }}>
                            Usalo cuando el historial no tiene aportes y el lector no puede procesar el PDF.
                            Vas a poder cargar los períodos a mano en el paso 2.
                        </p>
                    </div>
                ) : (
                    <div
                        className="hl-info-card"
                        style={{ maxWidth: '560px', margin: '0 auto', padding: '1.2rem', border: '1px solid #e0e0e0', borderRadius: '10px' }}
                    >
                        <h6 style={{ color: '#555', marginBottom: '0.9rem' }}>
                            <i className="bi bi-person-vcard me-2"></i>Datos de la persona
                        </h6>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' }}>
                            <div>
                                <label style={{ fontSize: '0.8rem', color: '#777', display: 'block', marginBottom: '0.3rem' }}>Apellido</label>
                                <input
                                    type="text"
                                    value={manualApellido}
                                    onChange={(e) => setManualApellido(e.target.value)}
                                    placeholder="Ej: Cabrera"
                                    style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ddd' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '0.8rem', color: '#777', display: 'block', marginBottom: '0.3rem' }}>Nombre</label>
                                <input
                                    type="text"
                                    value={manualNombre}
                                    onChange={(e) => setManualNombre(e.target.value)}
                                    placeholder="Ej: Walter Oscar"
                                    style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ddd' }}
                                />
                            </div>
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#777', display: 'block', marginBottom: '0.3rem' }}>CUIL (11 dígitos)</label>
                            <input
                                type="text"
                                value={manualCuil}
                                onChange={(e) => setManualCuil(e.target.value)}
                                placeholder="Ej: 20-14099478-3"
                                style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setMostrarCargaManual(false);
                                    setManualApellido('');
                                    setManualNombre('');
                                    setManualCuil('');
                                    setError(null);
                                }}
                                style={{ padding: '0.55rem 1rem', borderRadius: '8px', border: '1px solid #ccc', background: '#fff', color: '#777', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="hl-btn-primary"
                                onClick={handleCargaManualSinArchivo}
                                disabled={!manualApellido.trim() || !manualNombre.trim() || manualCuil.replace(/\D/g, '').length !== 11}
                            >
                                Continuar al paso 2 <i className="bi bi-arrow-right ms-1"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // ─── Tab: Resumen Integrado (todas las secciones) ───
    const renderTabResumen = () => {
        if (!analisis) return null;

        const analisisHasta = analizarRegistrosHasta0694(registrosHasta0694, primerMesComputableYm);
        const mesesHasta = analisisHasta.totalMesesUnicos;
        const alertasReglasResumen = resumenReglas?.alertas || [];
        const excluidosMonotributo = alertasReglasResumen.find((a) => a.codigo === 'EXCLUIR_MONOTRIBUTO');
        const excluidosAutonomo = alertasReglasResumen.find((a) => a.codigo === 'EXCLUIR_AUTONOMO');
        const alertaServicioDomestico = alertasReglasResumen.find((a) => a.codigo === 'ALERTA_SERVICIO_DOMESTICO');
        const alertaMonotributoSocial = alertasReglasResumen.find((a) => a.codigo === 'ALERTA_MONOTRIBUTO_SOCIAL');
        const alertaUcap = alertasReglasResumen.find((a) => a.codigo === 'ALERTA_UCAP_27705');
        const alertaSubsidioDesempleo = alertasReglasResumen.find((a) => a.codigo === 'ALERTA_SUBSIDIO_DESEMPLEO');
        const periodosDesdeNoComputables = new Set(
            (ajustesDesde0794 || []).map(a => normalizarYearMonth(a.periodo)).filter(Boolean)
        );
        const mesesDesdeNoComputables = periodosDesdeNoComputables.size;
        const mesesPre94 = (analisis?.mesesExpandidos || []).filter(m => {
            const ym = normalizarYearMonth(m.yearMonth);
            return ym && ym < '1994-07' && (!primerMesComputableYm || ym >= primerMesComputableYm);
        }).length;
        const mesesPost94 = (analisis?.mesesExpandidos || []).filter(m => {
            const ym = normalizarYearMonth(m.yearMonth);
            return ym && ym >= '1994-07' && (!primerMesComputableYm || ym >= primerMesComputableYm);
        }).length;
        const registrosSicam = (resumenReglas?.registrosClasificados || []).filter(
            (r) => r.tipoAporteResumen === 'MONOTRIBUTO' || r.tipoAporteResumen === 'AUTONOMO'
        );
        const registrosRelDep = (resumenReglas?.registrosClasificados || []).filter(
            (r) => r.tipoAporteResumen === 'RELACION_DEPENDENCIA'
        );
        const registrosOtros = (resumenReglas?.registrosClasificados || []).filter(
            (r) => ['SERVICIO_DOMESTICO', 'MONOTRIBUTO_SOCIAL', 'LEY_27705'].includes(r.tipoAporteResumen)
        );
        const mesesManualSicamCount = calculoDefinitivoConSicam.mesesManualSicam?.length || 0;
        const totalAutomaticoBase = (calculoDefinitivoConSicam.mesesComputablesAutomaticos || []).length;
        const totalDefinitivoConManual = mesesManualSicamCount > 0
            ? (calculoDefinitivoConSicam.totalMesesComputablesDefinitivo || totalAutomaticoBase)
            : totalAutomaticoBase;
        const mesesAgregadosManual = Math.max(0, totalDefinitivoConManual - totalAutomaticoBase);
        // Meses SICAM que superponen con la base (ya existían, no suman al definitivo)
        const sicamSuperpuestos = Math.max(0, mesesManualSicamCount - mesesAgregadosManual);

        return (
            <div className="hl-step3-print-area">
                {/* ═══ Totales definitivos ═══ */}
                <div className="hl-cruce-totales" style={{ marginBottom: '1.2rem' }}>
                    <div className="hl-cruce-total-card computable">
                        <i className="bi bi-check-circle-fill"></i>
                        <div>
                            <div className="hl-cruce-total-value">{totalMesesComputables}</div>
                            <div className="hl-cruce-total-label">Meses computables</div>
                        </div>
                    </div>
                    <div className="hl-cruce-total-card computable" style={{ borderColor: '#7b1fa2' }}>
                        <i className="bi bi-plus-circle-fill" style={{ color: '#7b1fa2' }}></i>
                        <div>
                            <div className="hl-cruce-total-value" style={{ color: '#7b1fa2' }}>{mesesAgregadosManual}</div>
                            <div className="hl-cruce-total-label">Meses SICAM manual agregados</div>
                        </div>
                    </div>
                </div>

                {/* ═══ Estadísticas por sección ═══ */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.8rem', marginBottom: '1.2rem' }}>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-label" style={{ marginBottom: '0.2rem' }}>Resumen (únicos)</div>
                        <div className="hl-stat-value" style={{ color: '#1976d2' }}>{analisis.totalMesesUnicos}</div>
                        <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                            Pre 07/94: <strong>{mesesPre94}</strong> · Post: <strong>{mesesPost94}</strong>
                        </div>
                    </div>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-label" style={{ marginBottom: '0.2rem' }}>HASTA 06/94</div>
                        <div className="hl-stat-value" style={{ color: '#7b1fa2' }}>{mesesHasta}</div>
                        <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                            {registrosHasta0694.length} registros · {analisisHasta.mesesSuperpuestos} superpuestos
                        </div>
                    </div>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-label" style={{ marginBottom: '0.2rem' }}>DESDE 07/94</div>
                        <div className="hl-stat-value" style={{ color: mesesDesdeNoComputables > 0 ? '#d32f2f' : '#999' }}>{mesesDesdeNoComputables}</div>
                        <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.2rem' }}>
                            meses no computables
                        </div>
                    </div>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-label" style={{ marginBottom: '0.2rem' }}>Superpuestos</div>
                        <div className="hl-stat-value" style={{ color: (analisis.totalMesesSuperpuestos + sicamSuperpuestos) > 0 ? '#e65100' : '#999' }}>
                            {analisis.totalMesesSuperpuestos + sicamSuperpuestos}
                        </div>
                        {sicamSuperpuestos > 0 && (
                            <div style={{ fontSize: '0.62rem', color: '#e65100', marginTop: '0.1rem' }}>
                                {sicamSuperpuestos} de SICAM
                            </div>
                        )}
                    </div>
                </div>

                {/* ═══ U2: Fórmula de cálculo — chips visuales ═══ */}
                <div style={{ marginBottom: '1rem', padding: '0.7rem 0.9rem', background: '#e8f4fd', borderRadius: '10px', border: '1px solid #bee3f8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.55rem', flexWrap: 'wrap', gap: '0.3rem' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1976d2' }}>
                            <i className="bi bi-calculator me-1"></i>Cómputo de meses
                        </span>
                        {renderToggleAnios()}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem' }}>
                        {/* Chip pre-94 */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: '1px solid #90caf9', borderRadius: '8px', padding: '0.25rem 0.55rem', minWidth: '56px' }}>
                            <span style={{ fontWeight: 800, fontSize: '1rem', color: '#1565c0', lineHeight: 1.1 }}>
                                {formatearContador(mesesHasta > 0 ? mesesHasta : mesesPre94)}
                            </span>
                            <span style={{ fontSize: '0.62rem', color: '#555', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>
                                {mesesHasta > 0 ? 'HASTA 06/94' : 'pre-07/94'}
                            </span>
                        </div>

                        <span style={{ fontWeight: 700, color: '#1976d2', fontSize: '1rem' }}>+</span>

                        {/* Chip post-94 */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: '1px solid #90caf9', borderRadius: '8px', padding: '0.25rem 0.55rem', minWidth: '56px' }}>
                            <span style={{ fontWeight: 800, fontSize: '1rem', color: '#1565c0', lineHeight: 1.1 }}>
                                {formatearContador(mesesPost94)}
                            </span>
                            <span style={{ fontSize: '0.62rem', color: '#555', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>post-07/94</span>
                        </div>

                        {mesesDesdeNoComputables > 0 && <>
                            <span style={{ fontWeight: 700, color: '#d32f2f', fontSize: '1rem' }}>−</span>
                            {/* Chip descuento DESDE */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff8f8', border: '1px solid #ef9a9a', borderRadius: '8px', padding: '0.25rem 0.55rem', minWidth: '56px' }}>
                                <span style={{ fontWeight: 800, fontSize: '1rem', color: '#c62828', lineHeight: 1.1 }}>
                                    {formatearContador(mesesDesdeNoComputables)}
                                </span>
                                <span style={{ fontSize: '0.62rem', color: '#555', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>no comp.</span>
                            </div>
                        </>}

                        <span style={{ fontWeight: 700, color: '#1976d2', fontSize: '1.1rem' }}>=</span>

                        {/* Chip resultado */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#1976d2', border: '1px solid #1565c0', borderRadius: '8px', padding: '0.3rem 0.7rem', minWidth: '64px' }}>
                            <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff', lineHeight: 1.1 }}>
                                {formatearContador(totalMesesComputables)}
                            </span>
                            <span style={{ fontSize: '0.62rem', color: '#bbdefb', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>COMPUTABLES</span>
                        </div>

                        {mesesAgregadosManual > 0 && <>
                            <span style={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1rem' }}>+</span>
                            {/* Chip SICAM manual — muestra solo los meses netos (sin superpuestos con la base) */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fdf4ff', border: '1px solid #ce93d8', borderRadius: '8px', padding: '0.25rem 0.55rem', minWidth: '56px' }}>
                                <span style={{ fontWeight: 800, fontSize: '1rem', color: '#7b1fa2', lineHeight: 1.1 }}>
                                    {formatearContador(mesesAgregadosManual)}
                                </span>
                                <span style={{ fontSize: '0.62rem', color: '#555', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>SICAM</span>
                            </div>

                            <span style={{ fontWeight: 700, color: '#1976d2', fontSize: '1.1rem' }}>=</span>

                            {/* Chip definitivo */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#2e7d32', border: '1px solid #1b5e20', borderRadius: '8px', padding: '0.3rem 0.7rem', minWidth: '64px' }}>
                                <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#fff', lineHeight: 1.1 }}>
                                    {formatearContador(totalDefinitivoConManual)}
                                </span>
                                <span style={{ fontSize: '0.62rem', color: '#a5d6a7', marginTop: '0.1rem', textAlign: 'center', lineHeight: 1.2 }}>DEFINITIVO</span>
                            </div>
                        </>}
                    </div>
                    {sicamSuperpuestos > 0 && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#e65100', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            <span>
                                <strong>{sicamSuperpuestos} mes{sicamSuperpuestos === 1 ? '' : 'es'} SICAM</strong> ya exist{sicamSuperpuestos === 1 ? 'ía' : 'ían'} en la base y no se sumaron al definitivo (superpuesto{sicamSuperpuestos === 1 ? '' : 's'} con relación de dependencia u otros aportes).
                            </span>
                        </div>
                    )}
                </div>

                {(excluidosMonotributo || excluidosAutonomo) && (
                    <div className="hl-alert hl-alert-warning" style={{ marginBottom: '0.8rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Registros excluidos del cómputo automático:</strong>{' '}
                        {excluidosMonotributo && (
                            <span>
                                Monotributo: <strong>{excluidosMonotributo.cantidadRegistros}</strong> registros ({excluidosMonotributo.cantidadMesesUnicos} meses únicos){' '}
                            </span>
                        )}
                        {excluidosAutonomo && (
                            <span>
                                Autónomo: <strong>{excluidosAutonomo.cantidadRegistros}</strong> registros ({excluidosAutonomo.cantidadMesesUnicos} meses únicos){''}
                            </span>
                        )}
                        . Revisar SICAM y cargar manualmente los períodos si corresponde.
                    </div>
                )}

                {(alertaServicioDomestico || alertaMonotributoSocial || alertaUcap || alertaSubsidioDesempleo) && (
                    <div className="hl-alert hl-alert-info" style={{ marginBottom: '1rem' }}>
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Alertas de tipología de aporte:</strong>{' '}
                        {alertaServicioDomestico && <span>Servicio doméstico: {alertaServicioDomestico.cantidadMesesUnicos} meses. </span>}
                        {alertaMonotributoSocial && <span>Monotributo social: {alertaMonotributoSocial.cantidadMesesUnicos} meses. </span>}
                        {alertaUcap && <span>Ley 27705 (UCAP): {alertaUcap.cantidadMesesUnicos} meses. </span>}
                        {alertaSubsidioDesempleo && <span>Subsidio/Prestación por desempleo: {alertaSubsidioDesempleo.cantidadMesesUnicos} meses (computables, excluidos de comparación pre-94). </span>}
                    </div>
                )}

                {/* ═══ Correcciones / advertencias ═══ */}
                {correcciones.length > 0 && (
                    <div style={{ marginBottom: '1.2rem' }}>
                        <h6 style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                            <i className="bi bi-exclamation-diamond me-2" style={{ color: '#e65100' }}></i>
                            Correcciones ({correcciones.length})
                        </h6>
                        {correcciones.map((corr, i) => (
                            <div key={i} className={`hl-correccion-item hl-correccion-${corr.severidad?.toLowerCase() || 'info'}`}>
                                <div className="hl-correccion-header">
                                    <span className={`hl-correccion-badge hl-correccion-badge-${corr.severidad?.toLowerCase() || 'info'}`}>
                                        {corr.severidad || 'INFO'}
                                    </span>
                                    <span className="hl-correccion-fuente">
                                        {corr.fuente === 'HASTA_0694' ? 'HASTA 06/94' : corr.fuente === 'DESDE_0794' ? 'DESDE 07/94' : corr.fuente}
                                    </span>
                                </div>
                                <div className="hl-correccion-mensaje">{corr.mensaje}</div>
                                {corr.mesesOriginales != null && corr.mesesCorregidos != null && corr.diferencia != null && corr.diferencia !== 0 && (
                                    <div className="hl-correccion-detalle">
                                        <span>Original: <strong>{corr.mesesOriginales}</strong></span>
                                        <i className="bi bi-arrow-right" style={{ margin: '0 0.4rem' }}></i>
                                        <span>Corregido: <strong>{corr.mesesCorregidos}</strong></span>
                                        <span style={{ marginLeft: '0.4rem', color: corr.diferencia > 0 ? '#d32f2f' : '#2e7d32' }}>
                                            ({corr.diferencia > 0 ? '-' : '+'}{Math.abs(corr.diferencia)})
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* ═══ Tabla de registros del Resumen (editable) ═══ */}
                <h6 style={{ fontWeight: 600, marginBottom: '0.6rem' }}>
                    <i className="bi bi-table me-2"></i>Registros del Resumen ({registros.length})
                </h6>
                <div className="hl-table-wrapper" style={{ marginBottom: '1.2rem' }}>
                    <table className="hl-table">
                        <thead>
                            <tr>
                                <th style={{ width: '30px' }}>#</th>
                                <th>Razón Social</th>
                                <th>CUIT/Cuenta</th>
                                <th style={{ width: '150px' }}>Tipo aporte</th>
                                <th style={{ width: '110px' }}>Desde</th>
                                <th style={{ width: '110px' }}>Hasta</th>
                                <th style={{ width: '70px' }}>Meses</th>
                                <th style={{ width: '170px' }}>Régimen / Caja</th>
                                <th style={{ width: '50px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map((reg) => {
                                const claveFila = `R:${reg.indiceOriginal}`;
                                const marcaFila = marcasFila[claveFila] || { regimenId: '', cajaId: '' };
                                const rangoValido = validarPeriodo(reg.desde) && validarPeriodo(reg.hasta) && validarRango(reg.desde, reg.hasta);
                                let cantMeses = 0;
                                if (rangoValido) {
                                    const d = reg.desde.split('/');
                                    const h = reg.hasta.split('/');
                                    cantMeses = (parseInt(h[1]) * 12 + parseInt(h[0])) - (parseInt(d[1]) * 12 + parseInt(d[0])) + 1;
                                }
                                return (
                                    <tr key={reg.indiceOriginal}>
                                        <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{reg.indiceOriginal + 1}</td>
                                        <td><input value={reg.razonSocial} onChange={(e) => handleEditRegistro(reg.indiceOriginal, 'razonSocial', e.target.value)} /></td>
                                        <td><input value={reg.cuitCuenta} onChange={(e) => handleEditRegistro(reg.indiceOriginal, 'cuitCuenta', e.target.value)} /></td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`hl-correccion-badge ${reg.excluirDelComputo ? 'hl-correccion-badge-warning' : 'hl-correccion-badge-info'}`}>
                                                {reg.tipoAporteResumen === 'RELACION_DEPENDENCIA' ? 'REL. DEP.' : reg.tipoAporteResumen?.replaceAll('_', ' ')}
                                            </span>
                                        </td>
                                        <td><input className={!validarPeriodo(reg.desde) ? 'input-error' : ''} value={reg.desde} placeholder="MM/YYYY" onChange={(e) => handleEditRegistro(reg.indiceOriginal, 'desde', e.target.value)} /></td>
                                        <td><input className={!validarPeriodo(reg.hasta) || (validarPeriodo(reg.desde) && !validarRango(reg.desde, reg.hasta)) ? 'input-error' : ''} value={reg.hasta} placeholder="MM/YYYY" onChange={(e) => handleEditRegistro(reg.indiceOriginal, 'hasta', e.target.value)} /></td>
                                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{rangoValido ? cantMeses : '-'}</td>
                                        <td>{renderCeldaMarcas('R', reg.razonSocial, claveFila, marcaFila)}</td>
                                        <td><button className="hl-btn-danger" onClick={() => handleEliminarRegistro(reg.indiceOriginal)} title="Eliminar"><i className="bi bi-trash"></i></button></td>
                                    </tr>
                                );
                            })}

                            {!mostrarNuevaFilaResumen && (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center' }}>
                                        <button className="hl-btn-outline" onClick={handleIniciarNuevaFilaResumen} style={{ minWidth: '220px' }}>
                                            <i className="bi bi-plus-lg me-2"></i>Agregar período al resumen
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {mostrarNuevaFilaResumen && (
                                <tr>
                                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#1976d2' }}>+</td>
                                    <td>
                                        <input
                                            value={nuevaFilaResumen.razonSocial}
                                            placeholder="Razón Social"
                                            onChange={(e) => setNuevaFilaResumen((prev) => ({ ...prev, razonSocial: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={nuevaFilaResumen.cuitCuenta}
                                            placeholder="CUIT/Cuenta"
                                            onChange={(e) => setNuevaFilaResumen((prev) => ({ ...prev, cuitCuenta: e.target.value }))}
                                        />
                                    </td>
                                    <td className="readonly" style={{ textAlign: 'center', color: '#777' }}>AUTO</td>
                                    <td>
                                        <input
                                            className={!validarPeriodo(nuevaFilaResumen.desde) && nuevaFilaResumen.desde ? 'input-error' : ''}
                                            value={nuevaFilaResumen.desde}
                                            placeholder="MM/YYYY"
                                            onChange={(e) => setNuevaFilaResumen((prev) => ({ ...prev, desde: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className={
                                                (!validarPeriodo(nuevaFilaResumen.hasta) && nuevaFilaResumen.hasta) ||
                                                (validarPeriodo(nuevaFilaResumen.desde) && validarPeriodo(nuevaFilaResumen.hasta) && !validarRango(nuevaFilaResumen.desde, nuevaFilaResumen.hasta))
                                                    ? 'input-error'
                                                    : ''
                                            }
                                            value={nuevaFilaResumen.hasta}
                                            placeholder="MM/YYYY"
                                            onChange={(e) => setNuevaFilaResumen((prev) => ({ ...prev, hasta: e.target.value }))}
                                        />
                                    </td>
                                    <td style={{ textAlign: 'center', fontWeight: 600 }}>
                                        {validarPeriodo(nuevaFilaResumen.desde) && validarPeriodo(nuevaFilaResumen.hasta) && validarRango(nuevaFilaResumen.desde, nuevaFilaResumen.hasta)
                                            ? ((parseInt(nuevaFilaResumen.hasta.split('/')[1], 10) * 12 + parseInt(nuevaFilaResumen.hasta.split('/')[0], 10)) -
                                                (parseInt(nuevaFilaResumen.desde.split('/')[1], 10) * 12 + parseInt(nuevaFilaResumen.desde.split('/')[0], 10)) + 1)
                                            : '-'}
                                    </td>
                                    <td className="readonly" style={{ textAlign: 'center', color: '#aaa', fontSize: '0.72rem' }}>guardá primero</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button className="hl-btn-success" onClick={handleGuardarNuevaFilaResumen} title="Guardar" style={{ padding: '0.3rem 0.5rem', marginRight: '0.3rem' }}>
                                            <i className="bi bi-check-lg"></i>
                                        </button>
                                        <button className="hl-btn-outline" onClick={handleCancelarNuevaFilaResumen} title="Cancelar" style={{ padding: '0.3rem 0.5rem' }}>
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {resumenMarcas.hay && (
                    <div className="hl-alert hl-alert-info" style={{ marginBottom: '1rem' }}>
                        <i className="bi bi-flag-fill me-2"></i>
                        <strong>Períodos marcados:</strong>{' '}
                        {resumenMarcas.regimenes.map((r) => `${r.nombre} (${r.filas} fila${r.filas === 1 ? '' : 's'})`).join(', ')}
                        {resumenMarcas.regimenes.length > 0 && resumenMarcas.cajas.length > 0 ? ' · ' : ''}
                        {resumenMarcas.cajas.map((c) => `${c.nombre}${c.esCordoba ? ' (provincial)' : ''} (${c.filas} fila${c.filas === 1 ? '' : 's'})`).join(', ')}.
                        {' '}El prorrateo y la caja otorgante se calculan en el Paso 3.
                    </div>
                )}

                <div className="hl-info-card" style={{ marginBottom: '1rem' }}>
                    <h6 style={{ marginBottom: '0.5rem' }}>
                        <i className="bi bi-plus-square me-2"></i>
                        Carga manual de períodos SICAM
                    </h6>
                    <p style={{ marginBottom: '0.6rem', color: '#666', fontSize: '0.85rem' }}>
                        Períodos excluidos del cómputo automático (Monotributo, Autónomo) o ya comprados bajo Ley 24.476.
                        Se suman a la importación final.
                    </p>

                    <div className="hl-sicam-form-grid">
                        <div className="hl-sicam-field">
                            <label>Desde</label>
                            <input
                                value={manualDesdeInput}
                                placeholder="MM/YYYY"
                                onChange={(e) => setManualDesdeInput(e.target.value)}
                            />
                        </div>
                        <div className="hl-sicam-field">
                            <label>Hasta</label>
                            <input
                                value={manualHastaInput}
                                placeholder="MM/YYYY"
                                onChange={(e) => setManualHastaInput(e.target.value)}
                            />
                        </div>
                        <div className="hl-sicam-field">
                            <label>Tipo</label>
                            <select value={manualTipoInput} onChange={(e) => setManualTipoInput(e.target.value)}>
                                <option value="MONOTRIBUTO">Monotributo</option>
                                <option value="AUTONOMO">Autónomo</option>
                                <option value="LEY_24476">Ley 24.476 (ya comprada)</option>
                                <option value="NO_APORTANTE">No aportante (ocupa lugar, no computa)</option>
                            </select>
                        </div>
                        <div className="hl-sicam-actions">
                            <button className="hl-btn-outline" onClick={agregarPeriodoManualSicam}>
                                <i className="bi bi-plus-lg me-1"></i>Agregar período
                            </button>
                        </div>
                    </div>

                    {manualPeriodosSicam.length > 0 && (
                        <div className="hl-table-wrapper">
                            <table className="hl-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '130px' ,  alignItems: 'center'}}>Tipo</th>
                                        <th style={{ width: '110px' ,  alignItems: 'center'}}>Desde</th>
                                        <th style={{ width: '110px' ,  alignItems: 'center'}}>Hasta</th>
                                        <th style={{ width: '95px' ,  alignItems: 'center'}}>Meses</th>
                                        <th style={{ width: '60px' ,  alignItems: 'center'}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manualPeriodosSicam.map((p) => {
                                        const tipoLabel = p.tipo === 'MONOTRIBUTO' ? 'Monotributo'
                                            : p.tipo === 'AUTONOMO' ? 'Autónomo'
                                            : p.tipo === 'LEY_24476' ? 'Ley 24.476'
                                            : p.tipo === TIPO_NO_APORTANTE ? 'No aportante'
                                            : p.tipo;
                                        const esLey24476 = p.tipo === 'LEY_24476';
                                        const esNoAportante = p.tipo === TIPO_NO_APORTANTE;
                                        return (
                                            <tr key={p.id}>
                                                <td>
                                                    <span className={`hl-correccion-badge ${esNoAportante ? 'hl-correccion-badge-critico' : esLey24476 ? 'hl-correccion-badge-warning' : 'hl-correccion-badge-info'}`}
                                                        title={esNoAportante ? 'Ocupa lugar: no se puede comprar por moratoria ni estrategia, y no suma como mes computable.' : undefined}>
                                                        {tipoLabel}
                                                    </span>
                                                </td>
                                                <td>{formatearYearMonth(p.desde)}</td>
                                                <td>{formatearYearMonth(p.hasta)}</td>
                                                <td style={{ textAlign: 'center' }}>{p.cantidadMeses} meses{esNoAportante ? ' (no computan)' : ''}</td>
                                                <td>
                                                    <button className="hl-btn-danger" onClick={() => eliminarPeriodoManualSicam(p.id)} title="Quitar período">
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ═══ Tablas visuales por tipo de aporte ═══ */}
                <h6 style={{ fontWeight: 600, marginBottom: '0.6rem' }}>
                    <i className="bi bi-diagram-3 me-2"></i>Vista por tipo de aporte
                </h6>

                <details style={{ marginBottom: '1rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                        <i className="bi bi-shield-exclamation me-2"></i>
                        SICAM detectado en PDF (no cargado manualmente) ({registrosSicam.length} registros)
                    </summary>
                    <div className="hl-table-wrapper" style={{ marginTop: '0.5rem' }}>
                        <table className="hl-table">
                            <thead>
                                <tr>
                                    <th>Razón Social</th>
                                    <th style={{ width: '150px' }}>CUIT/Cuenta</th>
                                    <th style={{ width: '130px' }}>Tipo</th>
                                    <th style={{ width: '110px' }}>Desde</th>
                                    <th style={{ width: '110px' }}>Hasta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registrosSicam.map((reg) => (
                                    <tr key={`sicam-${reg.indiceOriginal}`}>
                                        <td>{reg.razonSocial}</td>
                                        <td>{reg.cuitCuenta}</td>
                                        <td>{reg.tipoAporteResumen?.replaceAll('_', ' ')}</td>
                                        <td>{reg.desde}</td>
                                        <td>{reg.hasta}</td>
                                    </tr>
                                ))}
                                {registrosSicam.length === 0 && (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#777' }}>No se detectaron líneas SICAM en el resumen.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </details>

                <details style={{ marginBottom: '1rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                        <i className="bi bi-briefcase me-2"></i>Relación de dependencia ({registrosRelDep.length} registros)
                    </summary>
                    <div className="hl-table-wrapper" style={{ marginTop: '0.5rem' }}>
                        <table className="hl-table">
                            <thead>
                                <tr>
                                    <th>Razón Social</th>
                                    <th style={{ width: '150px' }}>CUIT/Cuenta</th>
                                    <th style={{ width: '110px' }}>Desde</th>
                                    <th style={{ width: '110px' }}>Hasta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registrosRelDep.map((reg) => (
                                    <tr key={`rd-${reg.indiceOriginal}`}>
                                        <td>{reg.razonSocial}</td>
                                        <td>{reg.cuitCuenta}</td>
                                        <td>{reg.desde}</td>
                                        <td>{reg.hasta}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>

                <details style={{ marginBottom: '1.2rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                        <i className="bi bi-tags me-2"></i>Otros tipos computables ({registrosOtros.length} registros)
                    </summary>
                    <div className="hl-table-wrapper" style={{ marginTop: '0.5rem' }}>
                        <table className="hl-table">
                            <thead>
                                <tr>
                                    <th>Razón Social</th>
                                    <th style={{ width: '150px' }}>CUIT/Cuenta</th>
                                    <th style={{ width: '170px' }}>Tipo</th>
                                    <th style={{ width: '110px' }}>Desde</th>
                                    <th style={{ width: '110px' }}>Hasta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registrosOtros.map((reg) => (
                                    <tr key={`ot-${reg.indiceOriginal}`}>
                                        <td>{reg.razonSocial}</td>
                                        <td>{reg.cuitCuenta}</td>
                                        <td>{reg.tipoAporteResumen?.replaceAll('_', ' ')}</td>
                                        <td>{reg.desde}</td>
                                        <td>{reg.hasta}</td>
                                    </tr>
                                ))}
                                {registrosOtros.length === 0 && (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#777' }}>No hay otros tipos computables detectados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </details>

                {/* Solapamientos */}
                {analisis.solapamientos.length > 0 && (
                    <div style={{ marginBottom: '1.2rem' }}>
                        <h6 style={{ fontWeight: 600, color: '#e65100', marginBottom: '0.6rem' }}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Superposiciones detectadas ({analisis.solapamientos.length} meses)
                        </h6>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {analisis.solapamientos.map((sol, i) => (
                                <div key={i} className="hl-solapamiento-item">
                                    <strong>{formatearYearMonth(sol.yearMonth)}</strong>
                                    <span style={{ color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                                        — {sol.empleadoresInvolucrados.join(' / ')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Meses expandidos */}
                <details style={{ marginBottom: '1.2rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                        <i className="bi bi-calendar3 me-2"></i>
                        Ver todos los meses expandidos ({analisis.mesesExpandidos.length})
                    </summary>
                    <div className="hl-meses-grid" style={{ marginTop: '0.5rem' }}>
                        {analisis.mesesExpandidos.map((m, i) => (
                            <span key={i} className={`hl-mes-chip ${m.superpuesto ? 'superpuesto' : ''}`}>
                                {formatearYearMonth(m.yearMonth)}
                            </span>
                        ))}
                    </div>
                </details>
            </div>
        );
    };

    // ─── Tab: HASTA 06/94 ───
    const renderTabHasta0694 = () => {
        const analisisHasta = analizarRegistrosHasta0694(registrosHasta0694, primerMesComputableYm);
        const totalMeses = analisisHasta.totalMesesUnicos;
        const sinMeses = registrosHasta0694.filter(r => r.mesesTrabajados == null || r.mesesTrabajados === 0);

        return (
            <div>
                {/* Estadísticas */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.8rem', marginBottom: '1.2rem' }}>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-value" style={{ color: '#1976d2' }}>{registrosHasta0694.length}</div>
                        <div className="hl-stat-label">Registros</div>
                    </div>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-value" style={{ color: '#2e7d32' }}>{totalMeses}</div>
                        <div className="hl-stat-label">Meses únicos computables</div>
                    </div>
                    {analisisHasta.mesesSuperpuestos > 0 && (
                        <div className="hl-info-card" style={{ textAlign: 'center' }}>
                            <div className="hl-stat-value" style={{ color: '#f57c00' }}>{analisisHasta.mesesSuperpuestos}</div>
                            <div className="hl-stat-label">Meses superpuestos</div>
                        </div>
                    )}
                    {sinMeses.length > 0 && (
                        <div className="hl-info-card" style={{ textAlign: 'center' }}>
                            <div className="hl-stat-value" style={{ color: '#f57f17' }}>{sinMeses.length}</div>
                            <div className="hl-stat-label">Sin meses declarados</div>
                        </div>
                    )}
                    {analisisHasta.mesesExcluidosPorEdad > 0 && (
                        <div className="hl-info-card" style={{ textAlign: 'center' }}>
                            <div className="hl-stat-value" style={{ color: '#9c27b0' }}>{analisisHasta.mesesExcluidosPorEdad}</div>
                            <div className="hl-stat-label">Excluidos por edad (menores de 18)</div>
                        </div>
                    )}
                </div>

                {analisisHasta.mesesExcluidosPorEdad > 0 && (
                    <div className="hl-alert hl-alert-warning" style={{ marginBottom: '1rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>{analisisHasta.mesesExcluidosPorEdad} mes{analisisHasta.mesesExcluidosPorEdad > 1 ? 'es' : ''} excluido{analisisHasta.mesesExcluidosPorEdad > 1 ? 's' : ''}</strong>{' '}
                        por ser anteriores al mes en que la persona cumple 18 años
                        ({primerMesComputableYm?.split('-').reverse().join('/')}).
                        Aportes anteriores a esa fecha no son computables para la jubilación.
                    </div>
                )}

                <div className="hl-alert hl-alert-info" style={{ marginBottom: '1rem' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    La columna <strong>Meses Trab.</strong> es el valor definitivo para el cómputo.
                    Alta/Baja + Año solo se usan para identificar los meses específicos al importar.
                    Si alta/baja no es válida, se aplica fallback al fin del año.
                </div>

                {/* Tabla editable */}
                <div className="hl-table-wrapper" style={{ marginBottom: '1.2rem' }}>
                    <table className="hl-table">
                        <thead>
                            <tr>
                                <th style={{ width: '30px' }}>#</th>
                                <th>Razón Social</th>
                                <th>Cuenta</th>
                                <th style={{ width: '70px' }}>Año</th>
                                <th style={{ width: '50px' }}>Carác.</th>
                                <th style={{ width: '130px' }}>Alta/Baja</th>
                                <th style={{ width: '90px' }}>Meses Trab.</th>
                                <th style={{ width: '170px' }}>Régimen / Caja</th>
                                <th style={{ width: '50px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {registrosHasta0694.map((reg) => (
                                <tr key={reg.indiceOriginal} className={reg.mesesTrabajados == null ? 'hl-fila-sin-meses' : ''}>
                                    <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{reg.indiceOriginal + 1}</td>
                                    <td><input value={reg.razonSocial} onChange={(e) => handleEditHasta0694(reg.indiceOriginal, 'razonSocial', e.target.value)} /></td>
                                    <td><input value={reg.cuenta} onChange={(e) => handleEditHasta0694(reg.indiceOriginal, 'cuenta', e.target.value)} /></td>
                                    <td><input value={reg.anio} style={{ textAlign: 'center', width: '90px' }} onChange={(e) => handleEditHasta0694(reg.indiceOriginal, 'anio', e.target.value)} /></td>
                                    <td className="readonly" style={{ textAlign: 'center' }}>{reg.caracterServicios}</td>
                                    <td>
                                        <input
                                            value={reg.altaBaja || ''}
                                            placeholder="DD/MM - DD/MM"
                                            style={{ textAlign: 'center' }}
                                            onChange={(e) => handleEditHasta0694(reg.indiceOriginal, 'altaBaja', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={reg.mesesTrabajados != null ? reg.mesesTrabajados : ''}
                                            placeholder="—"
                                            style={{ textAlign: 'center', fontWeight: 700 }}
                                            onChange={(e) => handleEditHasta0694(reg.indiceOriginal, 'mesesTrabajados', e.target.value)}
                                        />
                                    </td>
                                    <td>{renderCeldaMarcas('H', reg.razonSocial, `H:${reg.indiceOriginal}`, marcasFila[`H:${reg.indiceOriginal}`] || { regimenId: '', cajaId: '' })}</td>
                                    <td><button className="hl-btn-danger" onClick={() => handleEliminarHasta0694(reg.indiceOriginal)} title="Eliminar"><i className="bi bi-trash"></i></button></td>
                                </tr>
                            ))}

                            {!mostrarNuevaFilaHasta && (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center' }}>
                                        <button className="hl-btn-outline" onClick={handleIniciarNuevaFilaHasta} style={{ minWidth: '220px' }}>
                                            <i className="bi bi-plus-lg me-2"></i>Agregar período a HASTA 06/94
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {mostrarNuevaFilaHasta && (
                                // Formulario simplificado: solo empleador (opcional), año y cantidad de meses.
                                // Alta/baja se omite — el mapeo a mes específico se hace internamente por fallback.
                                <tr>
                                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#1976d2' }}>+</td>
                                    <td colSpan={2}>
                                        <input
                                            value={nuevaFilaHasta.razonSocial}
                                            placeholder="Empleador (opcional)"
                                            onChange={(e) => setNuevaFilaHasta((prev) => ({ ...prev, razonSocial: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={nuevaFilaHasta.anio}
                                            style={{ textAlign: 'center' }}
                                            placeholder="Año"
                                            onChange={(e) => setNuevaFilaHasta((prev) => ({ ...prev, anio: e.target.value }))}
                                        />
                                    </td>
                                    <td className="readonly" style={{ textAlign: 'center', color: '#aaa', fontSize: '0.78rem' }}>—</td>
                                    <td className="readonly" style={{ textAlign: 'center', color: '#aaa', fontSize: '0.78rem' }}>—</td>
                                    <td>
                                        <input
                                            value={nuevaFilaHasta.mesesTrabajados != null ? nuevaFilaHasta.mesesTrabajados : ''}
                                            placeholder="Meses"
                                            style={{ textAlign: 'center', fontWeight: 700 }}
                                            onChange={(e) => setNuevaFilaHasta((prev) => ({ ...prev, mesesTrabajados: e.target.value }))}
                                        />
                                    </td>
                                    <td className="readonly" style={{ textAlign: 'center', color: '#aaa', fontSize: '0.72rem' }}>guardá primero</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button className="hl-btn-success" onClick={handleGuardarNuevaFilaHasta} title="Guardar" style={{ padding: '0.3rem 0.5rem', marginRight: '0.3rem' }}>
                                            <i className="bi bi-check-lg"></i>
                                        </button>
                                        <button className="hl-btn-outline" onClick={handleCancelarNuevaFilaHasta} title="Cancelar" style={{ padding: '0.3rem 0.5rem' }}>
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {registrosHasta0694.length === 0 && (
                    <div className="hl-alert hl-alert-warning" style={{ marginTop: '1rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No se encontraron registros en la sección "HASTA 06/94".
                    </div>
                )}
            </div>
        );
    };

    // ─── Tab: DESDE 07/94 (control de no computables) ───
    const renderTabDesde0794 = () => {
        const periodosNoComputables = new Set(registrosDesde0794.filter(r => r.remImpSsEsCero).map(r => r.periodo));

        return (
            <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.8rem', marginBottom: '1.2rem' }}>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-value" style={{ color: '#1976d2' }}>{registrosDesde0794.length}</div>
                        <div className="hl-stat-label">Registros DESDE 07/94</div>
                    </div>
                    <div className="hl-info-card" style={{ textAlign: 'center' }}>
                        <div className="hl-stat-value" style={{ color: periodosNoComputables.size > 0 ? '#d32f2f' : '#2e7d32' }}>{periodosNoComputables.size}</div>
                        <div className="hl-stat-label">Meses no computables</div>
                    </div>
                </div>

                <div className="hl-alert hl-alert-info" style={{ marginBottom: '1rem' }}>
                    <i className="bi bi-shield-exclamation me-2"></i>
                    Esta sección se usa solo como control. Genera descuentos cuando <strong>REM IMP. SS</strong> está en el rango <strong>[0, 10]</strong> (valores bajos indican período sin aporte real).
                </div>

                {ajustesDesde0794.length > 0 && (
                    <div style={{ marginBottom: '1.2rem' }}>
                        <h6 style={{ fontWeight: 600, marginBottom: '0.6rem' }}>
                            <i className="bi bi-list-check me-2"></i>Ajustes aplicados ({ajustesDesde0794.length})
                        </h6>
                        <div className="hl-table-wrapper">
                            <table className="hl-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '110px' }}>Período</th>
                                        <th>Razón Social</th>
                                        <th style={{ width: '150px' }}>CUIT</th>
                                        <th style={{ width: '190px' }}>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ajustesDesde0794.map((adj, idx) => (
                                        <tr key={`${adj.periodo}-${adj.cuit}-${idx}`}>
                                            <td>{adj.periodo}</td>
                                            <td>{adj.razonSocial}</td>
                                            <td>{adj.cuit}</td>
                                            <td><span className="hl-correccion-badge hl-correccion-badge-info">REM IMP. SS EN [0,10]</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* E8/U3: columnas P, T.R., Días y Horas ocultas — no influyen en el cálculo */}
                <div className="hl-table-wrapper" style={{ marginTop: '0.6rem' }}>
                    <table className="hl-table">
                        <thead>
                            <tr>
                                <th style={{ width: '30px' }}>#</th>
                                <th>Razón Social</th>
                                <th style={{ width: '140px' }}>CUIT</th>
                                <th style={{ width: '95px' }}>Período</th>
                                <th style={{ width: '110px' }}>Rem. Imp. SS</th>
                                <th style={{ width: '50px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {registrosDesde0794.map((row) => (
                                <tr key={row.indiceOriginal} className={row.remImpSsEsCero ? 'hl-fila-sin-meses' : ''}>
                                    <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{row.indiceOriginal + 1}</td>
                                    <td><input value={row.razonSocial} onChange={(e) => handleEditDesde0794(row.indiceOriginal, 'razonSocial', e.target.value)} /></td>
                                    <td><input value={row.cuit} onChange={(e) => handleEditDesde0794(row.indiceOriginal, 'cuit', e.target.value)} /></td>
                                    <td><input value={row.periodo} style={{ textAlign: 'center' }} onChange={(e) => handleEditDesde0794(row.indiceOriginal, 'periodo', e.target.value)} /></td>
                                    <td>
                                        <input
                                            value={row.remImpSs}
                                            style={{ textAlign: 'center', fontWeight: row.remImpSsEsCero ? 700 : 500, color: row.remImpSsEsCero ? '#d32f2f' : 'inherit' }}
                                            onChange={(e) => handleEditDesde0794(row.indiceOriginal, 'remImpSs', e.target.value)}
                                        />
                                    </td>
                                    <td><button className="hl-btn-danger" onClick={() => handleEliminarDesde0794(row.indiceOriginal)} title="Eliminar"><i className="bi bi-trash"></i></button></td>
                                </tr>
                            ))}

                            {!mostrarNuevaFilaDesde && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center' }}>
                                        <button className="hl-btn-outline" onClick={handleIniciarNuevaFilaDesde} style={{ minWidth: '220px' }}>
                                            <i className="bi bi-plus-lg me-2"></i>Agregar período a DESDE 07/94
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {mostrarNuevaFilaDesde && (
                                <tr>
                                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#1976d2' }}>+</td>
                                    <td>
                                        <input
                                            value={nuevaFilaDesde.razonSocial}
                                            placeholder="Razón Social"
                                            onChange={(e) => setNuevaFilaDesde((prev) => ({ ...prev, razonSocial: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={nuevaFilaDesde.cuit}
                                            placeholder="CUIT"
                                            onChange={(e) => setNuevaFilaDesde((prev) => ({ ...prev, cuit: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={nuevaFilaDesde.periodo}
                                            placeholder="MM/YYYY"
                                            style={{ textAlign: 'center' }}
                                            onChange={(e) => setNuevaFilaDesde((prev) => ({ ...prev, periodo: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            value={nuevaFilaDesde.remImpSs}
                                            placeholder="Rem"
                                            style={{ textAlign: 'center' }}
                                            onChange={(e) => setNuevaFilaDesde((prev) => ({ ...prev, remImpSs: e.target.value }))}
                                        />
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                            <button className="hl-btn-icon hl-btn-icon-success" onClick={handleGuardarNuevaFilaDesde} title="Guardar">
                                                <i className="bi bi-check-lg"></i>
                                            </button>
                                            <button className="hl-btn-icon hl-btn-icon-danger" onClick={handleCancelarNuevaFilaDesde} title="Cancelar">
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {registrosDesde0794.length === 0 && (
                    <div className="hl-alert hl-alert-warning" style={{ marginTop: '1rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No hay filas en DESDE 07/94 para control de REM IMP. SS.
                    </div>
                )}
            </div>
        );
    };

    const renderPaso2 = () => {
        const sinRegistrosDetectados = registros.length === 0 && registrosHasta0694.length === 0 && registrosDesde0794.length === 0;
        const puedeContinuarAPaso3 = analisis != null;

        return (
            <div>
                {/* Header con nombre y CUIL */}
                <div className="hl-info-card" style={{ marginBottom: '1.2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <strong style={{ color: '#888', fontSize: '0.8rem' }}>NOMBRE DETECTADO</strong>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{nombreCompleto || 'No detectado'}</div>
                        </div>
                        <div>
                            <strong style={{ color: '#888', fontSize: '0.8rem' }}>CUIL</strong>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{cuil || 'No detectado'}</div>
                        </div>
                    </div>
                </div>

                {borradorGuardadoMsg && (
                    <div className="hl-alert hl-alert-success" style={{ marginBottom: '1rem' }}>
                        <i className="bi bi-check-circle me-2"></i>{borradorGuardadoMsg}
                    </div>
                )}

                {/* ── Datos personales: fecha de nacimiento y sexo previsional ── */}
                <div className="hl-info-card" style={{ marginBottom: '1.2rem' }}>
                    <strong style={{ color: '#555', display: 'block', marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                        <i className="bi bi-person-badge me-2"></i>
                        Datos personales
                    </strong>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 190px', minWidth: 0 }}>
                            <label style={{ fontSize: '0.78rem', color: '#888', display: 'block', marginBottom: '0.2rem' }}>Fecha de nacimiento</label>
                            <input
                                type="date"
                                value={fechaNacimientoPdf}
                                onChange={(e) => {
                                    setFechaNacimientoPdf(e.target.value);
                                    setMoratoriaFechaNacimiento(e.target.value);
                                }}
                                style={{ padding: '0.42rem 0.65rem', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.92rem', width: '100%' }}
                            />
                            {primerMesComputableYm && (
                                <div style={{ fontSize: '0.72rem', color: '#1976d2', marginTop: '0.18rem' }}>
                                    Aportes antes del {primerMesComputableYm.split('-').reverse().join('/')} excluidos.
                                </div>
                            )}
                        </div>
                        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                            <label style={{ fontSize: '0.78rem', color: '#888', display: 'block', marginBottom: '0.2rem' }}>Sexo previsional</label>
                            <select
                                value={moratoriaSexo}
                                onChange={(e) => setMoratoriaSexo(e.target.value)}
                                style={{ padding: '0.42rem 0.65rem', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.92rem', width: '100%' }}
                            >
                                <option value="">Seleccionar...</option>
                                <option value="F">Mujer (60 años)</option>
                                <option value="M">Hombre (65 años)</option>
                            </select>
                        </div>
                    </div>
                    {(!fechaNacimientoPdf || !moratoriaSexo) && (
                        <div style={{ fontSize: '0.77rem', color: '#888', marginTop: '0.4rem' }}>
                            <i className="bi bi-info-circle me-1"></i>
                            Necesarios para excluir aportes previos a los 18 años y calcular las estrategias del paso 3.
                        </div>
                    )}
                </div>

                {sinRegistrosDetectados && (
                    <div className="hl-alert hl-alert-info" style={{ marginBottom: '1rem' }}>
                        <i className="bi bi-info-circle me-2"></i>
                        No se detectaron aportes en el PDF. Podés continuar al paso 3 para evaluar moratorias y monotributo con <strong>0 meses computables</strong>.
                    </div>
                )}

                {/* Tabs */}
                <div className="hl-tabs">
                    <button
                        className={`hl-tab ${tabActiva === 'resumen' ? 'active' : ''}`}
                        onClick={() => setTabActiva('resumen')}
                    >
                        <i className="bi bi-clipboard-data"></i>
                        Resumen
                        {correcciones.length > 0 && (
                            <span className="hl-tab-badge" style={{ background: '#e65100', color: '#fff' }}>
                                {correcciones.length}
                            </span>
                        )}
                    </button>
                    <button
                        className={`hl-tab ${tabActiva === 'hasta0694' ? 'active' : ''}`}
                        onClick={() => setTabActiva('hasta0694')}
                    >
                        <i className="bi bi-calendar-minus"></i>
                        Hasta 06/94
                        <span className="hl-tab-badge">{registrosHasta0694.length}</span>
                    </button>
                    <button
                        className={`hl-tab ${tabActiva === 'desde0794' ? 'active' : ''}`}
                        onClick={() => setTabActiva('desde0794')}
                    >
                        <i className="bi bi-calendar-check"></i>
                        Desde 07/94
                        <span className="hl-tab-badge" style={{ background: ajustesDesde0794.length > 0 ? '#d32f2f' : undefined, color: ajustesDesde0794.length > 0 ? '#fff' : undefined }}>
                            {registrosDesde0794.length}
                        </span>
                    </button>
                </div>

                {/* Contenido de la tab activa */}
                {tabActiva === 'resumen' && renderTabResumen()}
                {tabActiva === 'hasta0694' && renderTabHasta0694()}
                {tabActiva === 'desde0794' && renderTabDesde0794()}

                {/* Botones de navegación */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button className="hl-btn-outline" onClick={() => { setPasoActual(0); setArchivo(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                        <i className="bi bi-arrow-left me-2"></i>Volver
                    </button>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="hl-btn-primary"
                            disabled={!puedeContinuarAPaso3}
                            onClick={() => { setPasoActual(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        >
                            Continuar<i className="bi bi-arrow-right ms-2"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderPaso3 = () => {
        if (importacionResultado) {
            return (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div style={{ fontSize: '3rem', color: '#2e7d32', marginBottom: '1rem' }}>
                        <i className="bi bi-check-circle-fill"></i>
                    </div>
                    <h4 style={{ color: '#2e7d32', marginBottom: '1.5rem' }}>¡Importación exitosa!</h4>
                    <div className="hl-resumen-card" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <div className="hl-resumen-row">
                            <span>Meses importados:</span>
                            <strong style={{ color: '#2e7d32' }}>{importacionResultado.mesesImportados}</strong>
                        </div>
                        <div className="hl-resumen-row">
                            <span>Aportes reemplazados:</span>
                            <strong style={{ color: '#f57f17' }}>{importacionResultado.aportesReemplazados ?? importacionResultado.aportesAntes ?? 0}</strong>
                        </div>
                        <div className="hl-resumen-row">
                            <span>Total aportes del cliente:</span>
                            <strong>{importacionResultado.totalAportesCliente}</strong>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                        <button className="hl-btn-outline" onClick={handleReset}>
                            <i className="bi bi-arrow-repeat me-2"></i>Nueva importación
                        </button>
                        
                        <button 
                            className="hl-btn-primary" 
                            onClick={() => {
                                const cuilLimpio = clienteSeleccionado?.cuil.replace(/\D/g, ''); 
                                navigate(`/clientes/vista-detallada/${cuilLimpio}`);
                            }}
                        >
                            <i className="bi bi-person-lines-fill me-2"></i>Ir a vista del cliente
                        </button>
                    </div>
                </div>
            );
        }

        // Resumen consolidado para paso 3
        const {
            mesesHasta,
            mesesDesdeComputables,
            mesesRelDepSinSicam,
            mesesNuevosAportadosPorSicam,
            totalMesesDefinitivos,
        } = resumenComputablesPaso3;
        const sinMesesParaImportar = totalMesesDefinitivos === 0;
        const aportesAImportarPreview = (calculoDefinitivoConSicam.mesesComputablesDefinitivos || []).length;

        return (
            <div>
                {/* Resumen consolidado */}
                <div className="hl-info-card" style={{ marginBottom: '1.2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <strong style={{ color: '#888', fontSize: '0.8rem' }}>PERSONA EN PDF</strong>
                            <div style={{ fontWeight: 600 }}>{nombreCompleto} — {cuil}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#888', fontSize: '0.8rem' }}>MESES COMPUTABLES (definitivo)</strong>
                            <div style={{ fontWeight: 700, fontSize: '1.3rem', color: '#2e7d32' }}>{totalMesesDefinitivos}</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid #eee', fontSize: '0.85rem' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, color: '#555' }}>{mesesHasta}</div>
                            <div style={{ color: '#888' }}>Meses HASTA 06/94</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, color: '#555' }}>{mesesDesdeComputables}</div>
                            <div style={{ color: '#888' }}>Meses DESDE 07/94</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                             <div style={{ fontWeight: 700, color: '#2e7d32' }}>{mesesRelDepSinSicam}</div>
                             <div style={{ color: '#888' }}>Meses REL. DEP.</div>
                         </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, color: '#7b1fa2' }}>{mesesNuevosAportadosPorSicam}</div>
                            <div style={{ color: '#888' }}>Meses SICAM</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, color: '#2e7d32' }}>{totalMesesDefinitivos}</div>
                            <div style={{ color: '#888' }}>Total a importar</div>
                        </div>
                    </div>
                </div>

                {/* Alerta si hay correcciones pendientes */}
                {correcciones.length > 0 && (
                    <div className="hl-alert hl-alert-warning" style={{ marginBottom: '1.2rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Atención:</strong> Se detectaron {correcciones.length} discrepancia{correcciones.length > 1 ? 's' : ''} entre las secciones del PDF.
                        Revisá la pestaña "Resumen" en el paso anterior para más detalles.
                        {totalMesesNoComputables > 0 && (
                            <span> Hay <strong>{totalMesesNoComputables} meses no computables</strong>.</span>
                        )}
                    </div>
                )}

                {sinMesesParaImportar && (
                    <div className="hl-alert hl-alert-info" style={{ marginBottom: '1.2rem' }}>
                        <i className="bi bi-info-circle me-2"></i>
                        No hay meses computables para importar. Podés usar esta pantalla para analizar moratorias y monotributo, pero la confirmación queda deshabilitada.
                    </div>
                )}

                <div className="hl-moratoria-card" style={{ marginBottom: '1.2rem' }}>
                    <h6 style={{ marginBottom: '0.7rem', fontWeight: 700, color: '#555' }}>
                        <i className="bi bi-calculator me-2"></i>
                        Proyección informativa para completar 360 meses
                    </h6>

                    {/* Resumen de datos personales — desplegable para editar inline */}
                    <div
                        style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: paso3EditarDatos ? '0.6rem' : '0.8rem', padding: '0.5rem 0.8rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.87rem', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setPaso3EditarDatos(p => !p)}
                    >
                        <div>
                            <span style={{ color: '#888' }}>Fecha de nac.: </span>
                            <strong>{moratoriaFechaNacimiento ? moratoriaFechaNacimiento.split('-').reverse().join('/') : <span style={{ color: '#999' }}>—</span>}</strong>
                        </div>
                        <div>
                            <span style={{ color: '#888' }}>Sexo: </span>
                            <strong>{moratoriaSexo === 'F' ? 'Mujer (60 a.)' : moratoriaSexo === 'M' ? 'Hombre (65 a.)' : <span style={{ color: '#999' }}>—</span>}</strong>
                        </div>
                        <div style={{ marginLeft: 'auto', color: '#1976d2', fontSize: '0.8rem' }}>
                            <i className={`bi bi-chevron-${paso3EditarDatos ? 'up' : 'down'} me-1`}></i>
                            {paso3EditarDatos ? 'Cerrar' : 'Editar'}
                        </div>
                    </div>
                    {paso3EditarDatos && (
                        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '0.8rem', padding: '0.6rem 0.8rem', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                            <div style={{ flex: '1 1 175px', minWidth: 0 }}>
                                <label style={{ fontSize: '0.75rem', color: '#555', display: 'block', marginBottom: '0.15rem' }}>Fecha de nacimiento</label>
                                <input
                                    type="date"
                                    value={fechaNacimientoPdf}
                                    onChange={(e) => { setFechaNacimientoPdf(e.target.value); setMoratoriaFechaNacimiento(e.target.value); }}
                                    style={{ padding: '0.38rem 0.6rem', border: '1px solid #bbdefb', borderRadius: '6px',  width: '100%' }}
                                />
                            </div>
                            <div style={{ flex: '1 1 155px', minWidth: 0 }}>
                                <label style={{ fontSize: '0.75rem', color: '#555', display: 'block', marginBottom: '0.15rem' }}>Sexo previsional</label>
                                <select
                                    value={moratoriaSexo}
                                    onChange={(e) => setMoratoriaSexo(e.target.value)}
                                    style={{ padding: '0.38rem 0.6rem', border: '1px solid #bbdefb', borderRadius: '6px',  width: '100%' }}
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="F">Mujer (60 años)</option>
                                    <option value="M">Hombre (65 años)</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {moratoriaSexo === 'F' && (
                        <div className="hl-info-card" style={{ marginBottom: '0.8rem', marginTop: '0.8rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#555' }}>Bonificación por tareas de cuidado (hijos)</strong>
                                <button type="button" className="hl-btn-outline" onClick={agregarHijoMoratoria}>
                                    <i className="bi bi-plus-circle me-2"></i>Agregar hijo
                                </button>
                            </div>

                            {moratoriaHijos.length === 0 ? (
                                <div style={{ marginTop: '0.6rem', color: '#777', fontSize: '0.9rem' }}>
                                    No hay hijos cargados. Cada hijo suma 1 año; AUH suma 2 años; discapacidad suma 1 año; adoptado suma 1 año (acumulable).
                                </div>
                            ) : (
                                <div style={{ marginTop: '0.7rem', display: 'grid', gap: '0.5rem' }}>
                                    {moratoriaHijos.map((hijo, index) => (
                                        <div key={hijo.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: '0.6rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                                <strong style={{ color: '#555' }}>Hijo #{index + 1}</strong>
                                                <button
                                                    type="button"
                                                    className="hl-btn-danger"
                                                    onClick={() => quitarHijoMoratoria(hijo.id)}
                                                    style={{ padding: '0.25rem 0.55rem', fontSize: '0.8rem' }}
                                                >
                                                    Quitar
                                                </button>
                                            </div>
                                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(hijo.auh)}
                                                        onChange={(e) => toggleHijoMoratoria(hijo.id, 'auh', e.target.checked)}
                                                    />
                                                    Cobró AUH (+2 años)
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(hijo.discapacidad)}
                                                        onChange={(e) => toggleHijoMoratoria(hijo.id, 'discapacidad', e.target.checked)}
                                                    />
                                                    Discapacidad (+1 año)
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(hijo.adoptado)}
                                                        onChange={(e) => toggleHijoMoratoria(hijo.id, 'adoptado', e.target.checked)}
                                                    />
                                                    Adoptado (+1 año)
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!moratoriaFechaNacimiento || !moratoriaSexo ? (
                        <div className="hl-alert hl-alert-info" style={{ marginTop: '0.8rem', marginBottom: 0 }}>
                            Completá fecha de nacimiento y sexo para calcular Ley 24.476, monotributo futuro y Ley 27.705.
                        </div>
                    ) : analisisMoratorias?.valido === false ? (
                        <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.8rem', marginBottom: 0 }}>
                            No se pudo calcular el módulo informativo. Revisá los datos cargados.
                        </div>
                    ) : analisisMoratorias?.valido ? (
                        <div className="hl-moratoria-results">
                            {/* U1: toggle y header KPIs */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.3rem' }}>
                                <span style={{ fontSize: '0.78rem', color: '#888', fontWeight: 600 }}>Resumen de aportes</span>
                                {renderToggleAnios()}
                            </div>
                            <div className="hl-moratoria-kpis-grid">
                                <div className="hl-moratoria-kpi-card">
                                    <small>Edad actual</small>
                                    <strong>{analisisMoratorias.edadActual} años</strong>
                                </div>
                                <div className="hl-moratoria-kpi-card">
                                    <small>Cumple 18</small>
                                    <strong>{formatearFechaIso(analisisMoratorias.fechaCumple18)}</strong>
                                </div>
                                <div className="hl-moratoria-kpi-card">
                                    <small>Fecha jubilatoria</small>
                                    <strong>{formatearFechaIso(analisisMoratorias.fechaJubilacion)}</strong>
                                </div>
                                {/* M1: se oculta "Computables actuales" cuando hay exceso de edad para no desplazar "Faltantes a 360" */}
                                {!analisisMoratorias.bonificacionExcesoEdad?.aplica && (
                                    <div className="hl-moratoria-kpi-card">
                                        <small>Computables actuales</small>
                                        <strong>{formatearContador(analisisMoratorias.totalComputablesActual)}</strong>
                                    </div>
                                )}
                                <div className="hl-moratoria-kpi-card hl-moratoria-kpi-highlight">
                                    <small>Bonificación por hijos</small>
                                    <strong>{formatearContador(analisisMoratorias.bonificacionHijos?.mesesBonificados || 0)}</strong>
                                    {analisisMoratorias.bonificacionHijos?.aniosBonificados > 0 && (
                                        <span>({analisisMoratorias.bonificacionHijos.aniosBonificados} años)</span>
                                    )}
                                </div>
                                {analisisMoratorias.bonificacionExcesoEdad?.aplica && (
                                    <div className="hl-moratoria-kpi-card hl-moratoria-kpi-highlight" style={{ borderColor: '#7b1fa2' }}>
                                        <small>Bonificación exceso de edad</small>
                                        <strong style={{ color: '#7b1fa2' }}>{formatearContador(analisisMoratorias.bonificacionExcesoEdad.mesesBonificados)}</strong>
                                        <span style={{ fontSize: '0.72rem' }}>{analisisMoratorias.bonificacionExcesoEdad.mesesExcedidos} m. sobre edad jub.</span>
                                    </div>
                                )}
                                <div className="hl-moratoria-kpi-card hl-moratoria-kpi-highlight">
                                    <small>Computables + bonificaciones</small>
                                    <strong>{formatearContador(analisisMoratorias.totalComputablesConBonificacion)}</strong>
                                </div>
                                <div className="hl-moratoria-kpi-card hl-moratoria-kpi-danger">
                                    <small>Faltantes a 360</small>
                                    <strong>{formatearContador(analisisMoratorias.faltantesA360)}</strong>
                                </div>
                            </div>

                            <div className="hl-moratoria-flow-card" style={{ marginTop: '0.6rem' }}>
                                <div className="hl-moratoria-flow-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <span>
                                        <i className="bi bi-grid-3x3-gap-fill me-2"></i>
                                        Estrategias disponibles. Hacé click en cada tarjeta para sumar meses
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setMostrarEditarVentanas((p) => !p)}
                                        style={{
                                            background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer',
                                            fontSize: '0.8rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                        }}
                                    >
                                        <i className={`bi bi-sliders`}></i>
                                        {mostrarEditarVentanas ? 'Cerrar edición de ventanas' : 'Editar ventanas'}
                                    </button>
                                </div>

                                {mostrarEditarVentanas && (
                                    <div style={{ margin: '0.5rem 0 0.8rem', padding: '0.7rem 0.9rem', border: '1px dashed #bbb', borderRadius: '8px', background: '#fafafa' }}>
                                        <p style={{ fontSize: '0.78rem', color: '#777', marginBottom: '0.6rem' }}>
                                            Ajustá manualmente el rango de cada estrategia (formato MM/AAAA). Vacío = ventana calculada.
                                            Dentro de la ventana solo se cuentan los meses libres (no aportados).
                                        </p>
                                        {[
                                            { key: 'ley24476', label: 'Ley 24.476', win: analisisMoratorias.ley24476 },
                                            { key: 'monotributoFuturo', label: 'Aporte futuro', win: analisisMoratorias.monotributoFuturo },
                                            { key: 'ley27705', label: 'Ley 27.705', win: analisisMoratorias.ley27705 },
                                        ].map(({ key, label, win }) => (
                                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                                                <span style={{ minWidth: '110px', fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>{label}</span>
                                                <input
                                                    type="text"
                                                    value={ventanasEstrategiasOverride[key].inicioYm}
                                                    onChange={(e) => actualizarVentanaOverride(key, 'inicioYm', e.target.value)}
                                                    placeholder={`Desde ${formatearYearMonth(win.inicioYm)}`}
                                                    style={{ width: '130px', padding: '0.35rem 0.55rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.82rem' }}
                                                />
                                                <input
                                                    type="text"
                                                    value={ventanasEstrategiasOverride[key].finYm}
                                                    onChange={(e) => actualizarVentanaOverride(key, 'finYm', e.target.value)}
                                                    placeholder={`Hasta ${formatearYearMonth(win.finYm)}`}
                                                    style={{ width: '130px', padding: '0.35rem 0.55rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.82rem' }}
                                                />
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={restaurarVentanasPorDefecto}
                                            style={{ marginTop: '0.3rem', background: 'none', border: '1px solid #ccc', borderRadius: '6px', padding: '0.35rem 0.7rem', color: '#777', cursor: 'pointer', fontSize: '0.8rem' }}
                                        >
                                            Restaurar ventanas por defecto
                                        </button>
                                    </div>
                                )}

                                <div className="hl-estrategias-cards-grid">
                                    {[
                                        {
                                            key: 'ley24476',
                                            icon: 'bi-journal-bookmark-fill',
                                            titulo: 'Ley 24.476',
                                            subtitulo: `${formatearYearMonth(analisisMoratorias.ley24476.inicioYm)} a ${formatearYearMonth(analisisMoratorias.ley24476.finYm)}`,
                                            maximo: analisisMoratorias.estrategias.maximos.ley24476,
                                            habilitada: analisisMoratorias.estrategias.maximos.ley24476 > 0,
                                            descripcion: 'Compra de aportes históricos en ventana 24.476.',
                                        },
                                        {
                                            key: 'monotributoFuturo',
                                            icon: 'bi-calendar2-check-fill',
                                            titulo: 'Aporte futuro',
                                            subtitulo: `${formatearYearMonth(analisisMoratorias.monotributoFuturo.inicioYm)} a ${formatearYearMonth(analisisMoratorias.monotributoFuturo.finYm)}`,
                                            maximo: analisisMoratorias.estrategias.maximos.monotributoFuturo,
                                            habilitada: analisisMoratorias.estrategias.maximos.monotributoFuturo > 0,
                                            descripcion: 'Meses futuros hasta edad jubilatoria.',
                                        },
                                        {
                                            key: 'ley27705',
                                            icon: 'bi-shield-check',
                                            titulo: 'Ley 27.705',
                                            subtitulo: `${formatearYearMonth(analisisMoratorias.ley27705.inicioYm)} a ${formatearYearMonth(analisisMoratorias.ley27705.finYm)}`,
                                            maximo: analisisMoratorias.estrategias.maximos.ley27705,
                                            habilitada: analisisMoratorias.fallbackLey27705.elegible && analisisMoratorias.estrategias.maximos.ley27705 > 0,
                                            descripcion: analisisMoratorias.fallbackLey27705.elegible
                                                ? 'Última opción para completar faltantes.'
                                                : 'No habilitada por condición etaria.',
                                        },
                                    ].map((estrategia) => {
                                        const mesesSeleccionados = moratoriaEstrategiasSeleccion[estrategia.key] || 0;
                                        const activa = mesesSeleccionados > 0;
                                        const totalOtrosSeleccionados = Object.entries(moratoriaEstrategiasSeleccion)
                                            .filter(([key]) => key !== estrategia.key)
                                            .reduce((acc, [, value]) => acc + Math.max(0, Number(value || 0)), 0);
                                        const faltantesBase = Math.max(0, Number(analisisMoratorias?.faltantesA360 || 0));
                                        const restanteParaEstaTarjeta = Math.max(0, faltantesBase - totalOtrosSeleccionados);
                                        const mesesEfectivos = Math.min(estrategia.maximo, restanteParaEstaTarjeta);
                                        const estadoClase = !estrategia.habilitada
                                            ? 'is-disabled'
                                            : activa
                                                ? 'is-active'
                                                : 'is-idle';

                                        return (
                                            <button
                                                key={estrategia.key}
                                                type="button"
                                                className={`hl-estrategia-card ${estadoClase}`}
                                                onClick={() => toggleEstrategiaMoratoria(estrategia.key, estrategia.maximo, estrategia.habilitada)}
                                                disabled={!estrategia.habilitada}
                                            >
                                                <div className="hl-estrategia-card-head">
                                                    <span className="hl-estrategia-icon"><i className={`bi ${estrategia.icon}`}></i></span>
                                                    <div>
                                                        <strong>{estrategia.titulo}</strong>
                                                        <small>{estrategia.subtitulo}</small>
                                                    </div>
                                                </div>
                                                <p>{estrategia.descripcion}</p>
                                                <div className="hl-estrategia-card-footer">
                                                    <span>Máx: {formatearContador(estrategia.maximo)}</span>
                                                    <span>
                                                        {activa
                                                            ? <>Selec.: <strong>{formatearContador(mesesSeleccionados)}</strong></>
                                                            : <>+<strong>{formatearContador(mesesEfectivos)}</strong></>}
                                                    </span>
                                                </div>
                                                {!activa && estrategia.habilitada && mesesEfectivos < estrategia.maximo && (
                                                    <small style={{ color: '#7b1fa2', fontWeight: 600 }}>
                                                        Se aplican {formatearContador(mesesEfectivos)} de {formatearContador(estrategia.maximo)} por faltantes.
                                                    </small>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="hl-moratoria-flow-metrics" style={{ marginTop: '0.75rem' }}>
                                    <div>
                                        <small>Total estrategias</small>
                                        <strong>{formatearContador(analisisMoratorias.estrategias.totalSeleccionado)}</strong>
                                    </div>
                                    <div>
                                        <small>Total proyectado</small>
                                        <strong>{formatearContador(analisisMoratorias.estrategias.totalProyectadoConEstrategias)}</strong>
                                    </div>
                                    <div>
                                        <small>Faltantes finales</small>
                                        <strong>{formatearContador(analisisMoratorias.estrategias.faltantesLuegoSeleccion)}</strong>
                                    </div>
                                    <div>
                                        <small>Objetivo 360</small>
                                        <strong>{analisisMoratorias.estrategias.faltantesLuegoSeleccion === 0 ? '✓ Alcanzado' : 'Pendiente'}</strong>
                                    </div>
                                </div>

                                {/* Aporte futuro + exceso de edad. Seleccionable (solo estético): sin
                                    seleccionar muestra solo el borde verde; al seleccionar se sombrea. */}
                                {analisisMoratorias.aporteFuturoMonotributo?.aplica && (
                                    <button
                                        type="button"
                                        className="hl-moratoria-futuro-card"
                                        onClick={() => setAporteFuturoSeleccionado((p) => !p)}
                                        aria-pressed={aporteFuturoSeleccionado}
                                        style={{
                                            marginTop: '0.85rem',
                                            width: '100%',
                                            display: 'block',
                                            textAlign: 'left',
                                            padding: '0.75rem 1rem',
                                            borderRadius: '10px',
                                            border: '1px solid #00897b',
                                            background: aporteFuturoSeleccionado ? 'rgba(0, 137, 123, 0.10)' : 'transparent',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            transition: 'background 0.15s ease',
                                        }}
                                    >
                                        <strong style={{ color: '#00695c', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem', marginBottom: '0.3rem' }}>
                                            <i className="bi bi-hourglass-split"></i>
                                            Aporte futuro + exceso de edad
                                        </strong>
                                        <div style={{ fontSize: '0.85rem', color: '#00695c', lineHeight: 1.5 }}>
                                            Faltan <strong>{formatearContador(analisisMoratorias.aporteFuturoMonotributo.mesesAportesFaltantes)}</strong> de aportes,
                                            se obtendrían en <strong>{formatearContador(analisisMoratorias.aporteFuturoMonotributo.mesesTiempoNecesario)}</strong> de tiempo.
                                            Fecha estimada: <strong>{formatearYearMonth(analisisMoratorias.aporteFuturoMonotributo.fechaEstimadaYm)}</strong>
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* B2: costos de las estrategias (opcional). Si no se abre, no se imprime en el PDF. */}
                <div style={{ marginTop: '1.2rem', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                    <button
                        type="button"
                        onClick={() => setMostrarCostos((p) => !p)}
                        style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.7rem 1rem', background: '#f7f7f7', border: 'none', cursor: 'pointer',
                            fontWeight: 600, color: '#555', fontSize: '0.95rem',
                        }}
                    >
                        <span><i className="bi bi-cash-coin me-2"></i>Costos aproximados de las estrategias (opcional)</span>
                        <i className={`bi bi-chevron-${mostrarCostos ? 'up' : 'down'}`}></i>
                    </button>
                    {mostrarCostos && (
                        <div style={{ padding: '0.9rem 1rem' }}>
                            <p style={{ fontSize: '0.8rem', color: '#777', marginBottom: '0.8rem' }}>
                                Cargá los montos actuales. Se estiman al día de impresión y solo se muestran en el PDF si este panel está abierto.
                            </p>

                            <div style={{ display: 'grid', gap: '0.7rem' }}>
                                {/* Ley 24.476 — monto total manual */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                    <span style={{ minWidth: '150px', fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Ley 24.476 - total</span>
                                    <input
                                        type="text" inputMode="numeric"
                                        value={costos.ley24476Total}
                                        onChange={(e) => setCostos((c) => ({ ...c, ley24476Total: e.target.value }))}
                                        placeholder="Monto total aprox."
                                        style={{ width: '160px', padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    <small style={{ color: '#999' }}>El monto real se confirma el día del pago.</small>
                                </div>

                                {/* Aporte futuro / Monotributo — monto mensual */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                    <span style={{ minWidth: '150px', fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Aporte futuro - mensual</span>
                                    <input
                                        type="text" inputMode="numeric"
                                        value={costos.monotributoMensual}
                                        onChange={(e) => setCostos((c) => ({ ...c, monotributoMensual: e.target.value }))}
                                        placeholder="Monto mensual"
                                        style={{ width: '160px', padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    <small style={{ color: '#999' }}>Monto mensual si se aporta como monotributista. Se paga todos los meses (no se adelanta).</small>
                                </div>

                                {/* Ley 27.705 — monto mensual × meses seleccionados */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                    <span style={{ minWidth: '150px', fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>Ley 27.705 - mensual</span>
                                    <input
                                        type="text" inputMode="numeric"
                                        value={costos.ley27705Mensual}
                                        onChange={(e) => setCostos((c) => ({ ...c, ley27705Mensual: e.target.value }))}
                                        placeholder="Monto mensual actual"
                                        style={{ width: '160px', padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                    {Number.isFinite(parsearMonto(costos.ley27705Mensual)) && (moratoriaEstrategiasSeleccion.ley27705 || 0) > 0 && (
                                        <small style={{ color: '#1976d2', fontWeight: 600 }}>
                                            × {moratoriaEstrategiasSeleccion.ley27705} meses ≈ {formatearMonto(parsearMonto(costos.ley27705Mensual) * moratoriaEstrategiasSeleccion.ley27705)}
                                        </small>
                                    )}
                                    <small style={{ color: '#999', width: '100%' }}>Precio del mes actual; varía con la inflación.</small>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="hl-rti-check-card" style={{ marginTop: '1.2rem', marginBottom: '1.2rem' }}>
                    <div className="hl-rti-check-header">
                        <div>
                            <h5 style={{ marginBottom: '0.2rem', color: '#555' }}>
                                <i className="bi bi-hammer me-2"></i>
                                Regímenes diferenciales (prorrateo)
                            </h5>
                            <small style={{ color: '#6c757d' }}>
                                Marcá los períodos en el Paso 2 (columna Régimen / Caja). Acá se ve el prorrateo resultante.
                            </small>
                        </div>
                    </div>

                    {(regimenesDisponibles || []).length === 0 ? (
                        <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                            <i className="bi bi-info-circle me-2"></i>
                            No hay regímenes diferenciales configurados. Un administrador puede cargarlos en Datos Maestros → Datos Previsionales.
                        </div>
                    ) : (
                        <>
                            {sinFilasCargadas && (<>
                            <small className="hl-no-print" style={{ color: '#94a3b8', display: 'block', marginTop: '0.5rem' }}>Sin filas cargadas: asignación por rango (fallback).</small>
                            <div className="hl-no-print" style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.4rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Régimen</label>
                                    <select
                                        value={regimenAsignInput.regimenId}
                                        onChange={(e) => setRegimenAsignInput((s) => ({ ...s, regimenId: e.target.value }))}
                                        style={{ padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    >
                                        <option value="">Elegir…</option>
                                        {regimenesDisponibles.map((r) => (
                                            <option key={r.id} value={r.id}>
                                                {r.nombre} ({r.edadMinima}/{Math.round((r.serviciosMinimosMeses || 0) / 12)})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Desde</label>
                                    <input
                                        type="text"
                                        value={regimenAsignInput.desde}
                                        onChange={(e) => setRegimenAsignInput((s) => ({ ...s, desde: e.target.value }))}
                                        placeholder="MM/AAAA"
                                        style={{ width: '110px', padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Hasta</label>
                                    <input
                                        type="text"
                                        value={regimenAsignInput.hasta}
                                        onChange={(e) => setRegimenAsignInput((s) => ({ ...s, hasta: e.target.value }))}
                                        placeholder="MM/AAAA"
                                        style={{ width: '110px', padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                                    />
                                </div>
                                <button type="button" className="hl-btn-outline" onClick={agregarAsignacionRegimen}>
                                    <i className="bi bi-plus-circle me-1"></i>Asignar
                                </button>
                            </div>

                            {asignacionesRegimen.length > 0 && (
                                <div style={{ marginTop: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                    {asignacionesRegimen.map((a) => {
                                        const reg = regimenesDisponibles.find((r) => String(r.id) === String(a.regimenId));
                                        return (
                                            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.35rem 0.6rem' }}>
                                                <i className="bi bi-hammer text-secondary"></i>
                                                <strong>{reg ? reg.nombre : 'Régimen eliminado'}</strong>
                                                <span style={{ color: '#64748b' }}>
                                                    {formatearYearMonth(a.desde)} a {formatearYearMonth(a.hasta)}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="hl-no-print"
                                                    onClick={() => eliminarAsignacionRegimen(a.id)}
                                                    style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer' }}
                                                    title="Quitar asignación"
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            </>)}

                            {!moratoriaSexo && (
                                <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                                    <i className="bi bi-info-circle me-2"></i>
                                    Completá sexo y fecha de nacimiento (arriba) para calcular el prorrateo.
                                </div>
                            )}

                            {prorrateoRegimenes?.aplica && (
                                <div className="card shadow-lg border-2 rounded-4 p-4 mt-3" style={{ borderColor: prorrateoRegimenes.cumpleServicios ? '#2e7d32' : '#6c757d' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="fw-bold mb-0" style={{ color: '#6c757d' }}>Requisito prorrateado</h5>
                                        <span className={`badge rounded-pill px-3 py-2 ${prorrateoRegimenes.cumpleServicios ? 'bg-success' : 'bg-secondary'}`}>
                                            {prorrateoRegimenes.cumpleServicios ? 'CUMPLE SERVICIOS' : 'NO CUMPLE SERVICIOS'}
                                        </span>
                                    </div>

                                    <div className="small" style={{ color: '#374151' }}>
                                        <div className="mb-1">
                                            <strong>Edad requerida:</strong> {prorrateoRegimenes.edadRequeridaTexto}
                                            {fechaCumpleEdadRequerida && (
                                                <small style={{ color: '#7b1fa2', fontWeight: 600 }}> · la cumpliría el {fechaCumpleEdadRequerida}</small>
                                            )}
                                        </div>
                                        <div className="mb-1">
                                            <strong>Servicios requeridos:</strong> {formatearDuracionMeses(prorrateoRegimenes.serviciosRequeridosMeses)}
                                        </div>
                                        <div className="mb-2">
                                            <strong>Desglose:</strong>
                                            <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                                                {prorrateoRegimenes.componentes.map((c, i) => (
                                                    <li key={i}>
                                                        {c.esGeneral ? 'Régimen general' : c.nombre} ({c.edadMinima}/{Math.round(c.serviciosMinimosMeses / 12)}):{' '}
                                                        {formatearDuracionMeses(c.meses)}
                                                        {c.esBase && <small style={{ color: '#888' }}> (base)</small>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div style={{ color: '#6c757d' }}>
                                            <i className="bi bi-info-circle me-1"></i>
                                            Si ya alcanzó la edad, el exceso de edad corre desde esta edad requerida.
                                        </div>
                                    </div>

                                    {prorrateoRegimenes.regimenesConMeses
                                        .filter((r) => r.alertaPersonalizada)
                                        .map((r, i) => (
                                            <div key={i} className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                                                <i className="bi bi-bell-fill me-2"></i>
                                                <strong>{r.nombre}:</strong> {r.alertaPersonalizada}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="hl-rti-check-card" style={{ marginTop: '1.2rem', marginBottom: '1.2rem' }}>
                    <div className="hl-rti-check-header">
                        <div>
                            <h5 style={{ marginBottom: '0.2rem', color: '#555' }}>
                                <i className="bi bi-bank me-2"></i>
                                Cajas provinciales (caja otorgante)
                            </h5>
                            <small style={{ color: '#6c757d' }}>
                                Marcá los períodos en el Paso 2 (columna Régimen / Caja). Acá se ve la caja otorgante resultante.
                            </small>
                        </div>
                    </div>

                    {sinFilasCargadas && (<>
                    <small className="hl-no-print" style={{ color: '#94a3b8', display: 'block', marginTop: '0.5rem' }}>Sin filas cargadas: asignación por rango (fallback).</small>
                    <div className="hl-no-print" style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.4rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Caja</label>
                            <select
                                value={cajaAsignInput.cajaId}
                                onChange={(e) => setCajaAsignInput((s) => ({ ...s, cajaId: e.target.value }))}
                                style={{ padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                            >
                                <option value="">Elegir…</option>
                                {CAJAS_PROVINCIALES.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre}{c.esCordoba ? ' (provincial estudio)' : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Desde</label>
                            <input
                                type="text"
                                value={cajaAsignInput.desde}
                                onChange={(e) => setCajaAsignInput((s) => ({ ...s, desde: e.target.value }))}
                                placeholder="MM/AAAA"
                                style={{ width: '110px', padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555' }}>Hasta</label>
                            <input
                                type="text"
                                value={cajaAsignInput.hasta}
                                onChange={(e) => setCajaAsignInput((s) => ({ ...s, hasta: e.target.value }))}
                                placeholder="MM/AAAA"
                                style={{ width: '110px', padding: '0.42rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
                            />
                        </div>
                        <button type="button" className="hl-btn-outline" onClick={agregarAsignacionCaja}>
                            <i className="bi bi-plus-circle me-1"></i>Asignar
                        </button>
                    </div>

                    {asignacionesCaja.length > 0 && (
                        <div style={{ marginTop: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            {asignacionesCaja.map((a) => {
                                const caja = CAJAS_PROVINCIALES.find((c) => c.id === a.cajaId);
                                return (
                                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.35rem 0.6rem' }}>
                                        <i className="bi bi-bank text-secondary"></i>
                                        <strong>{caja ? caja.nombre : 'Caja desconocida'}</strong>
                                        <span style={{ color: '#64748b' }}>{formatearYearMonth(a.desde)} a {formatearYearMonth(a.hasta)}</span>
                                        <button
                                            type="button"
                                            className="hl-no-print"
                                            onClick={() => eliminarAsignacionCaja(a.id)}
                                            style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer' }}
                                            title="Quitar asignación"
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    </>)}

                    {sugerenciasCordoba.length > 0 && (
                        <div className="hl-alert hl-alert-warning hl-no-print" style={{ marginTop: '0.7rem' }}>
                            <i className="bi bi-lightbulb me-2"></i>
                            <strong>Empleadores aprendidos como Córdoba en este historial:</strong>{' '}
                            {sugerenciasCordoba.map((e) => `${e.razonSocial} (×${e.vecesConfirmado})`).join(', ')}.
                            <div style={{ marginTop: '0.5rem' }}>
                                <button type="button" className="btn btn-sm btn-warning" onClick={asignarSugeridosCordoba}>
                                    <i className="bi bi-check2-circle me-1"></i>
                                    Asignar estos meses a Córdoba
                                </button>
                            </div>
                        </div>
                    )}

                    {(Object.values(marcasFila).some((m) => m.cajaId === 'CORDOBA') || asignacionesCaja.some((a) => a.cajaId === 'CORDOBA')) && (
                        <div className="hl-no-print" style={{ marginTop: '0.6rem' }}>
                            <button type="button" className="hl-btn-outline" onClick={aprenderEmpleadoresCordoba}>
                                <i className="bi bi-mortarboard me-1"></i>
                                Aprender empleadores de Córdoba de este historial
                            </button>
                            {aprendizajeMsg && (
                                <small style={{ marginLeft: '0.6rem', color: '#2e7d32' }}>{aprendizajeMsg}</small>
                            )}
                        </div>
                    )}

                    {cajaOtorgante?.aplica && (() => {
                        const tipo = cajaOtorgante.tipo;
                        const esCordoba = tipo === 'CORDOBA_PROVINCIAL';
                        const esOtra = tipo === 'OTRA_PROVINCIAL';
                        const borde = esCordoba ? '#7b1fa2' : (esOtra ? '#ed6c02' : '#2e7d32');
                        return (
                            <div className="card shadow-lg border-2 rounded-4 p-4 mt-3" style={{ borderColor: borde }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0" style={{ color: '#6c757d' }}>Caja otorgante</h5>
                                    <span className="badge rounded-pill px-3 py-2" style={{ background: borde, color: '#fff' }}>
                                        {cajaOtorgante.otorgante}
                                    </span>
                                </div>

                                {esCordoba && (() => {
                                    const cordoba = cajaOtorgante.provinciales.find((c) => c.esCordoba);
                                    const faltanParaFlip = cordoba ? Math.max(0, cordoba.meses - cajaOtorgante.mesesGenerales + 1) : 0;
                                    return (
                                        <div className="hl-alert hl-alert-warning" style={{ marginBottom: '0.6rem' }}>
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            <strong>Jubilación provincial (Córdoba).</strong> La otorgante es Córdoba: es una jubilación provincial (la que inicia el estudio). No se computan hijos ni la compra de aportes.
                                            {faltanParaFlip > 0 && (
                                                <div style={{ marginTop: '0.35rem' }}>
                                                    Comprando <strong>{faltanParaFlip}</strong> {faltanParaFlip === 1 ? 'mes' : 'meses'} ANSES en períodos no aportados en ninguna caja, la otorgante pasaría a ANSES.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {esOtra && (
                                    <div className="hl-alert hl-alert-warning" style={{ marginBottom: '0.6rem' }}>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        <strong>Otorgante provincial de otra provincia ({cajaOtorgante.otorgante}).</strong> Sería una jubilación provincial que el estudio no inicia. Verificar con el operador.
                                    </div>
                                )}
                                {!esCordoba && !esOtra && (
                                    <div className="hl-alert hl-alert-success" style={{ marginBottom: '0.6rem' }}>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Otorgante ANSES: jubilación nacional. Las cajas provinciales se reconocen por reconocimiento de servicios.
                                    </div>
                                )}

                                <div className="small" style={{ color: '#374151' }}>
                                    <div className="mb-1"><strong>ANSES:</strong> {formatearDuracionMeses(cajaOtorgante.mesesGenerales)}</div>
                                    {cajaOtorgante.provinciales.map((c) => (
                                        <div key={c.id} className="mb-1">
                                            <strong>{c.nombre}:</strong> {formatearDuracionMeses(c.meses)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>

                <div className="hl-rti-check-card" style={{ marginTop: '1.2rem', marginBottom: '1.2rem' }}>
                    <div className="hl-rti-check-header">
                        <div>
                            <h5 style={{ marginBottom: '0.2rem', color: '#555' }}>
                                <i className="bi bi-clipboard2-pulse me-2"></i>
                                Chequeo de regularidad RTI, ART y minusvalía
                            </h5>
                            <small style={{ color: '#6c757d' }}>
                                Se calcula automáticamente. Este agregado es informativo.
                            </small>
                        </div>
                    </div>

                    <div className="hl-rti-controls hl-no-print">
                        <button
                            type="button"
                            className="hl-btn-outline"
                            onClick={ejecutarChequeoRegularidadRti}
                            disabled={chequeoRtiCargando}
                        >
                            {chequeoRtiCargando
                                ? 'Calculando...'
                                : (chequeoRtiResultado?.regularidad ? 'Recalcular ahora' : 'Calcular regularidad')}
                        </button>
                        {chequeoRtiResultado && (
                            <button
                                type="button"
                                className="hl-btn-outline"
                                onClick={handleImprimirRegularidades}
                                disabled={chequeoRtiCargando}
                            >
                                <i className="bi bi-printer me-2"></i>
                                Imprimir evaluación de regularidades
                            </button>
                        )}
                    </div>

                    {chequeoRtiError && (
                        <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {chequeoRtiError}
                        </div>
                    )}

                    {chequeoRtiResultado?.regularidad && (
                        <div style={{ marginTop: '0.7rem' }}>
                            <RegularidadAporte regularidad={chequeoRtiResultado.regularidad} />
                        </div>
                    )}


                    {chequeoRtiResultado?.regularidad && (
                        <>
                            {(chequeoRtiResultado.regularidad.periodosSicam || []).length > 0 && (
                                <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Revisar Declaracion Jurada (SICAM):</strong>{' '}
                                    {chequeoRtiResultado.regularidad.advertenciaSicam ||
                                        'Se detectaron periodos SICAM en esta evaluacion RTI.'}
                                    <div style={{ marginTop: '0.35rem' }}>
                                        <strong>Rangos detectados:</strong>{' '}
                                        {formatearPeriodosComoRangos(chequeoRtiResultado.regularidad.periodosSicam || [])}
                                    </div>
                                </div>
                            )}

                            {(chequeoRtiResultado.regularidad.periodosMonotributoSocial || []).length > 0 && (
                                <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.6rem' }}>
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Revisar Declaracion Jurada (Monotributo social):</strong>{' '}
                                    Estos periodos pueden impactar la evaluacion RTI de la misma forma que SICAM.
                                    <div style={{ marginTop: '0.35rem' }}>
                                        <strong>Rangos detectados:</strong>{' '}
                                        {formatearPeriodosComoRangos(chequeoRtiResultado.regularidad.periodosMonotributoSocial || [])}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {chequeoRtiResultado?.art && (() => {
                        const art = chequeoRtiResultado.art;
                        const mesesDesde = Math.max(0, Number(art.mesesDesdeUltimaRelacion || 0));
                        const enPlazo = Boolean(art.puedeIniciarTramite);

                        let borde = '#000000';
                        let badgeClass = 'bg-danger';
                        let badgeText = enPlazo ? 'EN PLAZO' : 'PLAZO VENCIDO';
                        let estadoTexto = enPlazo ? 'APTO PARA INICIO' : 'NO APTO PARA INICIO';

                        if (enPlazo) {
                            if (mesesDesde <= 11) {
                                borde = '#2e7d32';
                                badgeClass = 'bg-success';
                            } else if (mesesDesde <= 17) {
                                borde = '#ed6c02';
                                badgeClass = 'bg-warning text-dark';
                                badgeText = 'ATENCION';
                            } else {
                                borde = '#d32f2f';
                                badgeClass = 'bg-danger';
                                badgeText = 'CRITICO';
                            }
                        }

                        return (
                            <div className="card shadow-lg border-2 rounded-4 p-4 mt-3" style={{ borderColor: borde }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0" style={{ color: '#6c757d' }}>
                                        Deteccion de tramite ART
                                    </h5>
                                    <span className={`badge rounded-pill px-3 py-2 ${badgeClass}`}>{badgeText}</span>
                                </div>

                                <div className="mb-2">
                                    <strong style={{ color: borde }}>{estadoTexto}</strong>
                                </div>

                                <p className="mb-2" style={{ color: '#444' }}>{art.mensaje}</p>

                                {art.periodoUltimaRelacionDesde && art.periodoUltimaRelacionHasta && (
                                    <div className="small" style={{ color: '#374151' }}>
                                        <div>
                                            <strong>Tiempo desde fin de la última relación:</strong> {formatearDuracionMeses(mesesDesde)}
                                        </div>
                                        <div className="mb-1">
                                            <strong>Periodo:</strong>{' '}
                                            {formatearYearMonth(art.periodoUltimaRelacionDesde)} a {formatearYearMonth(art.periodoUltimaRelacionHasta)}
                                        </div>
                                        <div className="mb-1">
                                            <strong>Empleador:</strong> {art.ultimoEmpleador || 'No informado'}
                                            {' · '}
                                            <strong>CUIT:</strong> {art.ultimoCuitEmpleador || 'No informado'}
                                        </div>
                                        <div className="mb-1">
                                            <strong>Antigüedad en ese trabajo:</strong> {formatearDuracionMeses(art.antiguedadMeses || 0)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {chequeoRtiResultado?.minusvalia && (
                        <div className="mt-3">
                            <RegularidadMinusvalia regularidad={chequeoRtiResultado.minusvalia} cargando={false} />
                        </div>
                    )}

                </div>

                <h5 style={{ marginBottom: '1rem', color: '#555' }}>
                    <i className="bi bi-person-check me-2"></i>
                    Seleccionar cliente para importar
                </h5>

                {clienteSeleccionado && (
                    <div className="hl-alert hl-alert-warning" style={{ marginBottom: '0.9rem' }}>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        <strong>Importacion por reemplazo total:</strong>{' '}
                        Aportes actuales del cliente: <strong>{aportesActualesCliente ?? '-'}</strong>{' '}
                        | Aportes a importar: <strong>{aportesAImportarPreview}</strong>.
                        Al confirmar, se eliminan los anteriores y se guarda solo el nuevo lote.
                    </div>
                )}

                {/* Búsqueda de cliente */}
                {!clienteSeleccionado ? (
                    <div className="hl-cliente-search" style={{ marginBottom: '1.2rem' }}>
                        <div style={{ marginBottom: '0.4rem' }}>
                            <label style={{ fontWeight: 600, fontSize: '0.9rem', display: 'block' }}>
                                Buscar cliente por nombre, apellido o CUIL:
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                                type="text"
                                value={busquedaCliente}
                                onChange={(e) => handleBuscarCliente(e.target.value)}
                                placeholder="Ej: Farias o 27-17398319-6"
                                style={{ flex: 1, padding: '0.6rem 1rem', border: '1px solid #ddd' }}
                            />
                            <button
                                type="button"
                                className="hl-btn-outline"
                                style={{ padding: '0 1rem', fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                                onClick={() => setShowCrearClienteModal(true)}
                            >
                                <i className="bi bi-person-plus me-1"></i> Crear nuevo
                            </button>
                        </div>
                        {resultadosCliente.length > 0 && (
                            <div className="hl-cliente-dropdown">
                                {resultadosCliente.map((c) => (
                                    <div
                                        key={c.cuil}
                                        className="hl-cliente-option"
                                        onClick={() => handleSeleccionarCliente(c)}
                                    >
                                        <strong>{apellidoNombre(c.apellido, c.nombre)}</strong>
                                        <span style={{ color: '#888', marginLeft: '0.5rem' }}>CUIL: {c.cuil}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ marginBottom: '1.2rem' }}>
                        <label style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.3rem', display: 'block' }}>
                            Cliente seleccionado:
                        </label>
                        <div className="hl-cliente-selected">
                            <div>
                                <strong>{apellidoNombre(clienteSeleccionado.apellido, clienteSeleccionado.nombre)}</strong>
                                <span style={{ color: '#888', marginLeft: '0.5rem' }}>CUIL: {clienteSeleccionado.cuil}</span>
                            </div>
                            <button
                                className="hl-btn-outline"
                                style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem' }}
                                onClick={() => { clienteFijadoRef.current = null; setClienteSeleccionado(null); }}
                            >
                                Cambiar
                            </button>
                        </div>
                        {nombreCompleto && clienteSeleccionado.apellido && (
                            (() => {
                                const pdfNorm = (nombreCompleto || '').toLowerCase().trim();
                                const cliNorm = `${clienteSeleccionado.apellido} ${clienteSeleccionado.nombre}`.toLowerCase().trim();
                                const coincide = pdfNorm === cliNorm ||
                                    pdfNorm.includes(clienteSeleccionado.apellido.toLowerCase()) ||
                                    cliNorm.includes(pdfNorm.split(' ')[0]);
                                return coincide ? (
                                    <div className="hl-alert hl-alert-success" style={{ marginTop: '0.5rem' }}>
                                        <i className="bi bi-check-circle me-2"></i>
                                        El nombre del PDF coincide con el cliente seleccionado.
                                    </div>
                                ) : (
                                    <div className="hl-alert hl-alert-warning" style={{ marginTop: '0.5rem' }}>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        Atención: el nombre del PDF (<strong>{nombreCompleto}</strong>) no coincide exactamente con el cliente seleccionado.
                                    </div>
                                );
                            })()
                        )}
                    </div>
                )}

                {/* Botones */}
                <div className="hl-step3-actions hl-no-print" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="hl-btn-outline" onClick={() => { setPasoActual(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                            <i className="bi bi-arrow-left me-2"></i>Volver a editar
                        </button>
                        <button className="hl-btn-outline" onClick={handleImprimirPaso3}>
                            <i className="bi bi-printer me-2"></i>Imprimir Paso 3
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="hl-btn-outline" onClick={handleGuardarBorrador}>
                            <i className="bi bi-save me-2"></i>Guardar Borrador
                        </button>
                        <button
                            className="hl-btn-success"
                            disabled={!clienteSeleccionado || loading || sinMesesParaImportar}
                            onClick={handleConfirmar}
                        >
                            {loading ? (
                                <><span className="hl-spinner" style={{ width: 16, height: 16, borderWidth: 2, display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }}></span>Importando...</>
                            ) : (
                                <><i className="bi bi-check-lg me-2"></i>Confirmar importación</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="historial-laboral-page area-herramientas">
            <PageHeader
                variante="plano"
                icono="file-earmark-text"
                titulo="Importar Historia Laboral ANSES"
                descripcion="Cargá el PDF de Historia Laboral de ANSES para extraer automáticamente los períodos de aportes."
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '0.8rem', marginBottom: '1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    {renderAccionSuperiorIzquierda()}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {renderStepper(true)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {renderAccionSuperiorDerecha()}
                </div>
            </div>

            {error && (
                <div className="hl-alert hl-alert-error">
                    <i className="bi bi-x-circle me-2"></i>{error}
                    <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>×</button>
                </div>
            )}

            {successMsg && !importacionResultado && (
                <div className="hl-alert hl-alert-success">
                    <i className="bi bi-check-circle me-2"></i>{successMsg}
                </div>
            )}

            {loading && pasoActual === 0 && (
                <div className="hl-loading">
                    <div className="hl-spinner"></div>
                    <span>Procesando PDF...</span>
                </div>
            )}

            {pasoActual === 0 && !loading && renderPaso1()}
            {pasoActual === 1 && renderPaso2()}
            {pasoActual === 2 && renderPaso3()}
            
            {showCrearClienteModal && (
                <ModalRegistrarCliente
                    open={showCrearClienteModal}
                    onClose={() => setShowCrearClienteModal(false)}
                    onSuccess={handleClienteCreado}
                    mostrarDialogoConfirmacion={false}
                    initialData={{
                        cuil: cuil || '',
                        nombre: nombreCompleto 
                            ? (nombreCompleto.includes(',') 
                                ? nombreCompleto.split(',')[1].trim() 
                                : nombreCompleto.split(' ').slice(1).join(' ').trim() || nombreCompleto)
                            : '',
                        apellido: nombreCompleto 
                            ? (nombreCompleto.includes(',') 
                                ? nombreCompleto.split(',')[0].trim() 
                                : nombreCompleto.split(' ')[0].trim())
                            : ''
                    }}
                />
            )}

            {modalReemplazoOpen && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 rounded-4 shadow-lg">
                            <div className="modal-header border-0 pb-2">
                                <h5 className="modal-title fw-bold" style={{ color: '#1d4ed8' }}>
                                    <i className="bi bi-exclamation-diamond-fill me-2"></i>
                                    Confirmar reemplazo total de aportes
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setModalReemplazoOpen(false);
                                        setResumenReemplazo(null);
                                        setMesesPendientesImportacion([]);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body pt-0">
                                <div className="hl-alert hl-alert-warning mb-3">
                                    <i className="bi bi-arrow-repeat me-2"></i>
                                    Esta acción eliminará todos los aportes actuales del cliente y cargará únicamente el nuevo lote.
                                </div>
                                <div className="hl-resumen-card">
                                    <div className="hl-resumen-row">
                                        <span>Aportes actuales del cliente:</span>
                                        <strong>{resumenReemplazo?.actuales ?? 0}</strong>
                                    </div>
                                    <div className="hl-resumen-row">
                                        <span>Aportes del nuevo lote:</span>
                                        <strong style={{ color: '#2e7d32' }}>{resumenReemplazo?.aImportar ?? 0}</strong>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary rounded-pill px-4"
                                    onClick={() => {
                                        setModalReemplazoOpen(false);
                                        setResumenReemplazo(null);
                                        setMesesPendientesImportacion([]);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary rounded-pill px-4"
                                    onClick={() => ejecutarImportacionConfirmada(mesesPendientesImportacion)}
                                    disabled={loading}
                                >
                                    {loading ? 'Importando...' : 'Confirmar reemplazo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportarHistorialLaboralPage;

